import React, { useState, useEffect, useRef, useCallback } from 'react';
import { localToday, dateStr , classStartsAt, hoursUntil} from '../utils/dates';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getWeekKey, usageDocId } from '../utils/weekKey';
import { splitHours3, calcOverage3, evaluatePolicies, sumDaySplits } from '../utils/memberHours';
import { reservationsOpen, formatOpenDate, checkGuardrails, checkCanStream } from '../utils/memberGuards';
import { priceRental, rateChangeNotice } from '../utils/rentalPricing';

// Reservation policy phase controls live in settings/membershipTiers
// (Membership Tiers admin page). Both OFF = observation mode: policies are
// evaluated and recorded on every booking, nothing blocks or bills.
import { getFunctions, httpsCallable } from 'firebase/functions';
import { logEvent } from '../utils/logEvent';
import { refundCheckInsForClasses } from '../utils/classRefunds';
import { isComped, blockedByBalance, sessionCost } from '../utils/comp';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc,
  doc, getDoc, writeBatch, query, where, serverTimestamp, orderBy, increment,
} from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import WaiverDialog from '../components/WaiverDialog';
import FullCalendar from '@fullcalendar/react';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Alert, CircularProgress, FormControlLabel, Checkbox, Radio, RadioGroup,
  FormGroup, FormLabel, Tabs, Tab, IconButton, Menu, Collapse,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { logError } from '../utils/errorLogger';
import { HOUR_LABEL_INTERVAL, hourLabelOnly, quarterHourOptions } from '../utils/calendarLabels';
import { windowEvents } from '../utils/schoolWindow';

// ── Constants ─────────────────────────────────────────────────────────────────
const COURTS = [
  { id: 'court-1', title: 'Court 1' },
  { id: 'court-2', title: 'Court 2' },
  { id: 'court-3', title: 'Court 3' },
  { id: 'court-4', title: 'Court 4' },
  { id: 'court-5', title: 'Court 5' },
  { id: 'court-6', title: 'Court 6' },
];

const SESSION_TYPES = [
  'junior-practice', 'junior-private', 'junior-drop-in',
  'junior-evaluation', 'adult-practice', 'league', 'block-time',
];

const SKILL_GROUPS = ['', 'Beginner', 'Intermediate', 'Advanced', 'All'];

// Roster sort/filter on the class dialog. The last three narrow the list
// rather than reorder it; labels are the collapsed form for the closed
// dropdown, which is narrower than the menu.
const ROSTER_SORT_LABELS = {
  name: 'A–Z',
  active: 'Most active first',
  checkedin: 'Checked in',
  boys: 'Boys',
  girls: 'Girls',
};
// The same two filters on an adult session, where "boys" would be odd.
const ADULT_ROSTER_SORT_LABELS = { ...ROSTER_SORT_LABELS, boys: 'Men', girls: 'Women' };

// Athlete records carry gender as 'Male'/'Female'; older ones used athleteGender.
const rosterGenderOf = (a) => String(a.gender || a.athleteGender || '').trim().toLowerCase();
const STATUS_OPTIONS = ['scheduled', 'canceled', 'completed'];

const EVENT_COLOR_OPTIONS = [
  { label: 'Navy',      value: '#1E3A5F' },
  { label: 'Gold',      value: '#C9A84C' },
  { label: 'Green',     value: '#27AE60' },
  { label: 'Red',       value: '#C0392B' },
  { label: 'Purple',    value: '#8E44AD' },
  { label: 'Orange',    value: '#E67E22' },
  { label: 'Teal',      value: '#16A085' },
  { label: 'Blue',      value: '#2E86C1' },
  { label: 'Pink',      value: '#E91E8C' },
  { label: 'Grey',      value: '#7F8C8D' },
  { label: 'Dark Grey', value: '#4A4A4A' },
];

const CLASS_COLORS = {
  'junior-practice':    '#1E3A5F',
  'junior-private':     '#8E44AD',
  'junior-drop-in':     '#27AE60',
  'junior-evaluation':  '#E67E22',
  'adult-practice':     '#C0392B',
  // The same teal as league fixtures (LEAGUE_COLOR below).
  'league':             '#00838F',
  'block-time':         '#7F8C8D',
};

const RESERVATION_TYPES = [
  { value: 'member',     label: 'Member Reservation', color: '#2E86C1' },
  { value: 'practice',   label: 'Practice',    color: '#27AE60' },
  { value: 'clinic',     label: 'Clinic',      color: '#8E44AD' },
  { value: 'private',    label: 'Private',     color: '#E67E22' },
  { value: 'other',      label: 'Other',       color: '#7F8C8D' },
];

// League fixtures write their own reservations from the fixture list, so this
// type is coloured but deliberately kept OUT of RESERVATION_TYPES: that list
// also fills the manual "Type" dropdown, and a league-coloured block with no
// league behind it would read as a fixture while surviving Clear schedule.
// Customer reservations are handled the same way, just below.
//
// Teal, because every other slot in the palette is taken — league nights were
// falling through to the grey used by Other and Block Time.
const LEAGUE_COLOR = '#00838F';

// School season holds, written by the Schedule tab of /admin/schools. Same reasoning as
// league: kept out of RESERVATION_TYPES, and edited only on that page.
const SCHOOL_COLOR = '#B9770E';

const resColor = (type) => (type === 'league'
  ? LEAGUE_COLOR
  : type === 'school'
    ? SCHOOL_COLOR
    : RESERVATION_TYPES.find(t => t.value === type)?.color || '#7F8C8D');

// Events booked for a Customer (customers collection) render in this color
// unless a manual eventColor is chosen. Distinct from member blue and all
// session-type colors so customer reservations are identifiable at a glance.
const CUSTOMER_COLOR = '#AD1457';

const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// Slugify text for URL paths: alphanumeric + dashes, lowercase
function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

// Build a default slug for a class from event name (or session type) + date + court.
// Court is included so multi-court occurrences on the same day get unique slugs.
function defaultClassSlug({ eventName, sessionType, skillGroup, date, courtNumber }) {
  const titlePart = slugify(eventName || `${sessionType || 'class'}${skillGroup ? '-' + skillGroup : ''}`);
  const courtPart = courtNumber ? slugify(String(courtNumber).replace('court-', 'c')) : '';
  return [titlePart, date, courtPart].filter(Boolean).join('-');
}

const emptyClassForm = {
  eventName: '',
  customerId: '',
  eventColor: '',
  description: '',
  scoreboardUseDescription: false,
  sessionType: 'junior-practice',
  skillGroup: '',
  date: '',
  startTime: '17:45',
  endTime: '19:45',
  headCoachId: '',
  assistantCoachIds: [],
  courtNumber: 'court-1',
  courts: ['court-1'],
  status: 'scheduled',
  cancelReason: '',
  maxAthletes: '',
  seasonId: 'season_36',
  repeat: false,
  repeatDays: [],
  repeatUntil: '',
  qrPlanId: '',
  slug: '',
};

const emptyResForm = {
  title: '',
  type: 'practice',
  notes: '',
  courts: [],
  startTime: '',
  endTime: '',
  date: '',
};

function beachToSkillGroup(beachExp) {
  const b = (beachExp || '').toLowerCase();
  if (b.includes('no experience') || b.includes('beginner')) return 'Beginner';
  if (b.includes('intermediate')) return 'Intermediate';
  if (b.includes('advanced')) return 'Advanced';
  return '';
}

// Customers docs may carry name or firstName/lastName depending on how they
// were created; resolve a display name defensively.
function customerDisplayName(c) {
  if (!c) return '';
  return c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || c.id || '';
}

function formatSessionLabel(t) {
  const map = {
    'junior-practice':   'Junior Practice',
    'junior-private':    'Junior Private',
    'junior-drop-in':    'Junior Drop-in',
    'junior-evaluation': 'Junior Evaluation',
    'adult-practice':    'Adult Practice',
    'league':            'League',
    'block-time':        'Block Time',
  };
  return map[t] || t;
}

function calcDuration(start, end) {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
}

// Recent attendance, not lifetime totals — someone who came every week two
// years ago does not belong at the top of today's check-in list.
// Matches ACTIVITY_WINDOW_DAYS on the check-in page: the two lists answer the
// same question and must not disagree about who is active.
const ROSTER_ACTIVITY_DAYS = 30;

export default function Schedule() {
  const calendarRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [deepLinkClassId, setDeepLinkClassId] = useState(searchParams.get('classId') || null);

  // Mobile UX for the class detail dialog
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [coachSectionOpen, setCoachSectionOpen] = useState(false);

  // Data
  const [classes, setClasses]           = useState([]);
  const [customers, setCustomers]       = useState([]);
  const [plans, setPlans]               = useState([]);
  const [reservations, setReservations] = useState([]);
  const [shirtAthleteIds, setShirtAthleteIds] = useState(new Set());
  // Member court reservations
  const [memberTier, setMemberTier] = useState(null);
  // checkCanStream reads membership.status as well as the tier. Stored beside
  // memberTier and set in the same breath below, so the camera switch never
  // renders against half the answer.
  const [memberMembership, setMemberMembership] = useState(null);
  const [memberUsageThisWeek, setMemberUsageThisWeek] = useState(null);
  // The rate card row that prices this member, for court rentals. Mirrors what
  // createMemberBooking bills, so the dialog can't quote a different number.
  const [memberRentalRate, setMemberRentalRate] = useState(null);
  const [policyCtl, setPolicyCtl] = useState({ policyEnforcementEnabled: false, overageBillingEnabled: false, reservationsOpenDate: '' });
  // settings/rentalPolicy — the Reservation Guardrails panel on Court Rentals →
  // Policy. UX only here; createMemberBooking enforces the same four caps. Left
  // null until loaded so the dialog never blocks on defaults the club may have
  // changed; the server still refuses anything out of bounds.
  const [rentalPolicy, setRentalPolicy] = useState(null);

  useEffect(() => {
    getDoc(doc(db, 'settings', 'membershipTiers'))
      .then(s => { if (s.exists()) setPolicyCtl({
        policyEnforcementEnabled: !!s.data().policyEnforcementEnabled,
        overageBillingEnabled: !!s.data().overageBillingEnabled,
        reservationsOpenDate: s.data().reservationsOpenDate || '',
      }); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getDoc(doc(db, 'settings', 'rentalPolicy'))
      .then(s => setRentalPolicy(s.exists() ? s.data() : {}))
      .catch(() => {});
  }, []);

  // Member court booking go-live. UX only — createMemberBooking re-checks this
  // server-side. The calendar itself stays visible and read-only while closed,
  // so members can still see practices, classes and rentals.
  // Court rentals are exempt: the club already rents courts to groups, and a
  // renter is invited onto a known rate one at a time rather than arriving by
  // self-signup. Mirrors the same exemption in createMemberBooking.
  const isCourtRental = memberTier?.pricingMode === 'court_rental';
  const bookingOpen = isCourtRental
    || reservationsOpen(policyCtl.reservationsOpenDate, localToday());
  const canMemberBook = !!memberTier && bookingOpen;

  // The court camera. Deliberately computed for every member, not only the
  // ones who can use it: the switch is shown to everybody and disabled for the
  // rest, because a perk nobody can see is a perk nobody upgrades for. The
  // server re-checks this in createMemberBooking — this only decides what the
  // dialog says.
  const streamEligibility = memberTier
    ? checkCanStream(memberMembership || {}, memberTier)
    : { ok: false, code: 'no_tier', message: '' };
  // The tier decides; the club-wide flag is only the fallback. A $0 rental tier
  // is entirely overage by design, while a tier with included hours may not want
  // members chased for a small excess — one global switch cannot serve both.
  const overageBillingOn = memberTier?.overageBillingEnabled == null
    ? policyCtl.overageBillingEnabled
    : !!memberTier.overageBillingEnabled;

  // 'now' = Stripe Checkout per booking; 'monthly' = invoiced on the last day
  // of the month. Stored on the member so the choice sticks.
  const [courtBilling, setCourtBilling] = useState('monthly');
  // A security deposit owed blocks every booking, so it is surfaced before
  // someone picks a court and only then finds out.
  const [depositOwedCents, setDepositOwedCents] = useState(0);
  const [depositBusy, setDepositBusy] = useState(false);
  const [courtCreditCents, setCourtCreditCents] = useState(0);

  const [primeBlocks, setPrimeBlocks] = useState([]);
  // settings/schoolSchedule, for drawing the high-school window.
  const [schoolSettings, setSchoolSettings] = useState(null);
  const [tintColors, setTintColors] = useState({ prime: '#DDD2B5', nonPrime: '#CCDBEE' });
  const [memberBookings, setMemberBookings] = useState([]);
  const [memberBookingDialog, setMemberBookingDialog] = useState(false);
  const [memberBookingStream, setMemberBookingStream] = useState(false);
  const GUEST_FEATURE_ENABLED = false; // guest pass temporarily disabled
  const [memberBookingForm, setMemberBookingForm] = useState({ date: '', startTime: '18:00', endTime: '19:00', court: 'court-1', guest: false });
  const [memberBookingSaving, setMemberBookingSaving] = useState(false);
  // Renter waiver gate. createMemberBooking is the authority — this only reacts
  // to its refusal, so the dialog can never appear for someone the server would
  // have let through.
  const [waiverDoc, setWaiverDoc] = useState(null);
  const [waiverDialogOpen, setWaiverDialogOpen] = useState(false);
  const [viewingMemberBooking, setViewingMemberBooking] = useState(null);
  const [coaches, setCoaches]           = useState([]);
  const [isAdmin, setIsAdmin]           = useState(false);
  const [isCoach, setIsCoach]           = useState(false);
  // court_admin: sees who holds each member booking and can move one to another
  // court (never another time — times drive hours and cost). Not combined with
  // admin, which already has all of this.
  const [isCourtAdmin, setIsCourtAdmin] = useState(false);
  const [moveToCourt, setMoveToCourt]   = useState('');
  const [currentUserName, setCurrentUserName] = useState('');
  const [currentUserUid, setCurrentUserUid] = useState('');
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [classLogs, setClassLogs]       = useState([]);
  // Folded by default: at practice the dialog is a check-in screen, and an
  // open log with its note field took a row of phone height from the roster.
  const [classLogOpen, setClassLogOpen] = useState(false);
  // Attendance text: { severity, text } shown inside the class dialog, where
  // the page-level success banner would be hidden behind it.
  const [attendanceNotice, setAttendanceNotice] = useState(null);
  const [attendanceSending, setAttendanceSending] = useState(false);
  const [logInput, setLogInput]         = useState('');
  const [logSaving, setLogSaving]       = useState(false);
  const [classViewDialog, setClassViewDialog] = useState(false);
  const [viewingClass, setViewingClass]       = useState(null);
  const [qrDialog, setQrDialog]               = useState(false);
  const [viewingAttendance, setViewingAttendance] = useState([]);
  const [viewingAthletes, setViewingAthletes] = useState([]);
  // Check-in roster ordering. Activity is loaded once per session and cached —
  // it needs attendance across all classes, not just the one being viewed.
  const [rosterSort, setRosterSort] = useState('name');   // 'name' | 'active' | 'checkedin' | 'boys' | 'girls'
  const [rosterSearch, setRosterSearch] = useState('');   // type-to-find on the class dialog's roster
  const [athleteActivity, setAthleteActivity] = useState(null);
  const [athletesLoading, setAthletesLoading] = useState(false);
  const [checkingInAthlete, setCheckingInAthlete] = useState(null);
  const [mergeDialog, setMergeDialog] = useState(false);
  const [mergeSelected, setMergeSelected] = useState([]);
  const [mergeSaving, setMergeSaving] = useState(false);
  const [mergeCoach, setMergeCoach] = useState('');
  const [mergeCourts, setMergeCourts] = useState([]);
  const [mergeDate, setMergeDate] = useState('');
  const [mergeStartTime, setMergeStartTime] = useState('');
  const [mergeEndTime, setMergeEndTime] = useState('');

  // Bulk cancel
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelTypes, setCancelTypes] = useState([]);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSaving, setCancelSaving] = useState(false);
  // Single-class cancel (initiated from inside the class detail dialog).
  // Kept separate from the bulk-cancel state above so opening one doesn't
  // interfere with the other.
  const [singleCancelDialog, setSingleCancelDialog] = useState(false);
  const [singleCancelReason, setSingleCancelReason] = useState('');
  const [singleCancelSaving, setSingleCancelSaving] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [reserveChoice, setReserveChoice] = useState(null); // { athlete, attRec } — class-day tap on a reserved athlete
  const [loading, setLoading]           = useState(true);

  // UI
  const [calendarTitle, setCalendarTitle] = useState('');
  const [view, setView]                   = useState('resourceTimeGridDay');
  const [success, setSuccess]             = useState('');
  const [error, setError]                 = useState('');

  // Class dialog
  const [classDialog, setClassDialog]       = useState(false);
  const [selectedClass, setSelectedClass]   = useState(null);
  const [classForm, setClassForm]           = useState(emptyClassForm);
  const [saving, setSaving]                 = useState(false);
  const [updateScopeDialog, setUpdateScopeDialog] = useState(false);
  const [deleteScopeDialog, setDeleteScopeDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {scope,count,first,last}
  const [deleteAllCourts, setDeleteAllCourts] = useState(false);
  // Last drag-move, kept so the success banner can offer Undo. A drag applies
  // immediately now, so this is the way back from a mis-drop.
  const [undoMove, setUndoMove]             = useState(null);

  // Reservation dialog
  const [resDialog, setResDialog]   = useState(false);
  const [selectedRes, setSelectedRes] = useState(null);
  const [resForm, setResForm]         = useState(emptyResForm);

  // Create dialog (choose type)
  const [createDialog, setCreateDialog] = useState(false);
  const [pendingSelect, setPendingSelect] = useState(null); // {date, startTime, endTime, courtId}

  // ── Load data ───────────────────────────────────────────────────────────────
  // Wait for Firebase Auth to resolve before kicking off the data load. On
  // a direct page load (or refresh) `auth.currentUser` may be null while
  // Firebase rehydrates the session — relying on it produces a race where
  // currentUserEmail/Uid stay empty and the player-only filter in the class
  // dialog filters out everyone (resulting in "No athletes found").
  useEffect(() => {
    const load = async (user) => {
      let userRoles = [];
      try {
        if (user) {
          setCurrentUserUid(user.uid);
          setCurrentUserEmail((user.email || '').toLowerCase());
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const roles = userDoc.data().roles || [];
            userRoles = roles;
            const d = userDoc.data();
            setIsAdmin(roles.includes('admin'));
            setIsCoach((roles.includes('coach') || roles.includes('assistant_coach')) && !roles.includes('admin'));
            setIsCourtAdmin(roles.includes('court_admin') && !roles.includes('admin'));
            setCurrentUserName(`${d.firstName || ''} ${d.lastName || ''}`.trim() || user.email || 'Unknown');
          }
        }
        // Prime time blocks — loaded for ALL users (calendar background tint).
        // A block is prime when its rentalTimeBlocks doc has isPrime: true.
        try {
          const [blockSnap, brandSnap] = await Promise.all([
            getDocs(collection(db, 'rentalTimeBlocks')),
            getDoc(doc(db, 'settings', 'branding')),
          ]);
          getDoc(doc(db, 'settings', 'schoolSchedule'))
            .then(s => setSchoolSettings(s.exists() ? s.data() : null)).catch(() => {});
          setPrimeBlocks(blockSnap.docs
            .filter(b => b.data().isPrime === true)
            .map(b => ({ id: b.id, ...b.data() })));
          if (brandSnap.exists()) {
            const bd = brandSnap.data();
            setTintColors({
              prime: bd.primeTintColor || '#DDD2B5',
              nonPrime: bd.nonPrimeTintColor || '#CCDBEE',
            });
          }
        } catch (e) { console.warn('Prime blocks load failed:', e); }
        // Member reservation data — only for active members; failure is non-fatal
        try {
          const uDoc = await getDoc(doc(db, 'users', user.uid));
          if (uDoc.exists()) {
            const ud = uDoc.data();
            const mem = ud.membership || {};
            // A one-time membership with no expiresAt is malformed, not eternal —
            // treat it as expired. Recurring memberships legitimately have none.
            const memExp = mem.expiresAt
              ? (mem.expiresAt.toDate ? mem.expiresAt.toDate() : new Date(mem.expiresAt))
              : null;
            const oneTime = mem.pricingMode === 'one_time' || mem.pricingMode === 'one-time' || mem.isDayPass;
            const memActive = ['active', 'trialing'].includes(mem.status) &&
              mem.tierId &&
              (memExp ? memExp > new Date() : !oneTime);
            if (memActive) {
              const tierSnap = await getDoc(doc(db, 'membershipTiers', mem.tierId));
              if (tierSnap.exists()) {
                setMemberTier({ id: tierSnap.id, ...tierSnap.data() });
                setMemberMembership(mem);
              }
              if (mem.rentalRateId) {
                const rSnap = await getDoc(doc(db, 'rentalRates', mem.rentalRateId));
                if (rSnap.exists()) setMemberRentalRate({ id: rSnap.id, ...rSnap.data() });
              }
              // A renter pays at booking whatever is stored — see
              // paysCourtTimeUpFront in functions/index.js, which decides it
              // server-side. Reflected here so the dialog does not offer a
              // choice the server will not honour.
              setCourtBilling(mem.pricingMode === 'court_rental' ? 'now' : (mem.courtBilling || 'monthly'));
              setDepositOwedCents(Math.max(0,
                (Number(mem.depositRequiredCents) || 0) - (Number(mem.depositPaidCents) || 0)));
              setCourtCreditCents(Number(ud.courtCreditCents) || 0);
              const today = localToday();
              const usageSnap = await getDoc(doc(db, 'memberUsage', `${user.uid}_${getWeekKey(today)}`));
              if (usageSnap.exists()) setMemberUsageThisWeek(usageSnap.data());
            }
          }
        } catch (e) {
          console.warn('Member reservation data load failed:', e);
        }
        // All member bookings — visible to everyone as availability blocks
        try {
          const allMbSnap = await getDocs(collection(db, 'memberBookings'));
          setMemberBookings(allMbSnap.docs.map(x => ({ id: x.id, ...x.data() })));
        } catch (e) { console.warn('Member bookings load failed:', e); }
        const [classSnap, coachSnap, resSnap, planSnap, shirtSnap, custSnap] = await Promise.all([
          getDocs(collection(db, 'classes')),
          getDocs(query(collection(db, 'users'), where('roles', 'array-contains-any', ['coach', 'assistant_coach']))),
          getDocs(collection(db, 'reservations')),
          getDocs(collection(db, 'plans')),
          (userRoles.includes('admin') || userRoles.includes('coach') || userRoles.includes('assistant_coach')) ? getDocs(query(collection(db, 'shirtTasks'), where('done', '==', false))) : Promise.resolve({ docs: [] }),
          userRoles.includes('admin') ? getDocs(collection(db, 'customers')).catch(() => ({ docs: [] })) : Promise.resolve({ docs: [] }),
        ]);
        setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(c => !c.archived)
          .sort((a, b) => customerDisplayName(a).localeCompare(customerDisplayName(b))));
        setShirtAthleteIds(new Set(shirtSnap.docs.map(d => d.data().athleteId).filter(Boolean)));
        setClasses(classSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setPlans(planSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.active));
        const allCoaches = coachSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Deduplicate by email — removes pending docs when a real doc exists for same email
        const seenEmails = new Set();
        const dedupedCoaches = allCoaches.filter(c => {
          if (!c.email) return true;
          const key = c.email.toLowerCase();
          if (seenEmails.has(key)) return false;
          seenEmails.add(key);
          return true;
        });
        setCoaches(dedupedCoaches);
        setReservations(resSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        logError('Schedule', e.message, e.stack);
        setError('Failed to load schedule.');
      }
      setLoading(false);
    };
    // Subscribe to auth changes. onAuthStateChanged fires once with null
    // on initial mount before persisted session rehydrates, then again with
    // the real user. We only proceed with the data load when we have a real
    // user — the null fire would set empty user identity into state, then
    // the dialog filter would reject everyone on a player account.
    const unsub = onAuthStateChanged(auth, (user) => {
      console.log('[AuthState]', { hasUser: !!user, email: user?.email });
      if (!user) {
        // No persisted session. Route guard should keep unauthenticated
        // users out, but clear loading so we don't spin forever.
        setLoading(false);
        return;
      }
      load(user);
    });
    return unsub;
  }, []);

  // Auto-scroll the page to the earliest event of the visible day when data loads.
  // FullCalendar uses height="auto" here (no internal scroll container) so we have
  // to scroll the window itself rather than calling api.scrollToTime().
  useEffect(() => {
    if (loading) return;
    if (classes.length === 0 && reservations.length === 0) return;
    const tryScroll = (attempts = 0) => {
      const api = calendarRef.current?.getApi?.();
      if (!api) {
        if (attempts < 20) setTimeout(() => tryScroll(attempts + 1), 100);
        return;
      }
      const view = api.view;
      const vt = view.type;
      if (!['resourceTimeGridDay', 'timeGridDay', 'timeGridWeek'].includes(vt)) return;
      const startMs = view.activeStart.getTime();
      const endMs = view.activeEnd.getTime();
      const allStarts = [];
      classes.forEach(c => {
        if (c.status === 'canceled') return;
        if (!c.date || !c.startTime) return;
        const d = new Date(`${c.date}T${c.startTime}`);
        if (d.getTime() >= startMs && d.getTime() < endMs) allStarts.push(d);
      });
      reservations.forEach(r => {
        if (r.type === 'tournament') return;
        if (!r.date || !r.startTime) return;
        const d = new Date(`${r.date}T${r.startTime}`);
        if (d.getTime() >= startMs && d.getTime() < endMs) allStarts.push(d);
      });
      if (allStarts.length === 0) return;
      const earliest = allStarts.reduce((a, b) => (a < b ? a : b));
      const hh = earliest.getHours();
      const mm = earliest.getMinutes();
      const totalMin = Math.max(0, hh * 60 + mm - 30);
      const padded = `${String(Math.floor(totalMin / 60)).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}:00`;
      const scrollPageToTime = (timeStr) => {
        const row = document.querySelector(`[data-time="${timeStr}"]`);
        if (!row) return false;
        const rect = row.getBoundingClientRect();
        window.scrollTo({ top: window.scrollY + rect.top - 120, behavior: 'smooth' });
        return true;
      };
      scrollPageToTime(padded);
      // Retry once after layout settles
      setTimeout(() => scrollPageToTime(padded), 800);
    };
    setTimeout(() => tryScroll(), 600);
  }, [loading, classes, reservations]);

  // Deep-link: when ?classId=xxx is present, navigate the calendar to that class's date
  // and open its detail dialog. Used by Dashboard.js bell to jump straight to a class.
  useEffect(() => {
    if (loading || !deepLinkClassId) return;
    if (classes.length === 0) return;
    const target = classes.find(c => c.id === deepLinkClassId);
    if (!target) {
      // Class not in the currently loaded window — skip silently
      setDeepLinkClassId(null);
      return;
    }
    // Open the detail dialog with the same shape Schedule's normal click flow uses.
    setViewingClass({ id: target.id, ...target });
    setCoachSectionOpen(false);
    setClassViewDialog(true);
    setAttendanceNotice(null);
    // Navigate the calendar to that date if not already there.
    try {
      const api = calendarRef.current?.getApi?.();
      if (api && target.date) api.gotoDate(target.date);
    } catch {}
    // Clear the param so refreshing doesn't re-open the dialog.
    setDeepLinkClassId(null);
    const next = new URLSearchParams(searchParams);
    next.delete('classId');
    setSearchParams(next, { replace: true });
  }, [loading, classes, deepLinkClassId]);

  // Back from Stripe without paying — the cancel link carries the held booking
  // so the court goes back on the calendar immediately, rather than sitting
  // held until the sweeper notices half an hour later.
  useEffect(() => {
    const unpaid = searchParams.get('court_unpaid');
    if (!unpaid) return;
    (async () => {
      try {
        await httpsCallable(getFunctions(), 'abandonUnpaidBooking')({ bookingId: unpaid });
        setMemberBookings(prev => prev.filter(b => b.id !== unpaid));
        setError('Payment was not completed, so the court was released. You can book it again.');
      } catch (e) {
        console.warn('Could not release the held court:', e);
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('court_unpaid');
        setSearchParams(next, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Build calendar events ───────────────────────────────────────────────────
  // Who may see member names on bookings and move them between courts.
  const seesBookers = isAdmin || isCourtAdmin;

  const calendarEvents = [
    // Classes — one event per class (courtNumber maps to resource)
    ...classes.map(c => {
      // Customer-booked events get the customer color so they read distinctly
      // from member blue and session-type colors; a manual eventColor still wins.
      const color = c.status === 'canceled' ? '#aaa' : (c.eventColor || (c.customerId ? CUSTOMER_COLOR : CLASS_COLORS[c.sessionType]) || '#1E3A5F');
      const coach = coaches.find(co => co.id === c.headCoachId);
      const resourceId = c.courtNumber?.startsWith('court-') ? c.courtNumber : `court-${c.courtNumber}`;
      const courtNum = parseInt((c.courtNumber || '').replace('court-', '')) || 99;
      const baseTitle = c.eventName ? `${c.eventName}` : `${formatSessionLabel(c.sessionType)}${c.skillGroup ? ` — ${c.skillGroup}` : ''}`;
      return {
        id: c.id,
        resourceId,
        courtOrder: courtNum,
        title: isAdmin && c.customerName ? `${baseTitle} — ${c.customerName}` : baseTitle,
        start: `${c.date}T${c.startTime}`,
        end: `${c.date}T${c.endTime}`,
        backgroundColor: color,
        borderColor: color,
        extendedProps: { ...c, eventType: 'class' },
      };
    }),
    // Reservations — one event per court per reservation
    ...reservations.flatMap(r => {
      const courts = Array.isArray(r.courts) ? r.courts : [r.courts].filter(Boolean);
      return courts.map(courtId => ({
        id: `res-${r.id}-${courtId}`,
        resourceId: courtId,
        title: r.title || r.type,
        start: `${r.date}T${r.startTime}`,
        end: `${r.date}T${r.endTime}`,
        backgroundColor: resColor(r.type),
        borderColor: resColor(r.type),
        extendedProps: { ...r, reservationId: r.id, eventType: 'reservation' },
      }));
    }),
    // Member court bookings — Member Blue with dashed border
    ...memberBookings
      .filter(b => b.status !== 'cancelled')
      .flatMap(b => {
        const courts = Array.isArray(b.courts) ? b.courts : [b.courts].filter(Boolean);
        return courts.map(courtId => ({
          id: `mb-${b.id}-${courtId}`,
          resourceId: courtId,
          title: (() => {
            // Booked hours shown after the time range in the event header
            const mins = (t) => { const [h, m] = String(t || '0:0').split(':').map(Number); return h * 60 + (m || 0); };
            const hrs = Math.max(0, (mins(b.endTime) - mins(b.startTime)) / 60);
            const hrsLabel = ` (${hrs % 1 === 0 ? hrs : hrs.toFixed(1)}h)`;
            const awaiting = b.status === 'pending_approval' ? ' · awaiting approval' : '';
            if (b.memberId === currentUserUid) return `My Booking${hrsLabel}${b.guestCount ? ' +1 guest' : ''}${awaiting}`;
            if (seesBookers && awaiting) return `Request — ${b.memberName || 'Member'}${hrsLabel}${awaiting}`;
            return seesBookers ? `Reserved — ${b.memberName || 'Member'}${hrsLabel}` : 'Reserved';
          })(),
          start: `${b.date}T${b.startTime}`,
          end: `${b.date}T${b.endTime}`,
          // Admins see a distinct, stable color per member so different members'
          // bookings are visually separable. Non-admin viewers keep the uniform
          // member blue — per-member colors would let users correlate anonymous
          // "Reserved" blocks across days.
          ...(() => {
            const MEMBER_PALETTE = ['#2E86C1', '#8E44AD', '#16A085', '#D35400', '#C2185B', '#5D6D7E', '#0E7C61', '#B7950B', '#7B241C', '#1F618D'];
            if (!seesBookers || b.memberId === currentUserUid) return { backgroundColor: '#2E86C1', borderColor: '#2E86C1' };
            let h = 0;
            for (let ci = 0; ci < (b.memberId || '').length; ci++) h = ((h << 5) - h + b.memberId.charCodeAt(ci)) | 0;
            const c = MEMBER_PALETTE[Math.abs(h) % MEMBER_PALETTE.length];
            return { backgroundColor: c, borderColor: c };
          })(),
          classNames: ['member-booking-event'],
          extendedProps: { ...b, memberBookingId: b.id, eventType: 'memberBooking' },
        }));
      }),
    // The high-school window: one labelled block across every court on each
    // school day of the season. Drawn only; createMemberBooking decides who
    // may book inside it.
    ...windowEvents(schoolSettings, COURTS.map(ct => ct.id)),
    // Prime time background tint (gold) — recurring, one per prime block per court
    ...primeBlocks.flatMap((pb, i) =>
      COURTS.map(ct => ({
        id: `prime-bg-${pb.id || i}-${ct.id}`,
        resourceId: ct.id,
        daysOfWeek: (pb.days || []).map(d => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].indexOf(d)).filter(n => n >= 0),
        startTime: pb.startTime,
        endTime: pb.endTime,
        display: 'background',
        backgroundColor: tintColors.prime,
      }))
    ),
    // Non-prime background tint (light club blue) — explicitly computed
    // complement of the prime blocks within visible hours (07:30–22:00),
    // per weekday per court. No inverse-background (unreliable w/ recurrence).
    ...(() => {
      const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      const toM = t => { const [h, m] = String(t).split(':').map(Number); return h * 60 + (m || 0); };
      const toT = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
      const VIS_START = toM('07:30'), VIS_END = toM('22:00');
      const out = [];
      for (let day = 0; day < 7; day++) {
        // Merge prime intervals for this weekday
        const iv = primeBlocks
          .filter(pb => (pb.days || []).includes(DAYS[day]))
          .map(pb => [Math.max(VIS_START, toM(pb.startTime)), Math.min(VIS_END, toM(pb.endTime))])
          .filter(([a, b]) => b > a)
          .sort((a, b) => a[0] - b[0]);
        const merged = [];
        for (const [a, b] of iv) {
          if (merged.length && a <= merged[merged.length - 1][1]) {
            merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], b);
          } else merged.push([a, b]);
        }
        // Complement = non-prime segments
        const nonPrime = [];
        let cur = VIS_START;
        for (const [a, b] of merged) {
          if (a > cur) nonPrime.push([cur, a]);
          cur = Math.max(cur, b);
        }
        if (cur < VIS_END) nonPrime.push([cur, VIS_END]);
        for (const [a, b] of nonPrime) {
          for (const ct of COURTS) {
            out.push({
              id: `nonprime-bg-${day}-${a}-${ct.id}`,
              resourceId: ct.id,
              daysOfWeek: [day],
              startTime: toT(a),
              endTime: toT(b),
              display: 'background',
              backgroundColor: tintColors.nonPrime,
            });
          }
        }
      }
      return out;
    })(),
  ];

  // ── Overlap check for classes ───────────────────────────────────────────────
  // Build a unique slug for a class, appending -2/-3/... if collisions exist.
  // Checks against the in-memory `classes` list (which mirrors Firestore).
  const ensureUniqueSlug = useCallback((proposed, excludeId = null) => {
    const base = slugify(proposed);
    if (!base) return '';
    const taken = new Set(
      classes.filter(c => c.id !== excludeId && c.slug).map(c => c.slug)
    );
    if (!taken.has(base)) return base;
    let i = 2;
    while (taken.has(`${base}-${i}`)) i++;
    return `${base}-${i}`;
  }, [classes]);

  const hasOverlap = useCallback((date, startTime, endTime, courtNumber, excludeId = null) => {
    const toMins = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    const newStart = toMins(startTime);
    const newEnd = toMins(endTime);
    return classes.some(c => {
      if (c.id === excludeId) return false;
      if (c.date !== date) return false;
      if (c.status === 'canceled') return false;
      if (c.sessionType === 'block-time') return false;
      if (!courtNumber || !c.courtNumber) return false;
      const cCourt = c.courtNumber?.startsWith('court-') ? c.courtNumber : `court-${c.courtNumber}`;
      const formCourt = courtNumber?.startsWith('court-') ? courtNumber : `court-${courtNumber}`;
      if (cCourt !== formCourt) return false;
      const cStart = toMins(c.startTime);
      const cEnd = toMins(c.endTime);
      return newStart < cEnd && newEnd > cStart;
    });
  }, [classes]);

  // ── FullCalendar callbacks ──────────────────────────────────────────────────
  const handleDateClick = useCallback((info) => {
    // In month view, clicking a date should navigate to that day's day-view
    // rather than open the create-event dialog.
    if (info.view && info.view.type === 'dayGridMonth') {
      const api = calendarRef.current?.getApi?.();
      if (api) {
        api.changeView('resourceTimeGridDay', info.dateStr);
      }
    }
    // In time grid views, let the normal select (drag) handler create events —
    // don't do anything on a bare click.
  }, []);

  // ── Member booking helpers ──────────────────────────────────────────────
  const memberBookingPreview = (() => {
    if (!memberTier || !memberBookingForm.date) return null;
    const { date, startTime, endTime, court } = memberBookingForm;
    if (!startTime || !endTime || endTime <= startTime) return null;
    const split = splitHours3(startTime, endTime, date, primeBlocks, 1);
    const usage = memberUsageThisWeek || { regularUsed: 0, primeUsed: 0, weekendUsed: 0 };
    const dayUsage = sumDaySplits(
      memberBookings.filter(b => b.memberId === currentUserUid && b.date === date && b.status !== 'cancelled'),
      primeBlocks
    );
    const { flags, overage } = evaluatePolicies({
      split, usage, tier: memberTier, date, todayStr: localToday(),
      dayUsage, bookingCourts: [court],
    });
    // Court rentals bill from the rate card, not tier overage — quote what the
    // server will actually charge, or the dialog promises one price and the
    // invoice shows another.
    if (memberTier.pricingMode === 'court_rental' && memberRentalRate) {
      const priced = priceRental(startTime, endTime, date, 1, memberRentalRate, primeBlocks);
      return { ...split, ...overage, overageAmount: priced.amount,
        rateName: memberRentalRate.rateName || '', rateBreakdown: priced.breakdown,
        policyFlags: flags };
    }
    return { ...split, ...overage, policyFlags: flags };
  })();

  const guestUsedToday = memberBookings.some(b =>
    b.date === memberBookingForm.date && b.status !== 'cancelled' && (b.guestCount || 0) > 0
  );

  // Writes the acceptance the same way TrainingPlans and AdultMembership do —
  // users/{uid}.documentAcceptances[docId] — then retries the booking, so a
  // renter who signs lands on their court instead of on the form again.
  const handleAcceptWaiver = async (entry) => {
    const user = auth.currentUser;
    if (!user || !waiverDoc) throw new Error('Session expired. Please refresh and try again.');
    await updateDoc(doc(db, 'users', user.uid), {
      [`documentAcceptances.${waiverDoc.id}`]: entry,
    });
    setWaiverDialogOpen(false);
    await handleMemberBook();
  };

  const handleMemberBook = async () => {
    if (!memberTier || memberBookingSaving) return;
    const { date, startTime, endTime, court, guest } = memberBookingForm;
    setError('');
    if (!bookingOpen) {
      setError(`Court reservations open ${formatOpenDate(policyCtl.reservationsOpenDate)}.`);
      return;
    }
    if (!date || !startTime || !endTime || endTime <= startTime) { setError('Please pick a valid time range.'); return; }
    if (startTime < '07:30' || endTime > '22:00') { setError('Bookings must be within open hours (7:30 AM – 10:00 PM).'); return; }
    // Club reservation guardrails — duration, lead time, advance window, courts.
    // Admins bypass, as the Policy tab says. Skipped entirely until the policy
    // doc has loaded: the server checks the same four and is the one that counts.
    if (!isAdmin && rentalPolicy) {
      const guard = checkGuardrails(rentalPolicy, {
        date, startTime, endTime, courts: [court],
        todayStr: localToday(),
        hoursUntilStart: hoursUntil(date, startTime),
        tierMaxDurationHours: memberTier?.maxBookingDurationHours,
      });
      if (!guard.ok) { setError(guard.message); return; }
    }
    // Conflict check against classes, reservations, and member bookings on the same court
    const overlaps = (aS, aE, bS, bE) => aS < bE && bS < aE;
    const conflict =
      classes.some(c => c.status !== 'canceled' && c.date === date &&
        ((Array.isArray(c.courts) && c.courts.includes(court)) || c.courtNumber === court) &&
        overlaps(startTime, endTime, c.startTime, c.endTime)) ||
      reservations.some(r => r.date === date &&
        (Array.isArray(r.courts) ? r.courts.includes(court) : r.courts === court) &&
        overlaps(startTime, endTime, r.startTime, r.endTime)) ||
      memberBookings.some(b => b.status !== 'cancelled' && b.date === date &&
        (Array.isArray(b.courts) ? b.courts.includes(court) : b.courts === court) &&
        overlaps(startTime, endTime, b.startTime, b.endTime));
    if (conflict) { setError('That court is already booked for part of this time. Please pick another slot.'); return; }
    setMemberBookingSaving(true);
    try {
      // Authoritative path: the server re-checks tier, prime eligibility, the
      // one-prime-per-day rule and court conflicts, then writes the booking,
      // usage and any overage. Client checks above are UX only.
      const fn = httpsCallable(getFunctions(), 'createMemberBooking');
      const res = await fn({
        date, startTime, endTime, court, guest: !!guest,
        // Only ever true for a tier that carries it. The server refuses the
        // booking outright rather than dropping the flag, so sending it when
        // ineligible would cost the member their slot over a switch they were
        // shown; the dialog disables it for exactly that reason.
        stream: streamEligibility.ok && memberBookingStream,
      });
      const r = res.data;
      const split = r.split || { regularHours: 0, primeHours: 0, weekendHours: 0 };
      logEvent('memberBooking.created', {
        target: { id: r.id, name: currentUserName || '', date },
        description: `Member booking: ${date} ${startTime}–${endTime}, court ${court}${r.overageAmount > 0 ? ` ($${r.overageAmount.toFixed(2)} overage)` : ''}`,
        metadata: { regularHours: split.regularHours, primeHours: split.primeHours, overageAmount: r.overageAmount, guestCount: guest ? 1 : 0, touchesPrime: !!r.touchesPrime },
      });
      setMemberBookings(prev => [...prev, {
        id: r.id, memberId: currentUserUid, date, startTime, endTime, courts: [court],
        guestCount: guest ? 1 : 0,
        status: r.needsApproval ? 'pending_approval' : r.pendingPayment ? 'pending_payment' : 'confirmed',
        streamEnabled: streamEligibility.ok && memberBookingStream,
        touchesPrime: !!r.touchesPrime, localDate: date,
        regularHours: split.regularHours, primeHours: split.primeHours,
        weekendHours: split.weekendHours, overageAmount: r.overageAmount,
      }]);
      setMemberUsageThisWeek(prev => ({
        ...(prev || {}),
        regularUsed: ((prev?.regularUsed) || 0) + split.regularHours,
        primeUsed: ((prev?.primeUsed) || 0) + split.primeHours,
        weekendUsed: ((prev?.weekendUsed) || 0) + split.weekendHours,
      }));
      setMemberBookingDialog(false);
      // Remember the choice so it is the default next time. Renters have no
      // choice to remember.
      if (!isCourtRental) {
        try {
          await updateDoc(doc(db, 'users', currentUserUid), { 'membership.courtBilling': courtBilling });
        } catch (e) { console.warn('Could not save billing preference:', e); }
      }

      // Asked on every pay-now booking, not only one that carries overage of
      // its own: checkout settles the member's whole outstanding balance, and
      // the server is the only side that knows what is still owed. A booking
      // inside the allowance with nothing outstanding comes straight back as
      // paid with no url, which costs one call and keeps the rule in one place.
      if (r.needsApproval) {
        // Nothing to pay for until it's approved.
        setSuccess('Request sent. This time is reserved for high schools, so an admin will approve it — you\'ll get an email either way.');
      } else if (overageBillingOn && courtBilling === 'now') {
        // Credit is spent server-side first; a fully covered booking needs no
        // checkout and returns paid instead of a url.
        try {
          const pay = httpsCallable(getFunctions(), 'createCourtTimeCheckout');
          const res = await pay({ bookingId: r.id });
          if (res.data?.url) {
            // Leaving for Stripe. The court is held until they come back —
            // paid, or by the cancel link, or when the hold runs out.
            setSuccess(`Court held for ${r.holdMinutes || 35} minutes while you pay…`);
            window.location.href = res.data.url;
            return;
          }
          setSuccess(res.data?.creditUsedCents
            ? `Court booked and paid with $${(res.data.creditUsedCents / 100).toFixed(2)} of credit.`
            : 'Court booked!');
        } catch (e) {
          // Only a booking that is actually a HOLD gets given back. A booking
          // inside the allowance is confirmed on creation and stands on its own
          // — releasing it because a sweep of old balances failed would take a
          // court away over an unrelated debt.
          if (r.pendingPayment) {
            // Pay-now court time is held, not booked, until it is paid for. If
            // checkout cannot even start there is nothing to wait for, so give
            // the court back now rather than leaving it held for half an hour.
            try {
              await httpsCallable(getFunctions(), 'abandonUnpaidBooking')({ bookingId: r.id });
            } catch (relErr) {
              // The hold expires on its own; the sweeper is the backstop.
              console.warn('Could not release the held court:', relErr);
            }
            setMemberBookings(prev => prev.filter(b => b.id !== r.id));
            setError(`Payment could not be started (${e.message}). The court was not held — please try again.`);
          } else {
            setError(`Court booked, but the outstanding court time could not be charged (${e.message}).`);
          }
        }
      } else {
        setSuccess(overageBillingOn && r.overageAmount > 0
          ? `Court booked! $${r.overageAmount.toFixed(2)} in court time will be invoiced at month end.`
          : 'Court booked!');
      }
    } catch (e) {
      // Guard rejections carry a human-readable message from the server; show
      // it rather than a generic failure so the member knows what to change.
      const msg = e?.message && !/internal/i.test(e.message)
        ? e.message
        : 'Booking failed. Please try again.';
      logError('Schedule', e.message, e.stack);

      // A renter missing their waiver can fix it here and now, so offer the
      // document rather than only naming it. Insurance can't be self-served —
      // only an admin records one — so that half stays a message.
      const details = e?.details || {};
      if (details.code === 'renter_requirements' && details.waiverDocId
          && (details.missing || []).includes('waiver')) {
        try {
          const wd = await getDoc(doc(db, 'clubDocuments', details.waiverDocId));
          if (wd.exists()) {
            setWaiverDoc({ id: wd.id, ...wd.data() });
            setWaiverDialogOpen(true);
            // Insurance is still outstanding when both were missing, and the
            // dialog shouldn't imply signing is the whole story.
            setError((details.missing || []).includes('insurance') ? msg : '');
            setMemberBookingSaving(false);
            return;
          }
        } catch (wErr) {
          console.warn('Could not load the waiver document:', wErr);
        }
      }
      setError(msg);
    }
    setMemberBookingSaving(false);
  };

  // Court only, server-side — the callable re-checks the new court is free for
  // the booking's exact times and that the caller is admin or court_admin.
  const handleMoveCourt = async () => {
    const b = viewingMemberBooking;
    if (!b || !moveToCourt || memberBookingSaving) return;
    const from = (b.courts || [])[0];
    setMemberBookingSaving(true);
    setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'moveMemberBookingCourt');
      const { data: r } = await fn({ bookingId: b.memberBookingId, fromCourt: from, toCourt: moveToCourt });
      const courts = r?.courts || [moveToCourt];
      logEvent('memberBooking.courtMoved', {
        target: { id: b.memberBookingId, name: b.memberName || '', date: b.date },
        description: `Member booking moved: ${b.date} ${b.startTime}–${b.endTime} Court ${courtNum(from)} → Court ${courtNum(moveToCourt)}`,
        metadata: { fromCourt: from, toCourt: moveToCourt },
      });
      setMemberBookings(prev => prev.map(x => x.id === b.memberBookingId ? { ...x, courts } : x));
      setSuccess(`Moved ${b.memberName || 'the booking'} to Court ${courtNum(moveToCourt)}.`);
      setTimeout(() => setSuccess(''), 5000);
      setMemberBookingDialog(false);
      setViewingMemberBooking(null);
      setMoveToCourt('');
    } catch (e) {
      logError('Schedule', e.message, e.stack);
      // Conflict and "already finished" come back as readable messages.
      setError(e?.message && e.code !== 'internal' ? e.message : 'Move failed. Please try again.');
    }
    setMemberBookingSaving(false);
  };

  const handleMemberCancel = async () => {
    const b = viewingMemberBooking;
    if (!b || memberBookingSaving) return;
    setMemberBookingSaving(true);
    setError('');
    try {
      // Server-side: the cutoff decides whether the charge is released, and a
      // member must not be able to move that line by editing their own docs.
      const fn = httpsCallable(getFunctions(), 'cancelMemberBooking');
      const { data: r } = await fn({ bookingId: b.memberBookingId });
      logEvent('memberBooking.canceled', {
        target: { id: b.memberBookingId, name: b.memberName || '', date: b.date },
        description: `Member booking canceled: ${b.date} ${b.startTime}–${b.endTime}`,
        metadata: { regularHours: b.regularHours || 0, primeHours: b.primeHours || 0, hoursOut: r?.hoursOut },
      });
      // The window comes back from the server — it is an admin setting now, so
      // a message naming "1 hour" would go stale the moment it changes.
      const win = r?.cutoffHours ?? 1;
      const winText = `${win}-hour window`;
      const hoursLost = r?.hoursReturned === false ? ' The hours were not returned.' : '';
      if (r?.creditedCents > 0) {
        setCourtCreditCents(c => c + r.creditedCents);
        setSuccess(`Booking cancelled. $${(r.creditedCents / 100).toFixed(2)} added as court credit.`);
      } else if (r?.chargeStands > 0) {
        setError(`Booking cancelled, but it was inside the ${winText} so the $${r.chargeStands.toFixed(2)} still stands.${hoursLost}`);
      } else if (hoursLost) {
        setError(`Booking cancelled inside the ${winText}, so the hours were not returned.`);
      } else {
        setSuccess('Booking cancelled.');
      }
      setMemberBookings(prev => prev.map(x => x.id === b.memberBookingId ? { ...x, status: 'cancelled' } : x));
      if (b.memberId === currentUserUid) {
        // Only if the server actually gave them back — under the late-cancel
        // policy it may not have, and showing hours restored that aren't would
        // let them try to book time they no longer have.
        if (r?.hoursReturned !== false) {
          setMemberUsageThisWeek(prev => prev ? ({
            ...prev,
            regularUsed: Math.max(0, (prev.regularUsed || 0) - (b.regularHours || 0)),
            primeUsed: Math.max(0, (prev.primeUsed || 0) - (b.primeHours || 0)),
            weekendUsed: Math.max(0, (prev.weekendUsed || 0) - (b.weekendHours || 0)),
          }) : prev);
        }
      }
      setMemberBookingDialog(false);
      setViewingMemberBooking(null);
      // message already set above from the server's outcome
    } catch (e) {
      logError('Schedule', e.message, e.stack);
      setError('Cancel failed. Please try again.');
    }
    setMemberBookingSaving(false);
  };

  // Drag-to-create must start on genuinely empty court time.
  //
  // An event only fills part of its column — there are slivers either side, and
  // two events sharing a slot each render at half width — so a drag aimed at a
  // class can land on bare background at exactly that class's time. FullCalendar
  // hands that to select, not eventDrop, and the Create Class dialog opened
  // pre-filled over the class the admin meant to move: it looked like the drag
  // had snapped back and saved nothing.
  //
  // Keyed on the START instant only. Padding the whole span would block booking
  // right up against an existing event, which is legitimate.
  // Who may drag out a selection on the calendar. Admins draw classes with it;
  // members open their own booking dialog with it, and it is the ONLY way they
  // can — there is no "Book a court" button anywhere on this page.
  //
  // This used to also require !isCoach on both halves. On the admin half that
  // was dead weight (isCoach is defined as coach/assistant_coach AND NOT admin,
  // so it is always false when isAdmin is true). On the member half it was a
  // bug: it silently locked out anyone who both coaches and holds a membership,
  // leaving them a calendar they can read and cannot book on, with no message
  // saying why. A role describes what someone does for the club; a membership
  // describes what they have bought. Neither should cancel the other.
  //
  // Lives here, with the rest of the selection logic, rather than up beside
  // canMemberBook: isAdmin is a useState declared after that point, and reading
  // it earlier is a temporal-dead-zone crash, not a stale value.
  const canSelectOnCalendar = isAdmin || canMemberBook;

  const selectAllow = useCallback((span) => {
    const courtId = span.resource?.id;
    if (!courtId) return true;              // month/week views have no resources
    const date = dateStr(span.start);
    const startMins = span.start.getHours() * 60 + span.start.getMinutes();
    const mins = (t) => {
      const [h, m] = String(t || '0:0').split(':').map(Number);
      return h * 60 + (m || 0);
    };
    const onCourt = (v) => Array.isArray(v) ? v.includes(courtId) : v === courtId;
    const covers = (s, e) => startMins >= mins(s) && startMins < mins(e);
    const busy =
      classes.some(c => c.status !== 'canceled' && c.date === date
        && (onCourt(c.courts) || c.courtNumber === courtId) && covers(c.startTime, c.endTime)) ||
      reservations.some(r => r.date === date && onCourt(r.courts) && covers(r.startTime, r.endTime)) ||
      memberBookings.some(b => b.status !== 'cancelled' && b.date === date
        && onCourt(b.courts) && covers(b.startTime, b.endTime));
    return !busy;
  }, [classes, reservations, memberBookings]);

  const handleSelect = useCallback((info) => {
    // Active members: drag-to-select opens the member booking dialog.
    //
    // Keyed on memberTier alone, not on "not a coach". A role says what
    // someone does for the club; a membership says what they have bought.
    // They are independent, and a coach who holds a membership books their
    // own court time like any other member. Coaches without one still fall
    // through to the unauthorized log below, which is the right answer for
    // them — createMemberBooking would refuse them anyway, for having no
    // tier rather than for being a coach.
    if (!isAdmin && memberTier) {
      // Before go-live the calendar is read-only for members. Returning here
      // rather than falling through keeps this out of the coach
      // "unauthorized action" log below — it isn't a permission problem.
      if (!bookingOpen) return;
      if (info.view && info.view.type === 'dayGridMonth') return;
      const date = info.startStr.split('T')[0];
      const startTime = info.startStr.split('T')[1]?.slice(0, 5) || '18:00';
      const endTime = info.endStr.split('T')[1]?.slice(0, 5) || '19:00';
      const courtId = info.resource?.id || 'court-1';
      setMemberBookingForm({ date, startTime, endTime, court: courtId, guest: false });
      // Off for each new booking rather than sticky: filming is a per-session
      // decision, and a switch that remembers yesterday's answer streams a
      // game nobody meant to publish.
      setMemberBookingStream(false);
      setViewingMemberBooking(null);
      setMemberBookingDialog(true);
      return;
    }
    if (!isAdmin) {
      logError('Schedule', 'Unauthorized action: create event', `Coach attempted to create event on ${info.startStr}`);
      return;
    }
    // In month view, clicks select the whole day — ignore, dateClick handles nav
    if (info.view && info.view.type === 'dayGridMonth') return;
    const durationMs = info.end - info.start;
    if (durationMs <= 15 * 60 * 1000) return; // ignore single click
    const date = info.startStr.split('T')[0];
    const startTime = info.startStr.split('T')[1]?.slice(0, 5) || '17:45';
    const endTime = info.endStr.split('T')[1]?.slice(0, 5) || '19:45';
    const courtId = info.resource?.id || 'court-1';
    setPendingSelect({ date, startTime, endTime, courtId });
    setSelectedClass(null);
    setClassForm({
      ...emptyClassForm,
      date,
      startTime,
      endTime,
      courtNumber: courtId,
      courts: [courtId],
    });
    setClassDialog(true);
  }, [isAdmin, memberTier, bookingOpen]);

  const handleEventClick = useCallback((info) => {
    const props = info.event.extendedProps;
    // TEMP DEBUG: confirm the click handler fires at all and see the event props.
    console.log('[handleEventClick]', { eventType: props.eventType, props });
    // School holds are a twin of a schoolSessions record; editing the twin here
    // would leave the two disagreeing. Admins go to the school schedule.
    if (props.eventType === 'reservation' && props.type === 'school') {
      if (isAdmin) window.location.assign(`/admin/schools?tab=schedule&date=${props.date}`);
      return;
    }
    if (props.eventType === 'memberBooking') {
      // Own booking (or admin / court admin): open view/cancel dialog
      if (props.memberId === currentUserUid || isAdmin || isCourtAdmin) {
        setViewingMemberBooking({ ...props });
        setMoveToCourt('');
        setMemberBookingDialog(true);
      }
      return;
    }
    if (props.eventType === 'class') {
      // All users get readonly view — admins get an Edit button inside
      setViewingClass({ id: info.event.id, ...props });
      setViewingAttendance([]);
      setViewingAthletes([]);
      setRosterSearch('');
      setClassLogs([]);
      setLogInput('');
      setCoachSectionOpen(false);
      setClassViewDialog(true);
      setAttendanceNotice(null);
      setAttendanceLoading(true);
      setAthletesLoading(true);
      const classType = (props.sessionType || '').toLowerCase();
      const classSkill = (props.skillGroup || '').toLowerCase();
      getDocs(collection(db, 'athletes')).then(snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(a => a.athleteType !== 'unknown')
          .filter(a => !a.isArchived);
        // Get coach emails to exclude from athlete list
        const headCoach = coaches.find(co => co.id === props.headCoachId);
        const assistantCoachIds = props.assistantCoachIds || [];
        const coachEmails = new Set([
          ...(headCoach ? [headCoach.email] : []),
          ...coaches.filter(co => assistantCoachIds.includes(co.id)).map(co => co.email),
        ].filter(Boolean).map(e => e.toLowerCase()));
        // Players (non-admin, non-coach) only see themselves and their children
        const isPlayer = !isAdmin && !isCoach;
        const isAdult = classType.startsWith('adult');
        // TEMP DEBUG — remove after diagnosing "No athletes found" issue.
        // Shows filter inputs for player check-in. Logs once per dialog open.
        console.log('[CheckInFilter]', {
          isPlayer, isAdmin, isCoach, isAdult, classType,
          currentUserEmail, currentUserUid,
          allCount: all.length,
          sampleEmails: all.slice(0, 5).map(a => ({ id: a.id, email: a.email, type: a.athleteType, parentUserId: a.parentUserId })),
        });
        const filtered = all.filter(a => {
          if (a.email && coachEmails.has(a.email.toLowerCase())) return false;
          // Restrict players to self + their own children
          if (isPlayer) {
            const isSelf = a.email && currentUserEmail && a.email.toLowerCase() === currentUserEmail;
            const isChild = a.parentUserId && currentUserUid && a.parentUserId === currentUserUid;
            if (!isSelf && !isChild) return false;
          }
          const aType = (a.athleteType || '').toLowerCase();
          // Strict match: junior classes only show juniors, adult classes only show adults
          // A league has no age group of its own: list everyone.
          if (classType !== 'league') {
            if (isAdult && aType !== 'adult') return false;
            if (!isAdult && aType !== 'junior') return false;
          }
          return true;
        });
        // Dedupe: for adults match by email; for juniors NEVER match by email
        // (siblings share their parent's email and would collide). Match juniors
        // by firstName+lastName+parentUserId+birthday — distinguishing actual duplicates.
        const seen = new Map();
        for (const a of filtered) {
          const aType = (a.athleteType || '').toLowerCase();
          const isJunior = aType === 'junior';
          const emailKey = (a.email || '').toLowerCase().trim();
          const nameKey = `${(a.firstName || '').toLowerCase().trim()}|${(a.lastName || '').toLowerCase().trim()}|${aType}|${a.parentUserId || ''}|${a.birthday || ''}`;
          // Juniors: name-based key only (siblings share parent email).
          // Adults: prefer email key, fallback to name.
          const key = isJunior ? nameKey : (emailKey || nameKey);
          if (!key) { seen.set(a.id, a); continue; } // no identifying info — keep by id
          if (!seen.has(key)) {
            seen.set(key, a);
          } else {
            // Keep the one with more data (sessionBalance set, or higher balance, or has a parentUserId)
            const existing = seen.get(key);
            const score = (x) => (x.sessionBalance !== undefined ? 1 : 0) + (x.parentUserId ? 1 : 0) + (x.skillGroup ? 1 : 0);
            if (score(a) > score(existing)) seen.set(key, a);
          }
        }
        const deduped = Array.from(seen.values());
        setViewingAthletes(deduped.sort((a, b) => `${a.firstName}${a.lastName}`.localeCompare(`${b.firstName}${b.lastName}`)));
        setAthletesLoading(false);
      }).catch((err) => {
        // Surface to the UI and console so we can diagnose. Previously this
        // catch silently set loading to false, hiding permission and network
        // errors behind a generic "No athletes found" empty state.
        console.error('[CheckInFilter] athletes load failed:', err);
        setError('Failed to load athletes: ' + (err?.message || 'unknown error'));
        setAthletesLoading(false);
      });
      getDocs(query(collection(db, 'attendance'), where('classId', '==', info.event.id)))
        .then(async snap => {
          const records = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          // Enrich from the roster this handler already fetched rather than a
          // getDoc per attendance record — plus another per junior for the
          // parent email. That was up to 2 round trips per person on the list,
          // re-reading documents already in memory.
          const athById = new Map();
          try {
            const athSnap = await getDocs(collection(db, 'athletes'));
            athSnap.docs.forEach(d => athById.set(d.id, d.data()));
          } catch { /* enrichment is cosmetic — records still render */ }
          const parentIds = [...new Set(records
            .map(r => athById.get(r.athleteId))
            .filter(a => a && !a.email && a.parentUserId)
            .map(a => a.parentUserId))];
          const parentEmail = new Map();
          for (let i = 0; i < parentIds.length; i += 30) {
            try {
              const chunk = parentIds.slice(i, i + 30);
              const uSnap = await getDocs(query(collection(db, 'users'), where('__name__', 'in', chunk)));
              uSnap.docs.forEach(d => parentEmail.set(d.id, d.data().email || ''));
            } catch { /* leave those emails blank */ }
          }
          const enriched = records.map(r => {
            const ath = r.athleteId ? athById.get(r.athleteId) : null;
            if (!ath) return r;
            const email = ath.email || (ath.parentUserId ? (parentEmail.get(ath.parentUserId) || '') : '');
            return { ...r, schoolName: ath.schoolName, currentGrade: ath.currentGrade, graduationYear: ath.graduationYear, email };
          });
          setViewingAttendance(enriched);
        })
        .catch(() => {})
        .finally(() => setAttendanceLoading(false));

      // Activity for the "most active" ordering. One read of the ledger, cached
      // for the session — reopening a class does not refetch it.
      if (athleteActivity === null && (isAdmin || isCoach)) {
        getDocs(collection(db, 'attendance')).then(snap => {
          const since = dateStr(new Date(Date.now() - ROSTER_ACTIVITY_DAYS * 86400000));
          const map = {};
          snap.docs.forEach(d => {
            const r = d.data();
            // A reservation is an intention, not a visit.
            if (r.athleteId && r.status !== 'reserved' && (r.date || '') >= since) {
              map[r.athleteId] = (map[r.athleteId] || 0) + 1;
            }
          });
          setAthleteActivity(map);
        }).catch(() => setAthleteActivity({}));
      }
      // Load class logs
      getDocs(query(collection(db, 'classes', info.event.id, 'logs'), orderBy('timestamp', 'asc')))
        .then(snap => setClassLogs(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
        .catch(() => {});
      return;
    } else {
      if (!isAdmin) return; // members/coaches never open the admin reservation editor
      if (!props.reservationId && !props.id) return; // unknown event shape — don't open a broken editor
      setSelectedRes({ id: props.reservationId, ...props });
      setResForm({
        title: props.title || '',
        type: props.type || 'other',
        notes: props.notes || '',
        courts: Array.isArray(props.courts) ? props.courts : [props.courts].filter(Boolean),
        startTime: props.startTime || '',
        endTime: props.endTime || '',
        date: props.date || '',
      });
      setResDialog(true);
    }
  }, [isAdmin, isCoach, isCourtAdmin, currentUserEmail, currentUserUid]);

  const handleEventDrop = useCallback((info) => {
    if (!isAdmin) {
      info.revert();
      logError('Schedule', 'Unauthorized action: move event', `Coach attempted to move "${info.event.title}" to ${info.event.startStr}`);
      return;
    }
    const props = info.event.extendedProps;
    if (props.eventType === 'memberBooking') {
      info.revert();
      setError("Member bookings can't be moved yet — cancel and rebook, or adjust via the booking dialog.");
      setTimeout(() => setError(''), 5000);
      return;
    }

    if (props.eventType === 'reservation' && props.type === 'school') {
      info.revert();
      setError('School sessions are moved on the School Schedule page.');
      setTimeout(() => setError(''), 5000);
      return;
    }

    if (props.eventType === 'reservation') {
      const newDate = info.event.startStr.split('T')[0];
      const newStart = info.event.start.toTimeString().slice(0, 5);
      const newEnd = info.event.end?.toTimeString().slice(0, 5) || props.endTime;
      const newCourt = info.newResource?.id;
      const res = reservations.find(r => r.id === props.reservationId);
      if (!res) { info.revert(); return; }
      const updatedCourts = newCourt
        ? res.courts.map(c => c === info.oldResource?.id ? newCourt : c)
        : res.courts;
      updateDoc(doc(db, 'reservations', props.reservationId), {
        date: newDate, startTime: newStart, endTime: newEnd, courts: updatedCourts,
      }).then(() => {
        setReservations(prev => prev.map(r =>
          r.id === props.reservationId
            ? { ...r, date: newDate, startTime: newStart, endTime: newEnd, courts: updatedCourts }
            : r
        ));
      }).catch(e => { info.revert(); logError('Schedule', e.message, e.stack); });
      return;
    }

    // Class drop
    const newStart = info.event.start;
    const newEnd = info.event.end;
    const newDate = dateStr(newStart);
    const newStartTime = newStart.toTimeString().slice(0, 5);
    const newEndTime = newEnd ? newEnd.toTimeString().slice(0, 5) : props.endTime;
    const newCourt = info.newResource?.id || props.courtNumber;

    if (hasOverlap(newDate, newStartTime, newEndTime, newCourt, info.event.id) && false) {
      info.revert();
      setError('This time slot conflicts with another class on the same court.');
      return;
    }
    // A drag moves THIS occurrence, recurring or not, and applies straight away.
    // Nearly every class here belongs to a series, so asking This / future / all
    // on every drag put a modal in front of the common case — nudging one
    // practice. Series-wide time and court changes go through the edit dialog,
    // which still asks for scope. Undo below is the way back from a mis-drop.
    const update = {
      date: newDate, startTime: newStartTime, endTime: newEndTime, courtNumber: newCourt,
      durationMinutes: calcDuration(newStartTime, newEndTime),
    };
    const before = {
      date: props.date, startTime: props.startTime, endTime: props.endTime,
      courtNumber: props.courtNumber,
      durationMinutes: calcDuration(props.startTime, props.endTime),
    };
    updateDoc(doc(db, 'classes', info.event.id), update).then(() => {
      setClasses(prev => prev.map(c => c.id === info.event.id ? { ...c, ...update } : c));
      setUndoMove({ id: info.event.id, before });
      const movedCourt = newCourt !== props.courtNumber;
      setSuccess(`Moved to ${newStartTime}–${newEndTime}`
        + `${movedCourt ? ` on Court ${courtNum(newCourt)}` : ''}`
        + `${newDate !== props.date ? ` on ${newDate}` : ''}.`
        + (props.repeatGroupId ? ' This date only — edit the class to change the series.' : ''));
      setTimeout(() => { setSuccess(''); setUndoMove(null); }, 8000);
    }).catch(e => { info.revert(); logError('Schedule', e.message, e.stack); });
  }, [isAdmin, hasOverlap, reservations]);

  const handleEventResize = useCallback((info) => {
    if (!isAdmin) {
      info.revert();
      logError('Schedule', 'Unauthorized action: resize event', `Coach attempted to resize "${info.event.title}" ending ${info.event.endStr}`);
      return;
    }
    const props = info.event.extendedProps;
    if (props.eventType === 'memberBooking') {
      info.revert();
      setError("Member bookings can't be moved yet — cancel and rebook, or adjust via the booking dialog.");
      setTimeout(() => setError(''), 5000);
      return;
    }
    const newEnd = info.event.end?.toTimeString().slice(0, 5);
    if (!newEnd) { info.revert(); return; }

    if (props.eventType === 'reservation' && props.type === 'school') {
      info.revert();
      setError('School sessions are changed on the School Schedule page.');
      setTimeout(() => setError(''), 5000);
      return;
    }
    if (props.eventType === 'reservation') {
      updateDoc(doc(db, 'reservations', props.reservationId), { endTime: newEnd })
        .then(() => setReservations(prev => prev.map(r =>
          r.id === props.reservationId ? { ...r, endTime: newEnd } : r
        )))
        .catch(e => { info.revert(); logError('Schedule', e.message, e.stack); });
    } else {
      updateDoc(doc(db, 'classes', info.event.id), {
        endTime: newEnd, durationMinutes: calcDuration(props.startTime, newEnd),
      }).then(() => setClasses(prev => prev.map(c =>
        c.id === info.event.id ? { ...c, endTime: newEnd } : c
      ))).catch(e => { info.revert(); logError('Schedule', e.message, e.stack); });
    }
  }, [isAdmin]);

  // ── Open create dialog ──────────────────────────────────────────────────────
  const openCreateClass = () => {
    setSelectedClass(null);
    setClassForm({
      ...emptyClassForm,
      date: pendingSelect?.date || '',
      startTime: pendingSelect?.startTime || '17:45',
      endTime: pendingSelect?.endTime || '19:45',
      courtNumber: pendingSelect?.courtId || 'court-1',
    });
    setCreateDialog(false);
    setClassDialog(true);
  };

  const openCreateReservation = () => {
    setSelectedRes(null);
    setResForm({
      ...emptyResForm,
      date: pendingSelect?.date || '',
      startTime: pendingSelect?.startTime || '',
      endTime: pendingSelect?.endTime || '',
      courts: pendingSelect?.courtId ? [pendingSelect.courtId] : [],
    });
    setCreateDialog(false);
    setResDialog(true);
  };

  // ── Save class ──────────────────────────────────────────────────────────────
  const handleSaveClass = async () => {
    if (!classForm.date || !classForm.startTime || !classForm.endTime) {
      setError('Please fill in date, start time, and end time.'); return;
    }
    if (classForm.repeat && (classForm.repeatDays || []).length === 0) {
      setError('Please select at least one repeat day.'); return;
    }
    if (classForm.repeat && !classForm.repeatUntil) {
      setError('Please select a repeat end date.'); return;
    }
    const selectedCourts = classForm.courts?.length > 0 ? classForm.courts : [classForm.courtNumber || 'court-1'];

    // This class's other courts AS THEY STAND NOW. courtSiblings matches on the
    // class's current date and time, and the form may be about to change both,
    // so the set has to be captured before anything is written — afterwards the
    // anchor no longer matches its own siblings and they'd be invisible.
    const siblingsBefore = selectedClass
      ? courtSiblings(selectedClass).filter(c => c.id !== selectedClass.id)
      : [];
    // Courts a document already exists for. Those get moved, never re-created.
    const courtsAlreadyHeld = new Set([
      ...(selectedClass ? [selectedClass.courtNumber] : []),
      ...siblingsBefore.filter(c => selectedCourts.includes(c.courtNumber)).map(c => c.courtNumber),
    ]);

    // Editing an existing class and picking extra courts has to CREATE those
    // classes: one document is one court (the calendar keys events off
    // courtNumber), which is exactly what the create path below does. Before
    // this, the edit path wrote only courtNumber — the first selected court —
    // and silently dropped the rest, so a second court neither saved nor
    // appeared.
    const addExtraCourtClasses = async (baseData, keepCourt) => {
      const extras = selectedCourts.filter(c => c !== keepCourt && !courtsAlreadyHeld.has(c));
      const created = []; const skipped = [];
      const usedSlugs = new Set(classes.filter(c => c.slug).map(c => c.slug));
      for (const court of extras) {
        const already = classes.some(c => c.id !== selectedClass?.id
          && c.date === baseData.date && c.startTime === baseData.startTime
          && c.courtNumber === court && c.status !== 'canceled');
        // Already part of this class's own set — nothing to create and nothing
        // to report. Only a clash with SOMEONE ELSE's booking is a skip worth
        // telling the admin about; reporting both made re-saving an unchanged
        // multi-court class claim its own courts were "already booked".
        if (already) continue;
        if (hasOverlap(baseData.date, baseData.startTime, baseData.endTime, court)) {
          skipped.push(court); continue;
        }
        const base = defaultClassSlug({
          eventName: baseData.eventName, sessionType: baseData.sessionType,
          skillGroup: baseData.skillGroup, date: baseData.date, courtNumber: court,
        });
        let finalSlug = base; let i = 2;
        while (usedSlugs.has(finalSlug)) { finalSlug = `${base}-${i++}`; }
        usedSlugs.add(finalSlug);
        const data = { ...baseData, courtNumber: court, slug: finalSlug };
        const ref = await addDoc(collection(db, 'classes'), data);
        created.push({ id: ref.id, ...data });
      }
      if (created.length) setClasses(prev => [...prev, ...created]);
      return { created, skipped };
    };

    // Unticking a court deletes that court's class. A class carrying check-ins
    // is history — it is never removed by a form change, only reported back, so
    // an admin can't wipe attendance by fiddling with the picker. The anchor
    // itself is excluded: its court comes from courtNumber and is always kept.
    const removeUnselectedCourtClasses = async () => {
      const drop = siblingsBefore.filter(c => !selectedCourts.includes(c.courtNumber));
      if (!drop.length) return { removed: [], blocked: [] };
      const counts = await attendeeCountsFor(drop);
      const blocked = drop.filter(c => (counts[c.id] || 0) > 0);
      const removable = drop.filter(c => !(counts[c.id] > 0));
      if (removable.length) {
        const list = removable.map(c => `Court ${courtNum(c.courtNumber)}`).join(', ');
        const ok = window.confirm(
          `Remove ${list} from this class?\n\n`
          + `${removable.length === 1 ? 'That court’s class' : 'Those courts’ classes'} will be deleted.`);
        if (!ok) return { removed: [], blocked, cancelled: true };
        const batch = writeBatch(db);
        removable.forEach(c => batch.delete(doc(db, 'classes', c.id)));
        await batch.commit();
        const goneIds = new Set(removable.map(c => c.id));
        setClasses(prev => prev.filter(c => !goneIds.has(c.id)));
      }
      return { removed: removable, blocked };
    };

    // Carry the edit onto the class's other courts. Only the anchor document
    // holds the values the form edits; without this the siblings keep the OLD
    // date and time and are stranded at the original slot, no longer matching
    // the class they belong to. Each keeps its own courtNumber, and its own
    // slug (classData carries none), so only the shared fields move.
    //
    // Everything still present is moved — including a court unticked but kept
    // because it has check-ins, or because the delete was declined. Those are
    // still part of the class; leaving them behind is the orphan this fixes.
    const moveSiblingCourtClasses = async (baseData, deletedIds) => {
      const toMove = siblingsBefore.filter(c => !deletedIds.has(c.id));
      if (!toMove.length) return { moved: [] };
      const { courtNumber, ...shared } = baseData;
      const batch = writeBatch(db);
      toMove.forEach(c => batch.update(doc(db, 'classes', c.id), shared));
      await batch.commit();
      const movedIds = new Set(toMove.map(c => c.id));
      setClasses(prev => prev.map(c => movedIds.has(c.id) ? { ...c, ...shared } : c));
      return { moved: toMove };
    };

    // One sentence covering everything the save did to the court set.
    const courtSummary = (extra, gone, carried) => {
      const bits = [];
      if (extra.created.length) bits.push(`Added ${extra.created.length} court${extra.created.length === 1 ? '' : 's'}.`);
      if (gone.removed.length) bits.push(`Removed ${gone.removed.length} court${gone.removed.length === 1 ? '' : 's'}.`);
      if (carried.moved.length) bits.push(`Applied to ${carried.moved.length} other court${carried.moved.length === 1 ? '' : 's'}.`);
      if (extra.skipped.length) bits.push(`Skipped ${extra.skipped.map(courtNum).join(', ')} — already booked at that time.`);
      if (gone.blocked.length) bits.push(`Kept ${gone.blocked.map(c => courtNum(c.courtNumber)).join(', ')} — ${gone.blocked.length === 1 ? 'it has' : 'they have'} check-ins.`);
      return bits.join(' ');
    };

    // One class per chosen weekday per court, from `fromDate` to Repeat until,
    // all under one repeatGroupId. A date whose court is already taken at that
    // time is skipped and counted. `includeFrom` is false when the first date
    // already exists — a one-off being turned into a series keeps its own
    // documents (and their check-ins and logs) as the series' first date.
    const createRepeatDates = async (baseData, repeatGroupId, fromDate, includeFrom) => {
      const until = new Date(classForm.repeatUntil + 'T12:00:00');
      const current = new Date(fromDate + 'T12:00:00');
      if (!includeFrom) current.setDate(current.getDate() + 1);
      const created = []; let skipped = 0;
      const usedSlugs = new Set(classes.filter(c => c.slug).map(c => c.slug));
      while (current <= until) {
        const dayName = DAY_NAMES[current.getDay()];
        if ((classForm.repeatDays || []).includes(dayName)) {
          const dStr = dateStr(current);
          for (const court of selectedCourts) {
            if (hasOverlap(dStr, baseData.startTime, baseData.endTime, court)) {
              skipped++;
            } else {
              const baseSlug = defaultClassSlug({ eventName: baseData.eventName, sessionType: baseData.sessionType, skillGroup: baseData.skillGroup, date: dStr, courtNumber: court });
              let finalSlug = baseSlug;
              let i = 2;
              while (usedSlugs.has(finalSlug)) { finalSlug = `${baseSlug}-${i++}`; }
              usedSlugs.add(finalSlug);
              const data = { ...baseData, date: dStr, courtNumber: court, repeatGroupId, slug: finalSlug };
              const ref = await addDoc(collection(db, 'classes'), data);
              created.push({ id: ref.id, ...data });
            }
          }
        }
        current.setDate(current.getDate() + 1);
      }
      if (created.length) setClasses(prev => [...prev, ...created]);
      return { created, skipped };
    };
    setSaving(true); setError('');
    try {
      const classData = {
        eventName: classForm.eventName || '',
        customerId: classForm.customerId || '',
        customerName: classForm.customerId ? customerDisplayName(customers.find(c => c.id === classForm.customerId)) : '',
        eventColor: classForm.eventColor || '',
        description: classForm.description || '',
        scoreboardUseDescription: classForm.scoreboardUseDescription === true,
        notes: classForm.notes || '',
        qrPlanId: classForm.qrPlanId || '',
        sessionType: classForm.sessionType,
        skillGroup: classForm.skillGroup,
        date: classForm.date,
        startTime: classForm.startTime,
        endTime: classForm.endTime,
        headCoachId: classForm.headCoachId,
        assistantCoachIds: classForm.assistantCoachIds,
        courtNumber: classForm.courtNumber,
        status: classForm.status,
        cancelReason: classForm.cancelReason,
        // '' -> null (unset), '0' -> 0 (explicitly unlimited). Both read as
        // no cap downstream, but 0 round-trips back into the form.
        maxAthletes: String(classForm.maxAthletes ?? '').trim() === '' ? null : parseInt(classForm.maxAthletes, 10),
        ...(classForm.seasonId ? { seasonId: classForm.seasonId } : {}),
        durationMinutes: calcDuration(classForm.startTime, classForm.endTime),
      };
      if (selectedClass) {
        if (classForm.repeat && !selectedClass.repeatGroupId) {
          // Editing a single class but enabling repeat — slug stays as-is/regenerated for the original
          const existingSlug = classForm.slug || selectedClass.slug || defaultClassSlug({ ...classData, eventName: classData.eventName, sessionType: classData.sessionType, skillGroup: classData.skillGroup, date: classData.date, courtNumber: classData.courtNumber });
          const finalSlug = ensureUniqueSlug(existingSlug, selectedClass.id);
          const dataWithSlug = { ...classData, slug: finalSlug };
          await updateDoc(doc(db, 'classes', selectedClass.id), dataWithSlug);
          setClasses(prev => prev.map(c => c.id === selectedClass.id ? { ...c, ...dataWithSlug } : c));
          const gone = await removeUnselectedCourtClasses();
          const carried = await moveSiblingCourtClasses(
            classData, new Set(gone.removed.map(c => c.id)));
          const extra = await addExtraCourtClasses(classData, classData.courtNumber);
          const courtNote = courtSummary(extra, gone, carried);
          // A one-off becoming weekly. Its own date's documents — this court and
          // every other court it is on — join the new series as its first date,
          // so a later "all classes" edit or delete reaches them too, and the
          // rest of the dates are created after it.
          const repeatGroupId = `group_${Date.now()}`;
          const firstDate = [selectedClass, ...carried.moved, ...extra.created];
          const groupBatch = writeBatch(db);
          firstDate.forEach(c => groupBatch.update(doc(db, 'classes', c.id), { repeatGroupId }));
          await groupBatch.commit();
          const firstIds = new Set(firstDate.map(c => c.id));
          setClasses(prev => prev.map(c => firstIds.has(c.id) ? { ...c, repeatGroupId } : c));
          const series = await createRepeatDates(classData, repeatGroupId, classData.date, false);
          setClassDialog(false); setSelectedClass(null); setClassForm(emptyClassForm);
          setSuccess(`Class is now weekly: ${series.created.length} more class${series.created.length === 1 ? '' : 'es'} created`
            + `${series.skipped ? `, ${series.skipped} skipped due to conflicts` : ''}.${courtNote ? ` ${courtNote}` : ''}`);
          setTimeout(() => setSuccess(''), 8000);
          if (selectedClass.status !== 'canceled' && classData.status === 'canceled') {
            logEvent('class.canceled', {
              target: { id: selectedClass.id, name: classData.eventName || classData.sessionType, date: classData.date },
              description: `Class canceled via edit dialog: ${classData.cancelReason || 'no reason given'}`,
              metadata: { cancelReason: classData.cancelReason || '', via: 'edit-dialog' },
            });
            // The cancel rode along to the other courts via moveSiblingCourtClasses,
            // so their check-ins have to be refunded too — refunding the anchor
            // alone would cancel those athletes without giving the session back.
            await refundCheckInsForClasses([selectedClass.id, ...carried.moved.map(c => c.id)]);
          }
          if (selectedClass.status === 'canceled' && classData.status !== 'canceled') {
            logEvent('class.reactivated', {
              target: { id: selectedClass.id, name: classData.eventName || classData.sessionType, date: classData.date },
              description: `Class reactivated via edit dialog (status: ${classData.status})`,
              metadata: { newStatus: classData.status, via: 'edit-dialog' },
            });
          }
          // Done: without this the save fell through to the new-series path
          // below and created every date a second time.
          setSaving(false); return;
        } else {
          if (selectedClass.repeatGroupId) {
            setSaving(false); setUpdateScopeDialog(true); return;
          }
          const proposedSlug = classForm.slug || selectedClass.slug || defaultClassSlug({ eventName: classData.eventName, sessionType: classData.sessionType, skillGroup: classData.skillGroup, date: classData.date, courtNumber: classData.courtNumber });
          const finalSlug = ensureUniqueSlug(proposedSlug, selectedClass.id);
          const dataWithSlug = { ...classData, slug: finalSlug };
          await updateDoc(doc(db, 'classes', selectedClass.id), dataWithSlug);
          setClasses(prev => prev.map(c => c.id === selectedClass.id ? { ...c, ...dataWithSlug } : c));
          const gone = await removeUnselectedCourtClasses();
          const carried = await moveSiblingCourtClasses(
            classData, new Set(gone.removed.map(c => c.id)));
          const extra = await addExtraCourtClasses(classData, classData.courtNumber);
          const courtNote = courtSummary(extra, gone, carried);
          if (courtNote) {
            setSuccess(`Class updated. ${courtNote}`);
            setTimeout(() => setSuccess(''), 6000);
          }
          if (selectedClass.status !== 'canceled' && classData.status === 'canceled') {
            logEvent('class.canceled', {
              target: { id: selectedClass.id, name: classData.eventName || classData.sessionType, date: classData.date },
              description: `Class canceled via edit dialog: ${classData.cancelReason || 'no reason given'}`,
              metadata: { cancelReason: classData.cancelReason || '', via: 'edit-dialog' },
            });
            // The cancel rode along to the other courts via moveSiblingCourtClasses,
            // so their check-ins have to be refunded too — refunding the anchor
            // alone would cancel those athletes without giving the session back.
            await refundCheckInsForClasses([selectedClass.id, ...carried.moved.map(c => c.id)]);
          }
          if (selectedClass.status === 'canceled' && classData.status !== 'canceled') {
            logEvent('class.reactivated', {
              target: { id: selectedClass.id, name: classData.eventName || classData.sessionType, date: classData.date },
              description: `Class reactivated via edit dialog (status: ${classData.status})`,
              metadata: { newStatus: classData.status, via: 'edit-dialog' },
            });
          }
          setClassDialog(false); setSelectedClass(null); setClassForm(emptyClassForm);
          setSuccess('Class updated.'); setTimeout(() => setSuccess(''), 4000);
          setSaving(false); return;
        }
      }
      if (classForm.repeat) {
        const repeatGroupId = `group_${Date.now()}`;
        const { created, skipped } = await createRepeatDates(classData, repeatGroupId, classForm.date, true);
        setClassDialog(false); setClassForm(emptyClassForm);
        setSuccess(`Created ${created.length} classes.${skipped > 0 ? ` Skipped ${skipped} due to conflicts.` : ''}`);
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const newClasses = [];
        const usedSlugs = new Set(classes.filter(c => c.slug).map(c => c.slug));
        for (const court of selectedCourts) {
          const baseSlug = classForm.slug ? slugify(classForm.slug) : defaultClassSlug({ eventName: classData.eventName, sessionType: classData.sessionType, skillGroup: classData.skillGroup, date: classData.date, courtNumber: court });
          let finalSlug = baseSlug;
          let i = 2;
          while (usedSlugs.has(finalSlug)) { finalSlug = `${baseSlug}-${i++}`; }
          usedSlugs.add(finalSlug);
          const data = { ...classData, courtNumber: court, slug: finalSlug };
          const ref = await addDoc(collection(db, 'classes'), data);
          newClasses.push({ id: ref.id, ...data });
        }
        setClasses(prev => [...prev, ...newClasses]);
        setClassDialog(false); setClassForm(emptyClassForm);
        logEvent('class.created', {
          description: `Created ${newClasses.length} class${newClasses.length > 1 ? 'es' : ''}: ${newClasses[0]?.eventName || newClasses[0]?.sessionType || 'class'}${newClasses.length > 1 ? ` (+${newClasses.length - 1} more)` : ` (${newClasses[0]?.date || ''})`}`,
          metadata: { count: newClasses.length, firstClassId: newClasses[0]?.id || '' },
        });
        setSuccess(`Created ${newClasses.length} class${newClasses.length > 1 ? 'es' : ''}.`); setTimeout(() => setSuccess(''), 4000);
      }
    } catch (err) {
      logError('Schedule', err.message, err.stack);
      setError('Failed to save class: ' + (err.message || err.code || 'Unknown error'));
    }
    setSaving(false);
  };

  // ── Save reservation ────────────────────────────────────────────────────────
  const handleSaveRes = async () => {
    if (!resForm.title.trim()) { setError('Please enter a title.'); return; }
    if (!resForm.date) { setError('Please select a date.'); return; }
    if (!resForm.startTime || !resForm.endTime) { setError('Please set start and end times.'); return; }
    if (resForm.courts.length === 0) { setError('Please select at least one court.'); return; }
    setSaving(true); setError('');
    try {
      const data = {
        title: resForm.title.trim(), type: resForm.type, notes: resForm.notes,
        courts: resForm.courts, date: resForm.date,
        startTime: resForm.startTime, endTime: resForm.endTime,
      };
      if (selectedRes) {
        if (!selectedRes.id) { setError('This reservation could not be identified. Close and reopen it, or recreate it.'); setSaving(false); return; }
        await updateDoc(doc(db, 'reservations', selectedRes.id), data);
        setReservations(prev => prev.map(r => r.id === selectedRes.id ? { ...r, ...data } : r));
        setSuccess('Reservation updated.');
      } else {
        const ref = await addDoc(collection(db, 'reservations'), { ...data, createdAt: new Date().toISOString() });
        setReservations(prev => [...prev, { id: ref.id, ...data }]);
        setSuccess('Reservation created.');
      }
      setResDialog(false); setResForm(emptyResForm); setSelectedRes(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('Schedule', err.message, err.stack);
      setError('Failed to save: ' + err.message);
    }
    setSaving(false);
  };

  // ── Delete class ────────────────────────────────────────────────────────────
  // Resolve which classes a scope covers. Used both to show the count before
  // deleting and to run the delete, so the confirmation can't disagree with
  // what actually happens.
  // A repeating series covers every court it was created on under one
  // repeatGroupId, so scoping on repeatGroupId alone deletes sibling courts
  // too. Default to the court that was clicked; all-courts is opt-in.
  // courtNumber is stored as "court-4"; show it the way the calendar does.
  const courtNum = (v) => String(v || '').replace('court-', '');
  const seriesCourts = (anchor = selectedClass) => (anchor?.repeatGroupId
    ? [...new Set(classes.filter(c => c.repeatGroupId === anchor.repeatGroupId)
        .map(c => c.courtNumber).filter(Boolean))]
        .sort((a, b) => (parseInt(courtNum(a), 10) || 99) - (parseInt(courtNum(b), 10) || 99))
    : []);

  // The other courts THIS class runs on. One document is one court, so a class
  // spanning courts 1–3 is three docs; the edit dialog needs the whole set or
  // it shows a single court selected and "adding" an existing court silently
  // does nothing. Siblings share the date and time slot, plus either the
  // repeatGroupId (recurring) or the identifying fields (one-off) — matching
  // the duplicate test addExtraCourtClasses already applies.
  const courtSiblings = (anchor) => {
    if (!anchor?.date || !anchor?.startTime) return [];
    return classes.filter(c =>
      c.status !== 'canceled' &&
      c.date === anchor.date &&
      c.startTime === anchor.startTime &&
      c.endTime === anchor.endTime &&
      (anchor.repeatGroupId
        ? c.repeatGroupId === anchor.repeatGroupId
        : !c.repeatGroupId &&
          c.sessionType === anchor.sessionType &&
          (c.eventName || '') === (anchor.eventName || '') &&
          (c.skillGroup || '') === (anchor.skillGroup || ''))
    );
  };

  // Courts for the picker, anchor's own court first so courtNumber stays stable.
  const courtsForEdit = (anchor) => {
    const own = anchor?.courtNumber || 'court-1';
    const rest = courtSiblings(anchor).map(c => c.courtNumber).filter(c => c && c !== own);
    return [own, ...[...new Set(rest)]];
  };

  const classesForScope = (scope, anchor = selectedClass, allCourts = deleteAllCourts) => {
    if (!anchor) return [];
    if (scope === 'this') return [anchor];
    const inGroup = classes.filter(c => c.repeatGroupId === anchor.repeatGroupId
      && (allCourts || c.courtNumber === anchor.courtNumber));
    return scope === 'future' ? inGroup.filter(c => c.date >= anchor.date) : inGroup;
  };

  // Check-in counts for a set of classes, matching how the P&L counts an
  // attendee: checked in, not a coach. Chunked at 30 for Firestore's `in` limit.
  const attendeeCountsFor = async (classList) => {
    const counts = {};
    const ids = classList.map(c => c.id);
    for (let i = 0; i < ids.length; i += 30) {
      const snap = await getDocs(query(
        collection(db, 'attendance'), where('classId', 'in', ids.slice(i, i + 30))
      ));
      snap.docs.forEach(d => {
        const a = d.data();
        if (a.type === 'coach' || !a.checkedInAt) return;
        counts[a.classId] = (counts[a.classId] || 0) + 1;
      });
    }
    return counts;
  };

  // A class that has already happened AND has check-ins is history — deleting it
  // orphans those records and silently rewrites past attendance and revenue.
  // Cancel it instead; that keeps the class and its check-ins.
  const protectedFromDelete = async (classList) => {
    const today = localToday();
    const candidates = classList.filter(c => c.date && c.date <= today);
    if (!candidates.length) return new Set();
    const counts = await attendeeCountsFor(candidates);
    return new Set(candidates.filter(c => (counts[c.id] || 0) > 0).map(c => c.id));
  };

  const handleDeleteClass = async (scope, explicitList = null) => {
    setDeleteScopeDialog(false);
    setDeleteConfirm(null);
    const toDelete = explicitList || classesForScope(scope);
    if (!toDelete.length) return;
    const dates = toDelete.map(c => c.date).filter(Boolean).sort();
    try {
      // Record every id, not just the anchor: a "future" delete can remove
      // dozens of docs, and without the full list a later restore has nothing
      // to work from except a point-in-time snapshot.
      logEvent('class.deleted', {
        description: `Deleted ${toDelete.length} class${toDelete.length === 1 ? '' : 'es'}`
          + ` (${scope === 'this' ? 'single' : scope === 'future' ? 'this and future in series' : 'entire series'}): `
          + `${selectedClass?.eventName || selectedClass?.sessionType || ''} (${dates[0] || ''}`
          + `${dates.length > 1 ? ` → ${dates[dates.length - 1]}` : ''})`,
        metadata: {
          classId: selectedClass?.id || '', scope,
          repeatGroupId: selectedClass?.repeatGroupId || '',
          count: toDelete.length,
          firstDate: dates[0] || '', lastDate: dates[dates.length - 1] || '',
          courts: [...new Set(toDelete.map(c => c.courtNumber).filter(v => v != null))],
          classIds: toDelete.map(c => c.id),
        },
      });
      if (toDelete.length === 1) {
        await deleteDoc(doc(db, 'classes', toDelete[0].id));
      } else {
        for (let i = 0; i < toDelete.length; i += 400) {
          const batch = writeBatch(db);
          toDelete.slice(i, i + 400).forEach(c => batch.delete(doc(db, 'classes', c.id)));
          await batch.commit();
        }
      }
      const goneIds = new Set(toDelete.map(c => c.id));
      setClasses(prev => prev.filter(c => !goneIds.has(c.id)));
      setClassDialog(false); setSelectedClass(null); setClassForm(emptyClassForm);
      setSuccess(`Deleted ${toDelete.length} class${toDelete.length === 1 ? '' : 'es'}.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      logError('Schedule', err.message, err.stack);
      setError('Failed to delete: ' + err.message);
    }
  };

  // Single-class deletes go straight through; anything that removes a range
  // asks first, naming the count and the date span. Past classes holding
  // check-ins are dropped from the set either way and never deleted.
  const requestDeleteClass = async (scope) => {
    const list = classesForScope(scope);
    if (!list.length) return;
    let blocked;
    try {
      blocked = await protectedFromDelete(list);
    } catch (err) {
      logError('Schedule', err.message, err.stack);
      setError('Could not check attendance before deleting. Nothing was deleted.');
      setDeleteScopeDialog(false);
      return;
    }
    const allowed = list.filter(c => !blocked.has(c.id));

    if (!allowed.length) {
      setDeleteScopeDialog(false);
      setError(list.length === 1
        ? 'This class already happened and has check-ins, so it can\'t be deleted. Use Cancel Class instead — that keeps the class and its attendance.'
        : `All ${list.length} classes already happened and have check-ins, so none can be deleted. Use Cancel Class instead.`);
      setTimeout(() => setError(''), 8000);
      return;
    }
    if (allowed.length === 1 && !blocked.size) { handleDeleteClass(scope, allowed); return; }

    const dates = allowed.map(c => c.date).filter(Boolean).sort();
    setDeleteScopeDialog(false);
    setDeleteConfirm({
      scope, list: allowed, count: allowed.length,
      first: dates[0], last: dates[dates.length - 1], kept: blocked.size,
      courts: [...new Set(allowed.map(c => c.courtNumber).filter(Boolean))]
        .sort((a, b) => (parseInt(courtNum(a), 10) || 99) - (parseInt(courtNum(b), 10) || 99)),
    });
  };

  // ── Delete reservation ──────────────────────────────────────────────────────
  const handleDeleteRes = async () => {
    try {
      await deleteDoc(doc(db, 'reservations', selectedRes.id));
      setReservations(prev => prev.filter(r => r.id !== selectedRes.id));
      setResDialog(false); setSelectedRes(null);
      setSuccess('Reservation deleted.'); setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('Schedule', err.message, err.stack);
      setError('Failed to delete: ' + err.message);
    }
  };

  // ── Update series ───────────────────────────────────────────────────────────
  const updateSeries = async (scope) => {
    setUpdateScopeDialog(false);
    // Data for this specific class (includes courtNumber)
    const classData = {
      eventName: classForm.eventName || '', eventColor: classForm.eventColor || '',
      customerId: classForm.customerId || '',
      customerName: classForm.customerId ? customerDisplayName(customers.find(c => c.id === classForm.customerId)) : '',
      description: classForm.description || '',
      scoreboardUseDescription: classForm.scoreboardUseDescription === true,
      notes: classForm.notes || '',
      qrPlanId: classForm.qrPlanId || '',
      sessionType: classForm.sessionType, skillGroup: classForm.skillGroup,
      startTime: classForm.startTime, endTime: classForm.endTime,
      headCoachId: classForm.headCoachId, assistantCoachIds: classForm.assistantCoachIds,
      courtNumber: classForm.courtNumber, status: classForm.status,
      cancelReason: classForm.cancelReason,
      maxAthletes: String(classForm.maxAthletes ?? '').trim() === '' ? null : parseInt(classForm.maxAthletes, 10),
      durationMinutes: calcDuration(classForm.startTime, classForm.endTime),
    };
    // Data for sibling classes — excludes courtNumber so each keeps its own court
    const { courtNumber, ...classDataNoCourt } = classData;
    // The other courts THIS occurrence runs on. "This class only" means this
    // DATE, not this document: a series holds one doc per court per date, so
    // updating the anchor alone leaves its sibling courts at the old time,
    // stranded apart from the class they belong to.
    const sameDaySiblings = classes.filter(c => c.id !== selectedClass.id
      && c.repeatGroupId && c.repeatGroupId === selectedClass.repeatGroupId
      && c.date === selectedClass.date);
    // Ids this scope touches — needed after the branches for the cancel refund,
    // so it has to cover the sibling courts a cancel also marks canceled.
    const scopeIds = scope === 'this'
      ? [selectedClass.id, ...sameDaySiblings.map(c => c.id)]
      : classes.filter(c => c.repeatGroupId === selectedClass.repeatGroupId
          && (scope !== 'future' || c.date >= selectedClass.date)).map(c => c.id);
    try {
      if (scope === 'this') {
        const batch = writeBatch(db);
        batch.update(doc(db, 'classes', selectedClass.id), classData);
        sameDaySiblings.forEach(c => batch.update(doc(db, 'classes', c.id), classDataNoCourt));
        await batch.commit();
        const sibIds = new Set(sameDaySiblings.map(c => c.id));
        setClasses(prev => prev.map(c =>
          c.id === selectedClass.id ? { ...c, ...classData }
            : sibIds.has(c.id) ? { ...c, ...classDataNoCourt } : c));
      } else if (scope === 'future') {
        const toUpdate = classes.filter(c =>
          c.repeatGroupId === selectedClass.repeatGroupId && c.date >= selectedClass.date
        );
        const batch = writeBatch(db);
        toUpdate.forEach(c => batch.update(doc(db, 'classes', c.id),
          c.id === selectedClass.id ? classData : classDataNoCourt
        ));
        await batch.commit();
        setClasses(prev => prev.map(c =>
          toUpdate.some(u => u.id === c.id)
            ? { ...c, ...(c.id === selectedClass.id ? classData : classDataNoCourt) }
            : c
        ));
      } else {
        const toUpdate = classes.filter(c => c.repeatGroupId === selectedClass.repeatGroupId);
        const batch = writeBatch(db);
        toUpdate.forEach(c => batch.update(doc(db, 'classes', c.id),
          c.id === selectedClass.id ? classData : classDataNoCourt
        ));
        await batch.commit();
        setClasses(prev => prev.map(c =>
          c.repeatGroupId === selectedClass.repeatGroupId
            ? { ...c, ...(c.id === selectedClass.id ? classData : classDataNoCourt) }
            : c
        ));
      }
      // Extra courts. A recurring class saves through this function, not the
      // single-class path, so adding a court here needs its own handling —
      // without it the picker accepted a second court and nothing was written.
      // Scope is honoured: 'this' adds a court to one date, the others add it
      // to every date in scope, matching what the chosen option promises.
      const selectedCourts = classForm.courts?.length > 0 ? classForm.courts : [classForm.courtNumber];
      const extraCourts = selectedCourts.filter(c => c !== classData.courtNumber);
      if (extraCourts.length) {
        // One target per DATE. A multi-court series holds one doc per court per
        // date, so iterating every doc in scope tried to create the new court
        // once for each court already there — and the duplicate test reads
        // pre-write state, so it did not catch the repeats within one save.
        const targets = [...new Map(
          classes.filter(c => scopeIds.includes(c.id)).map(c => [c.date, c])
        ).values()];
        const usedSlugs = new Set(classes.filter(c => c.slug).map(c => c.slug));
        const created = []; let skipped = 0;
        for (const t of targets) {
          for (const court of extraCourts) {
            const exists = classes.some(c => c.date === t.date && c.startTime === classData.startTime
              && c.courtNumber === court && c.status !== 'canceled');
            if (exists || hasOverlap(t.date, classData.startTime, classData.endTime, court)) { skipped++; continue; }
            const base = defaultClassSlug({
              eventName: classData.eventName, sessionType: classData.sessionType,
              skillGroup: classData.skillGroup, date: t.date, courtNumber: court,
            });
            let finalSlug = base; let i = 2;
            while (usedSlugs.has(finalSlug)) { finalSlug = `${base}-${i++}`; }
            usedSlugs.add(finalSlug);
            // Same repeatGroupId, so the new court joins the series rather than
            // becoming a set of orphans that later series edits would miss.
            const data = { ...classData, date: t.date, courtNumber: court,
              repeatGroupId: selectedClass.repeatGroupId, slug: finalSlug };
            const ref = await addDoc(collection(db, 'classes'), data);
            created.push({ id: ref.id, ...data });
          }
        }
        if (created.length) setClasses(prev => [...prev, ...created]);
        setSuccess(`Series updated.${created.length ? ` Added ${created.length} class${created.length === 1 ? '' : 'es'} on ${extraCourts.map(c => c.replace('court-', 'Court ')).join(', ')}.` : ''}`
          + `${skipped ? ` Skipped ${skipped} — already booked at that time.` : ''}`);
        setTimeout(() => setSuccess(''), 6000);
      }
      if (selectedClass.status !== 'canceled' && classData.status === 'canceled') {
        logEvent('class.canceled', {
          target: { id: selectedClass.id, name: classData.eventName || classData.sessionType, date: selectedClass.date },
          description: `Class series update set status canceled (scope: ${scope}): ${classData.cancelReason || 'no reason given'}`,
          metadata: { cancelReason: classData.cancelReason || '', via: 'series-update', scope },
        });
        await refundCheckInsForClasses(scopeIds);
      }
      if (selectedClass.status === 'canceled' && classData.status !== 'canceled') {
        logEvent('class.reactivated', {
          target: { id: selectedClass.id, name: classData.eventName || classData.sessionType, date: selectedClass.date },
          description: `Class series update reactivated class (scope: ${scope}, status: ${classData.status})`,
          metadata: { newStatus: classData.status, via: 'series-update', scope },
        });
      }
      // ── Extend / shorten series when repeatUntil changed ──────────────────
      const groupAll = classes.filter(c => c.repeatGroupId === selectedClass.repeatGroupId);
      const curMaxDate = groupAll.reduce((max, c) => c.date > max ? c.date : max, '');
      const newUntil = classForm.repeatUntil || '';
      let extendedCount = 0, removedCount = 0;
      if (newUntil && newUntil !== curMaxDate) {
        if (newUntil > curMaxDate) {
          // EXTEND: generate docs for every repeatDay x court in the group
          const groupCourts = [...new Set(groupAll.map(c => c.courtNumber))];
          const repeatDays = (classForm.repeatDays || []).length
            ? classForm.repeatDays
            : [...new Set(groupAll.map(c => DAY_NAMES[new Date(c.date + 'T12:00:00').getDay()]))];
          const usedSlugs = new Set(classes.filter(c => c.slug).map(c => c.slug));
          const cursor = new Date(curMaxDate + 'T12:00:00');
          cursor.setDate(cursor.getDate() + 1);
          const untilD = new Date(newUntil + 'T12:00:00');
          const { courtNumber: _cn, ...templateData } = classData;
          while (cursor <= untilD) {
            const dayName = DAY_NAMES[cursor.getDay()];
            if (repeatDays.includes(dayName)) {
              const dStr = dateStr(cursor);
              for (const court of groupCourts) {
                if (hasOverlap(dStr, classForm.startTime, classForm.endTime, court)) continue;
                const baseSlug = defaultClassSlug({ eventName: classData.eventName, sessionType: classData.sessionType, skillGroup: classData.skillGroup, date: dStr, courtNumber: court });
                let finalSlug = baseSlug; let i = 2;
                while (usedSlugs.has(finalSlug)) { finalSlug = `${baseSlug}-${i++}`; }
                usedSlugs.add(finalSlug);
                const data = { ...templateData, date: dStr, courtNumber: court, repeatGroupId: selectedClass.repeatGroupId, slug: finalSlug };
                const ref = await addDoc(collection(db, 'classes'), data);
                setClasses(prev => [...prev, { id: ref.id, ...data }]);
                extendedCount++;
              }
            }
            cursor.setDate(cursor.getDate() + 1);
          }
        } else {
          // SHORTEN: delete group docs after the new until date (confirm first).
          // Pulling the end date into the past would otherwise delete classes
          // that already happened, orphaning their check-ins — those are kept.
          const toRemoveRaw = groupAll.filter(c => c.date > newUntil);
          const blockedShorten = await protectedFromDelete(toRemoveRaw);
          const toRemove = toRemoveRaw.filter(c => !blockedShorten.has(c.id));
          if (!toRemove.length && blockedShorten.size) {
            setError(`Kept ${blockedShorten.size} past class(es) with check-ins — nothing was deleted.`);
            setTimeout(() => setError(''), 8000);
          }
          if (toRemove.length && window.confirm(
            `Shortening this series will delete ${toRemove.length} class(es) after ${newUntil}.`
            + (blockedShorten.size ? ` ${blockedShorten.size} past class(es) with check-ins will be kept.` : '')
            + ` Continue?`
          )) {
            const delBatch = writeBatch(db);
            toRemove.forEach(c => delBatch.delete(doc(db, 'classes', c.id)));
            await delBatch.commit();
            const removedIds = new Set(toRemove.map(c => c.id));
            setClasses(prev => prev.filter(c => !removedIds.has(c.id)));
            removedCount = toRemove.length;
          }
        }
      }
      // ── Add / remove weekdays when repeatDays changed ─────────────────────
      const existingDays = [...new Set(groupAll.map(c => DAY_NAMES[new Date(c.date + 'T12:00:00').getDay()]))];
      const formDays = classForm.repeatDays || [];
      if (formDays.length) {
        const addedDays = formDays.filter(d => !existingDays.includes(d));
        const removedDays = existingDays.filter(d => !formDays.includes(d));
        const seriesEnd = newUntil && newUntil > curMaxDate ? newUntil : curMaxDate;
        const todayStr = dateStr(new Date());
        if (addedDays.length) {
          const groupCourts2 = [...new Set(groupAll.map(c => c.courtNumber))];
          const usedSlugs2 = new Set(classes.filter(c => c.slug).map(c => c.slug));
          const { courtNumber: _cn2, ...templateData2 } = classData;
          const cur = new Date(todayStr + 'T12:00:00');
          cur.setDate(cur.getDate() + 1);
          const endD = new Date(seriesEnd + 'T12:00:00');
          while (cur <= endD) {
            const dn = DAY_NAMES[cur.getDay()];
            if (addedDays.includes(dn)) {
              const dStr2 = dateStr(cur);
              for (const court of groupCourts2) {
                if (hasOverlap(dStr2, classForm.startTime, classForm.endTime, court)) continue;
                const baseSlug2 = defaultClassSlug({ eventName: classData.eventName, sessionType: classData.sessionType, skillGroup: classData.skillGroup, date: dStr2, courtNumber: court });
                let finalSlug2 = baseSlug2; let j = 2;
                while (usedSlugs2.has(finalSlug2)) { finalSlug2 = `${baseSlug2}-${j++}`; }
                usedSlugs2.add(finalSlug2);
                const data2 = { ...templateData2, date: dStr2, courtNumber: court, repeatGroupId: selectedClass.repeatGroupId, slug: finalSlug2 };
                const ref2 = await addDoc(collection(db, 'classes'), data2);
                setClasses(prev => [...prev, { id: ref2.id, ...data2 }]);
                extendedCount++;
              }
            }
            cur.setDate(cur.getDate() + 1);
          }
        }
        if (removedDays.length) {
          const toDrop = groupAll.filter(c =>
            removedDays.includes(DAY_NAMES[new Date(c.date + 'T12:00:00').getDay()]) && c.date > todayStr
          );
          if (toDrop.length && window.confirm(
            `Removing ${removedDays.join('/')} will delete ${toDrop.length} future class(es) on those days. Past classes are kept. Continue?`
          )) {
            const dropBatch = writeBatch(db);
            toDrop.forEach(c => dropBatch.delete(doc(db, 'classes', c.id)));
            await dropBatch.commit();
            const dropIds = new Set(toDrop.map(c => c.id));
            setClasses(prev => prev.filter(c => !dropIds.has(c.id)));
            removedCount += toDrop.length;
          }
        }
      }
      setClassDialog(false); setSelectedClass(null); setClassForm(emptyClassForm);
      const extra = extendedCount ? ` ${extendedCount} added.` : removedCount ? ` ${removedCount} removed.` : '';
      setSuccess('Classes updated.' + extra); setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      logError('Schedule', err.message, err.stack);
      setError('Failed to update series: ' + err.message);
    }
  };

  // ── Undo a drag-move ────────────────────────────────────────────────────────
  // A drag writes immediately, so this puts the class back exactly where it was.
  const runUndoMove = async () => {
    if (!undoMove) return;
    const { id, before } = undoMove;
    setUndoMove(null);
    try {
      await updateDoc(doc(db, 'classes', id), before);
      setClasses(prev => prev.map(c => c.id === id ? { ...c, ...before } : c));
      setSuccess('Move undone.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('Schedule', err.message, err.stack);
      setError('Could not undo the move: ' + err.message);
    }
  };

  // ── Navigation ──────────────────────────────────────────────────────────────
  const nav = (dir) => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    dir === 'prev' ? api.prev() : dir === 'next' ? api.next() : api.today();
    setCalendarTitle(api.view.title);
  };

  const switchView = (newView) => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.changeView(newView);
    setView(newView);
    setCalendarTitle(api.view.title);
  };

  const toggleCourt = (courtId) => {
    setResForm(f => ({
      ...f,
      courts: f.courts.includes(courtId)
        ? f.courts.filter(c => c !== courtId)
        : [...f.courts, courtId],
    }));
  };

  if (loading) return (
    <Layout>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    </Layout>
  );

  // Local YYYY-MM-DD for class-day comparisons (class dates are stored as strings).
  const schedToday = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
  };

  // Spot-holders = reserved + checked in. Waitlisted athletes don't hold a spot.
  const sortByWaitTime = (a, b) => {
    const t = r => r.waitlistedAt?.seconds ?? (r.waitlistedAt ? new Date(r.waitlistedAt).getTime() / 1000 : 0);
    return t(a) - t(b);
  };

  // When a spot frees up (cancel / undo), promote the earliest-waitlisted
  // athlete to reserved and email them. Fail-soft on the email — the
  // promotion stands even if sending fails.
  const promoteFromWaitlist = async () => {
    try {
      if (!viewingClass || !viewingClass.maxAthletes) return;
      const snap = await getDocs(query(collection(db, 'attendance'), where('classId', '==', viewingClass.id)));
      const recs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const active = recs.filter(r => r.type !== 'coach' && r.status !== 'waitlisted').length;
      if (active >= viewingClass.maxAthletes) return; // no free spot
      const waiting = recs.filter(r => r.status === 'waitlisted').sort(sortByWaitTime);
      if (waiting.length === 0) return;
      const next = waiting[0];
      await updateDoc(doc(db, 'attendance', next.id), {
        status: 'reserved',
        reservedAt: new Date(),
        promotedFromWaitlist: true,
      });
      setViewingAttendance(prev => prev.map(x => x.id === next.id ? { ...x, status: 'reserved', promotedFromWaitlist: true } : x));
      logEvent('waitlist.promoted', {
        target: { id: next.athleteId || '', email: next.athleteEmail || '', name: next.athleteName || '' },
        description: `Promoted from wait-list to reserved in ${viewingClass?.eventName || viewingClass?.sessionType || 'class'} (${viewingClass?.date || ''})`,
        metadata: { classId: viewingClass?.id || '' },
      });
      try {
        const functions = getFunctions();
        const sendPromo = httpsCallable(functions, 'sendWaitlistPromotion');
        await sendPromo({
          athleteName: next.athleteName || '',
          athleteEmail: next.athleteEmail || '',
          eventName: viewingClass.eventName || viewingClass.sessionType || 'class',
          date: viewingClass.date || '',
          startTime: viewingClass.startTime || '',
        });
      } catch (e) { console.warn('Waitlist promotion email failed:', e); }
    } catch (e) { console.error('promoteFromWaitlist failed:', e); }
  };

  // Reserve a spot in a future class — or join the wait-list if it's full.
  // No session is deducted — that happens at check-in on class day.
  const handleViewReserve = async (athlete) => {
    if (!viewingClass) return;
    if (checkingInAthlete) return;
    const existing = viewingAttendance.some(
      att => (att.athleteId === athlete.id || att.athleteId === athlete.athleteId) && att.type !== 'coach'
    );
    if (existing) return;
    const classFullNow = !!(viewingClass.maxAthletes &&
      viewingAttendance.filter(a => a.type !== 'coach' && a.status !== 'waitlisted').length >= viewingClass.maxAthletes);
    setCheckingInAthlete(athlete.id);
    try {
      const dupSnap = await getDocs(query(
        collection(db, 'attendance'),
        where('classId', '==', viewingClass.id),
        where('athleteId', '==', athlete.id)
      ));
      if (!dupSnap.empty) { setCheckingInAthlete(null); return; }
      const newStatus = classFullNow ? 'waitlisted' : 'reserved';
      const ref = await addDoc(collection(db, 'attendance'), {
        classStartsAt: classStartsAt(viewingClass),
        classId: viewingClass.id,
        athleteId: athlete.id,
        athleteName: `${athlete.firstName} ${athlete.lastName}`,
        athleteEmail: athlete.email || '',
        date: viewingClass.date || '',
        sessionType: viewingClass.sessionType || '',
        status: newStatus,
        ...(classFullNow ? { waitlistedAt: new Date() } : { reservedAt: new Date() }),
      });
      setViewingAttendance(prev => [...prev, {
        id: ref.id, classId: viewingClass.id,
        athleteId: athlete.id,
        athleteEmail: athlete.email || '',
        athleteName: `${athlete.firstName} ${athlete.lastName}`,
        status: newStatus,
        ...(classFullNow ? { waitlistedAt: { seconds: Date.now() / 1000 } } : {}),
      }]);
    } catch (err) {
      console.error('Reserve failed:', err);
      setError('Failed to reserve. Please try again.');
    }
    setCheckingInAthlete(null);
  };

  // Cancel a reservation (any time, no cutoff). Nothing to refund — no session
  // was deducted at reserve time.
  const handleCancelReservation = async (attRec) => {
    if (!attRec || !attRec.id) return;
    try {
      await deleteDoc(doc(db, 'attendance', attRec.id));
      setViewingAttendance(prev => prev.filter(a => a.id !== attRec.id));
      setReserveChoice(null);
      logEvent(attRec.status === 'waitlisted' ? 'waitlist.left' : 'reservation.canceled', {
        target: { id: attRec.athleteId || '', email: attRec.athleteEmail || '', name: attRec.athleteName || '' },
        description: `${attRec.status === 'waitlisted' ? 'Left the wait-list' : 'Canceled a reservation'} for ${viewingClass?.eventName || viewingClass?.sessionType || 'class'} (${attRec.date || viewingClass?.date || ''})`,
        metadata: { classId: attRec.classId || viewingClass?.id || '' },
      });
      if (attRec.status === 'reserved') await promoteFromWaitlist();
    } catch (err) {
      console.error('Cancel reservation failed:', err);
      setError('Failed to cancel reservation.');
    }
  };

  const handleViewCheckIn = async (athlete) => {
    if (!viewingClass || checkingInAthlete) return;
    // Future classes can't be checked into — they get reserved instead.
    if (viewingClass.date > schedToday()) {
      setError('This class is in the future — reserve a spot instead, then check in on class day.');
      return;
    }
    // A canceled class didn't happen. Checking in would burn a session that no
    // cancel-time refund will ever catch, since the cancel already ran.
    if ((viewingClass.status || '').toLowerCase() === 'canceled') {
      setError('This class is canceled — check-in would charge a session for a class that did not happen.');
      return;
    }
    // Capacity: members can't walk into a full class (their reserved spot, if
    // any, already counts). Admins and coaches can override.
    if (!(isAdmin || isCoach) && viewingClass.maxAthletes) {
      const holders = viewingAttendance.filter(a => a.type !== 'coach' && a.status !== 'waitlisted');
      const mine = holders.some(att => att.athleteId === athlete.id || att.athleteId === athlete.athleteId);
      if (!mine && holders.length >= viewingClass.maxAthletes) {
        setError('This class is full.');
        return;
      }
    }
    // League nights are paid for by the league, not from session balances, so
    // a check-in there records attendance and spends nothing. Recorded as
    // balanceAfter = balanceBefore, which also makes an undo refund nothing.
    const leagueNight = viewingClass?.sessionType === 'league';
    const cost = leagueNight ? 0 : sessionCost(athlete);
    // Players can't self-check-in with zero/negative balance — coach or admin must do it
    const isPlayer = !isAdmin && !isCoach;
    if (isPlayer && !leagueNight && blockedByBalance(athlete)) {
      setError('No sessions remaining. Please ask a coach or admin to check you in.');
      return;
    }
    // Prevent duplicate check-ins — but let reserved records through so the
    // Firestore check below converts the held spot into a check-in.
    const existingNonCoach = viewingAttendance.find(
      att => (att.athleteId === athlete.id || att.athleteId === athlete.athleteId) && att.type !== 'coach'
    );
    if (existingNonCoach && existingNonCoach.status !== 'reserved' && existingNonCoach.status !== 'waitlisted') return;
    setCheckingInAthlete(athlete.id);
    try {
      // Double-check Firestore to prevent race conditions
      const existingSnap = await getDocs(query(
        collection(db, 'attendance'),
        where('classId', '==', viewingClass.id),
        where('athleteId', '==', athlete.id)
      ));
      if (!existingSnap.empty) {
        const rec = { id: existingSnap.docs[0].id, ...existingSnap.docs[0].data() };
        if (rec.status === 'reserved' || rec.status === 'waitlisted') {
          // Held/waitlisted spot — convert the same doc into a check-in (deduct now).
          const newBalance = (athlete.sessionBalance || 0) - cost;
          await updateDoc(doc(db, 'attendance', rec.id), {
            status: 'checked_in',
            checkedInAt: new Date(),
            balanceBefore: athlete.sessionBalance || 0,
            balanceAfter: newBalance,
          });
          await updateDoc(doc(db, 'athletes', athlete.id), {
            sessionBalance: increment(-cost),
            lastCheckIn: serverTimestamp(),
          });
          setViewingAthletes(prev => prev.map(x => x.id === athlete.id ? { ...x, sessionBalance: newBalance } : x));
          setViewingAttendance(prev => {
            const others = prev.filter(x => x.id !== rec.id);
            return [...others, { ...rec, status: 'checked_in', checkedInAt: new Date(), balanceBefore: athlete.sessionBalance || 0, balanceAfter: newBalance }];
          });
          setReserveChoice(null);
          setCheckingInAthlete(null);
          return;
        }
        // Already checked in — refresh local attendance state to sync
        setViewingAttendance(prev => {
          const exists = prev.some(a => a.athleteId === athlete.id);
          if (exists) return prev;
          return [...prev, rec];
        });
        setCheckingInAthlete(null);
        return;
      }
      const newBalance = (athlete.sessionBalance || 0) - cost;
      // Capture the real Firestore doc reference so the optimistic local
      // state below can record the actual ID. Previously the local state
      // pushed a fake `Date.now()` id, which meant the very next undo would
      // try to delete a doc that doesn't exist → permission denied because
      // Firestore can't evaluate rules without resource.data.
      const newAttRef = await addDoc(collection(db, 'attendance'), {
        classStartsAt: classStartsAt(viewingClass),
        classId: viewingClass.id,
        athleteId: athlete.id,
        athleteName: `${athlete.firstName} ${athlete.lastName}`,
        // Store the email so Firestore rules can match
        //   request.auth.token.email == resource.data.athleteEmail
        // when the athlete (or their parent) tries to delete this record.
        // Without it, the rule clause has nothing to compare against and
        // deletes from non-staff users get rejected.
        athleteEmail: athlete.email || '',
        date: viewingClass.date || '',
        sessionType: viewingClass.sessionType || '',
        status: 'checked_in',
        checkedInAt: new Date(),
        balanceBefore: athlete.sessionBalance || 0,
        balanceAfter: newBalance,
      });
      await updateDoc(doc(db, 'athletes', athlete.id), {
        sessionBalance: increment(-cost),
        lastCheckIn: serverTimestamp(),
      });
      // Update local state
      setViewingAthletes(prev => prev.map(a => a.id === athlete.id ? { ...a, sessionBalance: newBalance } : a));
      setViewingAttendance(prev => [...prev, {
        id: newAttRef.id,
        athleteId: athlete.id,
        athleteEmail: athlete.email || '',
        athleteName: `${athlete.firstName} ${athlete.lastName}`,
        balanceBefore: athlete.sessionBalance || 0,
        checkedInAt: new Date(),
      }]);
    } catch (err) {
      console.error('Check-in failed:', err);
    }
    setCheckingInAthlete(null);
  };

  // Text the tapping admin a summary of who is checked in. The server builds
  // the message from Firestore, so it reports what is saved, not this screen.
  const handleSendAttendance = async () => {
    if (!viewingClass || attendanceSending) return;
    setAttendanceSending(true);
    setAttendanceNotice(null);
    try {
      const { data } = await httpsCallable(getFunctions(), 'sendAttendanceSummary')({ classId: viewingClass.id });
      setAttendanceNotice({
        severity: 'success',
        text: `Attendance texted to ${(data.to || []).join(', ')}: ${data.coaches} coach${data.coaches === 1 ? '' : 'es'}, ${data.players} player${data.players === 1 ? '' : 's'}.`,
      });
      setTimeout(() => setAttendanceNotice(n => (n && n.severity === 'success' ? null : n)), 5000);
    } catch (err) {
      console.error('sendAttendanceSummary failed:', err);
      setAttendanceNotice({ severity: 'error', text: err.message || 'Could not send the attendance text.' });
    } finally {
      setAttendanceSending(false);
    }
  };

  // Toggle a coach's attendance record. Assigning a coach already creates one
  // (see handleSwapCoach); this is how a row in the roster is undone, or a
  // coach marked attending without changing who holds the role.
  const handleCoachCheckIn = async (coach, role = 'head') => {
    if (!viewingClass || !coach) return;
    // Check if already checked in
    const existing = viewingAttendance.find(a => a.type === 'coach' && a.coachId === coach.id);
    if (existing) {
      // Undo: delete the record
      try {
        await deleteDoc(doc(db, 'attendance', existing.id));
        setViewingAttendance(prev => prev.filter(a => a.id !== existing.id));
      } catch (err) {
        console.error('Coach check-in undo failed:', err);
      }
      return;
    }
    try {
      const ref = await addDoc(collection(db, 'attendance'), {
        classStartsAt: classStartsAt(viewingClass),
        classId: viewingClass.id,
        type: 'coach',
        coachId: coach.id,
        coachRole: role, // 'head' or 'assistant'
        coachName: `${coach.firstName || ''} ${coach.lastName || ''}`.trim() || coach.email || 'Coach',
        date: viewingClass.date || '',
        sessionType: viewingClass.sessionType || '',
        checkedInAt: new Date(),
      });
      setViewingAttendance(prev => [...prev, {
        id: ref.id,
        type: 'coach',
        coachId: coach.id,
        coachRole: role,
        coachName: `${coach.firstName || ''} ${coach.lastName || ''}`.trim() || coach.email || 'Coach',
        checkedInAt: new Date(),
      }]);
    } catch (err) {
      console.error('Coach check-in failed:', err);
    }
  };

  // Swap which coach is assigned to a role on the currently-viewed class, and
  // mark them attending. Used by the head/asst coach dropdowns in the class
  // detail dialog. Steps:
  //   1) If a different coach previously held this role, drop their record —
  //      the record says this coach is on the class, so someone swapped out is
  //      no longer attending it.
  //   2) Update the class doc's headCoachId / assistantCoachIds accordingly.
  //   3) Add a fresh record for the new coach.
  // If newCoachId is empty, the role is cleared and the previous record dropped.
  //
  // ONE STEP ON PURPOSE: the admin assigns a coach at the point they know that
  // coach is taking the class, often hours ahead, and that single action is the
  // confirmation. checkedInAt is therefore when attendance was CONFIRMED, not
  // when the coach arrived — which is why the roster shows "attending" and no
  // time. Reporting it as a check-in time read as an arrival and was hours out.
  const handleSwapCoach = async (role, newCoachId) => {
    if (!viewingClass) return;
    const newCoach = newCoachId ? coaches.find(c => c.id === newCoachId) : null;
    try {
      const oldCoachId = role === 'head'
        ? (viewingClass.headCoachId || '')
        : ((viewingClass.assistantCoachIds || [])[0] || '');
      // 1) Drop the outgoing coach's record.
      if (oldCoachId && oldCoachId !== newCoachId) {
        const oldRec = viewingAttendance.find(a => a.type === 'coach' && a.coachId === oldCoachId);
        if (oldRec) {
          await deleteDoc(doc(db, 'attendance', oldRec.id));
          setViewingAttendance(prev => prev.filter(a => a.id !== oldRec.id));
        }
      }
      // 2) Update the class doc.
      const patch = role === 'head'
        ? { headCoachId: newCoachId || '' }
        : { assistantCoachIds: newCoachId ? [newCoachId] : [] };
      await updateDoc(doc(db, 'classes', viewingClass.id), patch);
      setViewingClass(prev => prev ? { ...prev, ...patch } : prev);
      // Mirror into local classes state so the calendar reflects the change.
      setClasses(prev => prev.map(c => c.id === viewingClass.id ? { ...c, ...patch } : c));
      // 3) Mark the incoming coach attending, unless they already are.
      if (newCoach) {
        const already = viewingAttendance.find(a => a.type === 'coach' && a.coachId === newCoach.id);
        if (!already) {
          const rec = {
            classId: viewingClass.id,
            type: 'coach',
            coachId: newCoach.id,
            coachRole: role,
            coachName: `${newCoach.firstName || ''} ${newCoach.lastName || ''}`.trim() || newCoach.email || 'Coach',
            date: viewingClass.date || '',
            sessionType: viewingClass.sessionType || '',
            checkedInAt: new Date(),
            classStartsAt: classStartsAt(viewingClass),
          };
          const ref = await addDoc(collection(db, 'attendance'), rec);
          setViewingAttendance(prev => [...prev, { id: ref.id, ...rec }]);
        }
      }
    } catch (err) {
      console.error('Coach swap failed:', err);
      setError('Failed to swap coach. Please try again.');
    }
  };

  // Undo athlete check-in (admins and coaches only)
  // Undo athlete check-in. Permitted for:
  //   - Admins / coaches: undo anyone (used during day-of cleanup)
  //   - Adult athlete: undo their own check-in (auth email matches athlete email)
  //   - Parent: undo a linked junior's check-in (parentUserId matches uid)
  // Refunds the session balance using balanceBefore if recorded, else +1.
  // Skips the previous window.confirm — the row's Undo button on tap is
  // explicit enough and the action is reversible (just tap Check In again).
  const handleUndoCheckIn = async (attendanceRec) => {
    if (!attendanceRec || !attendanceRec.id) return;
    // Permission check: staff OR ownership over the athlete record.
    if (!(isAdmin || isCoach)) {
      const ath = viewingAthletes.find(a => a.id === attendanceRec.athleteId);
      const ownsRecord = !!ath && (
        (ath.email && currentUserEmail && ath.email.toLowerCase() === currentUserEmail) ||
        (ath.parentUserId && ath.parentUserId === currentUserUid)
      );
      if (!ownsRecord) return;
      // Owning the record is not enough once the session has started — from
      // then on it is a record of who was on the court, and only staff may
      // change it. Guarded here as well as in the button's enabled state,
      // since the handler is reachable from more than one place.
      const started = !!(viewingClass?.date && viewingClass?.startTime)
        && new Date() >= new Date(`${viewingClass.date}T${viewingClass.startTime}`);
      if (started) return;
    }
    try {
      // Restore balance: prefer balanceBefore, else current+1
      const athleteId = attendanceRec.athleteId;
      if (athleteId) {
        try {
          const athDoc = await getDoc(doc(db, 'athletes', athleteId));
          if (athDoc.exists()) {
            const current = athDoc.data().sessionBalance || 0;
            // The difference across the check-in is what it actually cost —
            // one session, or none if the athlete is comped. Restoring to
            // balanceBefore also refunded anything spent since.
            const refund = (attendanceRec.balanceBefore !== undefined
              && attendanceRec.balanceAfter !== undefined)
              ? attendanceRec.balanceBefore - attendanceRec.balanceAfter
              : 1;                       // rows predating balanceAfter
            const restored = current + refund;   // on-screen only
            if (refund !== 0) {
              await updateDoc(doc(db, 'athletes', athleteId), { sessionBalance: increment(refund) });
            }
            setViewingAthletes(prev => prev.map(a => a.id === athleteId ? { ...a, sessionBalance: restored } : a));
          }
        } catch (e) {
          console.error('Balance restore failed:', e);
        }
      }
      await deleteDoc(doc(db, 'attendance', attendanceRec.id));
      setViewingAttendance(prev => prev.filter(a => a.id !== attendanceRec.id));
      logEvent('checkin.undone', {
        target: { id: attendanceRec.athleteId || '', email: attendanceRec.athleteEmail || '', name: attendanceRec.athleteName || '' },
        description: `Check-in undone for ${viewingClass?.eventName || viewingClass?.sessionType || 'class'} (${attendanceRec.date || viewingClass?.date || ''})`,
        metadata: { classId: attendanceRec.classId || viewingClass?.id || '' },
      });
      await promoteFromWaitlist();
    } catch (err) {
      console.error('Undo check-in failed:', err);
    }
  };

  // ── Class Log ────────────────────────────────────────────────────────────────
  const handleAddLog = async () => {
    if (!logInput.trim() || !viewingClass) return;
    setLogSaving(true);
    try {
      const entry = {
        author: currentUserName,
        message: logInput.trim(),
        timestamp: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, 'classes', viewingClass.id, 'logs'), entry);
      setClassLogs(prev => [...prev, { id: ref.id, ...entry, timestamp: { toDate: () => new Date() } }]);
      setLogInput('');
    } catch (err) {
      logError('Schedule', err.message, err.stack);
    }
    setLogSaving(false);
  };

  // ── Bulk Cancel ─────────────────────────────────────────────────────────────
  const CANCEL_TYPE_TILES = [
    { value: 'junior',    label: 'Junior',     matches: t => t.startsWith('junior') && t !== 'junior-private' },
    { value: 'adult',     label: 'Adult',      matches: t => t.startsWith('adult') },
    { value: 'private',   label: 'Private',    matches: t => t === 'junior-private' },
    { value: 'block',     label: 'Block Time', matches: t => t === 'block-time' },
    { value: 'league',    label: 'League',     matches: t => t === 'league' },
    { value: 'all-today', label: 'All Today',  matches: t => true },
  ];

  const handleBulkCancel = async () => {
    if (cancelTypes.length === 0) { setError('Select at least one class type.'); return; }
    if (!cancelReason) { setError('Select a cancellation reason.'); return; }
    const calApi = calendarRef.current?.getApi();
    const currentDate = calApi ? calApi.getDate() : new Date();
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth()+1).padStart(2,'0')}-${String(currentDate.getDate()).padStart(2,'0')}`;
    const toCancel = classes.filter(c => {
      if (c.date !== dateStr) return false;
      if (c.status === 'canceled') return false;
      return CANCEL_TYPE_TILES.some(tile => cancelTypes.includes(tile.value) && tile.matches(c.sessionType));
    });
    if (toCancel.length === 0) { setError('No matching scheduled classes found for today.'); return; }
    setCancelSaving(true); setError('');
    try {
      const batch = writeBatch(db);
      toCancel.forEach(c => batch.update(doc(db, 'classes', c.id), { status: 'canceled', cancelReason }));
      await batch.commit();
      setClasses(prev => prev.map(c =>
        toCancel.some(tc => tc.id === c.id) ? { ...c, status: 'canceled', cancelReason } : c
      ));
      logEvent('class.canceled', {
        description: `Canceled ${toCancel.length} class${toCancel.length > 1 ? 'es' : ''} today — ${cancelReason}`,
        metadata: { count: toCancel.length, reason: cancelReason, classIds: toCancel.map(c => c.id) },
      });
      const { refunded } = await refundCheckInsForClasses(toCancel.map(c => c.id));
      setCancelDialog(false); setCancelTypes([]); setCancelReason('');
      setSuccess(`Canceled ${toCancel.length} class${toCancel.length > 1 ? 'es' : ''}.`
        + (refunded ? ` Refunded ${refunded} session${refunded === 1 ? '' : 's'}.` : ''));
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      logError('Schedule', err.message, err.stack);
      setError('Failed to cancel classes: ' + err.message);
    }
    setCancelSaving(false);
  };

  // Cancel just the class currently open in the detail dialog. Mirrors the
  // bulk cancel update shape (status + cancelReason) so downstream filters
  // (calendar background, "show cancellations" rows, etc.) treat it the same.
  const handleSingleCancel = async () => {
    if (!viewingClass) return;
    if (!singleCancelReason) { setError('Select a cancellation reason.'); return; }
    setSingleCancelSaving(true); setError('');
    try {
      await updateDoc(doc(db, 'classes', viewingClass.id), {
        status: 'canceled',
        cancelReason: singleCancelReason,
      });
      setClasses(prev => prev.map(c =>
        c.id === viewingClass.id ? { ...c, status: 'canceled', cancelReason: singleCancelReason } : c
      ));
      setViewingClass(prev => prev ? { ...prev, status: 'canceled', cancelReason: singleCancelReason } : prev);
      setSingleCancelDialog(false);
      logEvent('class.canceled', {
        description: `Canceled class ${viewingClass.eventName || viewingClass.sessionType || ''} (${viewingClass.date || ''}) — ${singleCancelReason}`,
        metadata: { classId: viewingClass.id, reason: singleCancelReason },
      });
      const { refunded } = await refundCheckInsForClasses([viewingClass.id], {
        onAthleteBalance: (id, bal) => setViewingAthletes(prev =>
          prev.map(a => a.id === id ? { ...a, sessionBalance: bal } : a)),
      });
      setSingleCancelReason('');
      setClassViewDialog(false);
      setSuccess('Class canceled.'
        + (refunded ? ` Refunded ${refunded} session${refunded === 1 ? '' : 's'}.` : ''));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('Schedule', err.message, err.stack);
      setError('Failed to cancel class: ' + err.message);
    }
    setSingleCancelSaving(false);
  };

  const handleMerge = async () => {
    if (mergeSelected.length < 2) { setError('Select at least 2 classes to merge.'); return; }
    if (mergeCourts.length === 0) { setError('Select at least one court.'); return; }
    setMergeSaving(true);
    try {
      // Get attendance records from all selected classes
      const allAttendance = [];
      for (const cls of mergeSelected) {
        const attSnap = await getDocs(query(collection(db, 'attendance'), where('classId', '==', cls.id)));
        attSnap.docs.forEach(d => allAttendance.push({ id: d.id, ...d.data() }));
      }
      // Auto-generate event name from selected classes
      const mergeEventName = 'Merge of ' + mergeSelected.map(c =>
        c.eventName || formatSessionLabel(c.sessionType)
      ).join(' & ');
      // Create one new class per court
      const newClasses = [];
      for (const court of mergeCourts) {
        const data = {
          sessionType: mergeSelected[0].sessionType,
          skillGroup: mergeSelected[0].skillGroup || '',
          date: mergeDate || mergeSelected[0].date,
          startTime: mergeStartTime || mergeSelected[0].startTime,
          endTime: mergeEndTime || mergeSelected[0].endTime,
          headCoachId: mergeCoach || mergeSelected[0].headCoachId || '',
          assistantCoachIds: [],
          courtNumber: court,
          status: 'scheduled',
          maxAthletes: null,
          eventName: mergeEventName,
          eventColor: mergeSelected[0].eventColor || '',
          mergedFrom: mergeSelected.map(c => c.id),
        };
        const ref = await addDoc(collection(db, 'classes'), data);
        newClasses.push({ id: ref.id, ...data });
        // Reassign attendance to new class
        for (const att of allAttendance) {
          const { id: _oldId, ...attData } = att;
          // The row moves to a new class, so it takes that class's start
          // time rather than carrying the old one across.
          await addDoc(collection(db, 'attendance'), {
            ...attData, classId: ref.id, classStartsAt: classStartsAt(data),
          });
        }
      }
      // A merge deletes the originals, so it needs the same audit trail as a
      // delete — otherwise the removals leave no record anywhere.
      logEvent('class.merged', {
        description: `Merged ${mergeSelected.length} classes into ${newClasses.length} `
          + `class${newClasses.length === 1 ? '' : 'es'}: ${mergeEventName || mergeSelected[0]?.sessionType || ''} `
          + `(${mergeDate || mergeSelected[0]?.date || ''})`,
        metadata: {
          sourceCount: mergeSelected.length,
          sourceClassIds: mergeSelected.map(c => c.id),
          sourceDates: [...new Set(mergeSelected.map(c => c.date).filter(Boolean))],
          newClassIds: newClasses.map(c => c.id),
          eventName: mergeEventName || '',
        },
      });
      // Delete original classes
      for (const cls of mergeSelected) {
        await deleteDoc(doc(db, 'classes', cls.id));
      }
      setClasses(prev => [
        ...prev.filter(c => !mergeSelected.find(s => s.id === c.id)),
        ...newClasses,
      ]);
      setMergeDialog(false);
      setMergeSelected([]);
      setSuccess(`Merged ${mergeSelected.length} classes into ${newClasses.length} class${newClasses.length > 1 ? 'es' : ''}.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      logError('Schedule', err.message, err.stack);
      setError('Merge failed: ' + err.message);
    }
    setMergeSaving(false);
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
      {/* Member booking dialog (create + view/cancel) */}
      {/* Offered when createMemberBooking refuses a renter for a missing
          waiver. Accepting writes the acceptance and retries the booking. */}
      <WaiverDialog
        open={waiverDialogOpen}
        doc={waiverDoc}
        onClose={() => { setWaiverDialogOpen(false); setMemberBookingSaving(false); }}
        onAccepted={handleAcceptWaiver}
      />

      <Dialog open={memberBookingDialog} onClose={() => { setMemberBookingDialog(false); setViewingMemberBooking(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1E3A5F' }}>
          {viewingMemberBooking ? 'Court Booking' : 'Book Court Time'}
        </DialogTitle>
        <DialogContent>
          {viewingMemberBooking ? (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" sx={{ mb: 0.5 }}><b>{viewingMemberBooking.date}</b> · {viewingMemberBooking.startTime}–{viewingMemberBooking.endTime}</Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                {(viewingMemberBooking.courts || []).join(', ')}{viewingMemberBooking.guestCount ? ' · 1 guest' : ''}
              </Typography>
              {viewingMemberBooking.streamEnabled && (
                <Typography variant="body2" sx={{ color: '#1E6B3C', fontWeight: 600, mb: 0.5 }}>
                  {viewingMemberBooking.streamVideoId
                    ? 'Streamed — recording saved'
                    : 'Court camera on for this reservation'}
                </Typography>
              )}
              {seesBookers && viewingMemberBooking.memberName && (
                <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>Member: {viewingMemberBooking.memberName}</Typography>
              )}
              {(viewingMemberBooking.overageAmount || 0) > 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>Overage on this booking: ${(viewingMemberBooking.overageAmount || 0).toFixed(2)}</Alert>
              )}
              {seesBookers && (viewingMemberBooking.courts || []).length === 1 && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
                  <TextField label="Move to court" select size="small" fullWidth
                    value={moveToCourt}
                    onChange={e => setMoveToCourt(e.target.value)}
                    helperText="Same date and times — only the court changes.">
                    {COURTS.filter(ct => !(viewingMemberBooking.courts || []).includes(ct.id))
                      .map(ct => <MenuItem key={ct.id} value={ct.id}>{ct.title}</MenuItem>)}
                  </TextField>
                  <Button variant="outlined" disabled={!moveToCourt || memberBookingSaving}
                    onClick={handleMoveCourt} sx={{ alignSelf: 'flex-start', mt: 0.25 }}>
                    Move
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Date" type="date" value={memberBookingForm.date}
                onChange={e => setMemberBookingForm(f => ({ ...f, date: e.target.value }))}
                InputLabelProps={{ shrink: true }} size="small" fullWidth />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField select label="Start" value={memberBookingForm.startTime || ''}
                  onChange={e => setMemberBookingForm(f => ({ ...f, startTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }} size="small" fullWidth>
                  {quarterHourOptions(memberBookingForm.startTime).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField select label="End" value={memberBookingForm.endTime || ''}
                  onChange={e => setMemberBookingForm(f => ({ ...f, endTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }} size="small" fullWidth>
                  {quarterHourOptions(memberBookingForm.endTime).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Box>
              <TextField label="Court" select value={memberBookingForm.court}
                onChange={e => setMemberBookingForm(f => ({ ...f, court: e.target.value }))}
                size="small" fullWidth>
                {COURTS.map(ct => <MenuItem key={ct.id} value={ct.id}>{ct.title}</MenuItem>)}
              </TextField>
              {GUEST_FEATURE_ENABLED && (<>
              <FormControlLabel
                control={<Checkbox checked={memberBookingForm.guest} disabled={guestUsedToday}
                  onChange={e => setMemberBookingForm(f => ({ ...f, guest: e.target.checked }))} />}
                label={guestUsedToday ? 'Guest pass already used today' : 'Bring a guest (+1)'} />
              {memberBookingForm.guest && (
                <Alert severity="info" sx={{ py: 0.5 }}>Your guest must complete mini-registration at the facility before entering.</Alert>
              )}
              </>)}
              {/* Court camera — shown to every member, usable by some.
                  A disabled switch with the reason under it is the only place
                  most people will ever find out the perk exists, which is why
                  it is not hidden from the tiers that lack it. */}
              <Box>
                <FormControlLabel
                  control={<Checkbox checked={streamEligibility.ok && memberBookingStream}
                    disabled={!streamEligibility.ok}
                    onChange={e => setMemberBookingStream(e.target.checked)} />}
                  label="Live stream & record this reservation" />
                <Typography variant="caption" sx={{ display: 'block', color: streamEligibility.ok ? '#888' : '#B26A00', ml: 4, mt: -0.5 }}>
                  {streamEligibility.ok
                    ? 'The court camera goes live for your reservation and the recording is saved to it afterwards. Anyone with the link can watch.'
                    : streamEligibility.message}
                </Typography>
              </Box>
              {memberTier?.pricingMode === 'court_rental' && rateChangeNotice(memberRentalRate, localToday()) && (
                <Alert severity="info">{rateChangeNotice(memberRentalRate, localToday())}</Alert>
              )}
              {memberBookingPreview && (
                <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: 2, p: 1.5 }}>
                  <Typography variant="body2">
                    {memberBookingPreview.weekendHours > 0
                      ? `Weekend: ${memberBookingPreview.weekendHours.toFixed(1)} hr`
                      : `Regular: ${memberBookingPreview.regularHours.toFixed(1)} hr · Prime: ${memberBookingPreview.primeHours.toFixed(1)} hr`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {memberBookingPreview.weekendHours > 0
                      ? `After booking: ${memberBookingPreview.remainingWeekendAfter.toFixed(1)} weekend hrs left this week`
                      : `After booking: ${memberBookingPreview.remainingRegularAfter.toFixed(1)} regular / ${memberBookingPreview.remainingPrimeAfter.toFixed(1)} prime hrs left this week`}
                  </Typography>
                </Box>
              )}
              {overageBillingOn && memberBookingPreview && memberBookingPreview.overageAmount > 0 && (
                <Box sx={{ mt: 1, mb: 1, p: 1.5, borderRadius: 1, backgroundColor: '#f4f6f9', border: '1px solid #e0e4e8' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#1E3A5F', display: 'block', mb: 0.5 }}>
                    {isCourtRental ? 'Payment' : 'How would you like to pay?'}
                  </Typography>
                  {/* Court rental is charged at booking, so there is nothing to
                      choose — saying so beats a disabled radio nobody can move. */}
                  {isCourtRental && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Court time is charged when you book. Cancel up to an hour before and it comes
                      back as court credit.
                    </Typography>
                  )}
                  {!isCourtRental && (
                  <RadioGroup value={courtBilling} onChange={e => setCourtBilling(e.target.value)}>
                    <FormControlLabel value="now" control={<Radio size="small" />}
                      label={<Typography variant="body2">Pay now — cancel up to an hour before and the amount returns as court credit</Typography>} />
                    <FormControlLabel value="monthly" control={<Radio size="small" />}
                      label={<Typography variant="body2">Bill my time once a month — invoiced on the last day of the month</Typography>} />
                  </RadioGroup>
                  )}
                  {courtCreditCents > 0 && (
                    <Typography variant="caption" sx={{ color: '#1E6B3C', fontWeight: 600 }}>
                      ${(courtCreditCents / 100).toFixed(2)} court credit available — applied before any card charge.
                    </Typography>
                  )}
                </Box>
              )}
              {overageBillingOn && memberBookingPreview && memberBookingPreview.overageAmount > 0 && (
                <Alert severity="warning">
                  {(Number(memberTier?.weeklyRegularHours) || 0) === 0
                    ? <>Court time: <b>${memberBookingPreview.overageAmount.toFixed(2)}</b> — invoiced by the club.</>
                    : <>This exceeds your weekly hours. Estimated overage: <b>${memberBookingPreview.overageAmount.toFixed(2)}</b> — invoiced by the club.</>}
                </Alert>
              )}
              {error && <Alert severity="error">{error}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMemberBookingDialog(false); setViewingMemberBooking(null); }}>Close</Button>
          {viewingMemberBooking ? (
            // A court admin can move a booking but not cancel it; the server
            // refuses anyone but the member or an admin.
            (isAdmin || viewingMemberBooking.memberId === currentUserUid) && (
              <Button color="error" variant="contained" disabled={memberBookingSaving} onClick={handleMemberCancel}>
                {memberBookingSaving ? 'Cancelling…' : 'Cancel Booking'}
              </Button>
            )
          ) : (
            <Button variant="contained" disabled={memberBookingSaving} onClick={handleMemberBook}
              sx={{ backgroundColor: '#2E86C1' }}>
              {memberBookingSaving ? 'Booking…' : 'Confirm Booking'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <style>{`
        .fc .fc-event-main { color: white !important; }
        .fc .member-booking-event { border: 2px dashed rgba(255,255,255,0.8) !important; }
        .fc .fc-bg-event { opacity: 1 !important; }
        .fc .fc-highlight {
          background: rgba(201, 168, 76, 0.45) !important;
          border: 2px dashed #C9A84C !important;
        }
        .fc .fc-event-title { color: white !important; font-weight: 600 !important; }
        .fc .fc-event-time { color: rgba(255,255,255,0.9) !important; }
        .fc .fc-daygrid-event { color: white !important; }
        .fc .fc-daygrid-event .fc-event-title { color: white !important; font-weight: 600 !important; }
        .fc .fc-daygrid-event .fc-event-time { color: rgba(255,255,255,0.9) !important; }
      `}</style>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>Schedule</Typography>
            <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
              Drag to create · Drag events to move · Click to edit
            </Typography>
          </Box>
          {(isAdmin || isCoach) && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isAdmin && (
                <Button variant="outlined" onClick={() => {
                  setMergeSelected([]); setMergeCoach(''); setMergeCourts([]);
                  setMergeDate(''); setMergeStartTime(''); setMergeEndTime('');
                  setMergeDialog(true);
                }} sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}>
                  Merge Classes
                </Button>
              )}
              {isAdmin && (
                <Button variant="outlined" onClick={() => {
                  setCancelTypes([]); setCancelReason('');
                  setCancelDialog(true);
                }} sx={{ borderColor: '#C0392B', color: '#C0392B' }}>
                  Cancel Classes
                </Button>
              )}
              {isAdmin && (
                <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => { setPendingSelect(null); setSelectedClass(null); setClassForm({ ...emptyClassForm }); setClassDialog(true); }}
                  sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}>
                  New Event
                </Button>
              )}
            </Box>
          )}
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}
            onClose={() => { setSuccess(''); setUndoMove(null); }}
            action={undoMove ? (
              <Button color="inherit" size="small" onClick={runUndoMove} sx={{ fontWeight: 700 }}>UNDO</Button>
            ) : undefined}>
            {success}
          </Alert>
        )}
        {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {depositOwedCents > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}
            action={
              <Button size="small" variant="contained" disabled={depositBusy}
                onClick={async () => {
                  setDepositBusy(true);
                  try {
                    const fn = httpsCallable(getFunctions(), 'createDepositCheckoutSession');
                    const { data } = await fn({});
                    if (data?.url) { window.location.href = data.url; return; }
                    // Someone else recorded it while this page was open.
                    setDepositOwedCents(0);
                  } catch (e) {
                    setError(e.message || 'Could not start the deposit payment.');
                  }
                  setDepositBusy(false);
                }}>
                {depositBusy ? 'Working…' : 'Pay deposit'}
              </Button>
            }>
            A refundable security deposit of ${(depositOwedCents / 100).toFixed(2)} is needed before
            you can book. It is held against the account and returned when you stop renting — or pay
            the club directly and we'll record it.
          </Alert>
        )}

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {Object.entries(CLASS_COLORS).map(([type, color]) => (
            <Chip key={type} label={formatSessionLabel(type)} size="small"
              sx={{ backgroundColor: color, color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
          ))}
          {RESERVATION_TYPES.map(t => (
            <Chip key={t.value} label={t.label} size="small"
              sx={{ backgroundColor: t.color, color: 'white', fontWeight: 600, fontSize: '0.7rem',
                    border: '2px dashed rgba(255,255,255,0.4)' }} />
          ))}
          <Chip label="League" size="small"
            sx={{ backgroundColor: LEAGUE_COLOR, color: 'white', fontWeight: 600, fontSize: '0.7rem',
                  border: '2px dashed rgba(255,255,255,0.4)' }} />
          <Chip label="Customer" size="small"
            sx={{ backgroundColor: CUSTOMER_COLOR, color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
        </Box>

        {/* Pre-go-live notice. Replaces the hours chips rather than sitting
            beside them — "2.0 prime hrs left" next to "you can't book yet"
            reads as a bug. The calendar below stays visible and read-only. */}
        {memberTier && !bookingOpen && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Court reservations open {formatOpenDate(policyCtl.reservationsOpenDate)}.
            You can see the schedule now — booking turns on that morning.
          </Alert>
        )}

        {/* Member hours-remaining banner */}
        {memberTier && bookingOpen && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip size="small" label={`Regular: ${Math.max(0, (Number(memberTier.weeklyRegularHours) || 0) - ((memberUsageThisWeek?.regularUsed) || 0)).toFixed(1)} hrs left`}
              sx={{ backgroundColor: '#2E86C1', color: 'white', fontWeight: 600 }} />
            <Chip size="small" label={`Weekend: ${Math.max(0, (Number(memberTier.weeklyWeekendHours) || 0) - ((memberUsageThisWeek?.weekendUsed) || 0)).toFixed(1)} hrs left`}
              sx={{ backgroundColor: 'rgba(46,134,193,0.12)', color: '#1E3A5F', fontWeight: 600 }} />
            <Chip size="small" label={`Prime: ${Math.max(0, (Number(memberTier.weeklyPrimeHours) || 0) - ((memberUsageThisWeek?.primeUsed) || 0)).toFixed(1)} hrs left`}
              sx={{ backgroundColor: '#C9A84C', color: '#0D1E35', fontWeight: 700 }} />
            <Typography variant="caption" sx={{ color: '#888' }}>
              Resets Sunday · {memberTier.name || 'Member'}
            </Typography>
          </Box>
        )}

        {/* Calendar nav + view switcher */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={() => nav('prev')} size="small"><ChevronLeftIcon /></IconButton>
          <IconButton onClick={() => nav('today')} size="small"><TodayIcon /></IconButton>
          <IconButton onClick={() => nav('next')} size="small"><ChevronRightIcon /></IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E3A5F', ml: 1, flexGrow: 1 }}>
            {calendarTitle}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {[
              { key: 'dayGridMonth',         label: 'Month' },
              { key: 'timeGridWeek',          label: 'Week' },
              { key: 'resourceTimeGridDay',   label: 'Day' },
              { key: 'resourceTimelineWeek',  label: 'Timeline' },
            ].map(v => (
              <Button key={v.key} size="small"
                variant={view === v.key ? 'contained' : 'outlined'}
                onClick={() => switchView(v.key)}
                sx={{
                  minWidth: 72,
                  backgroundColor: view === v.key ? '#1E3A5F' : 'transparent',
                  borderColor: '#1E3A5F', color: view === v.key ? 'white' : '#1E3A5F',
                  '&:hover': { backgroundColor: view === v.key ? '#2E6DA4' : '#DEEAF1' },
                }}>
                {v.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* FullCalendar */}
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[resourceTimeGridPlugin, resourceTimelinePlugin, dayGridPlugin, timeGridPlugin, interactionPlugin, scrollGridPlugin]}
            schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
            initialView="resourceTimeGridDay"
            resources={COURTS}
            events={calendarEvents}
            selectable={canSelectOnCalendar}
            selectAllow={selectAllow}
            eventTextColor="white"
            eventDisplay="block"
            selectMirror={true}
            editable={isAdmin && !isCoach}
            eventOrder="courtOrder,start"
            eventResizableFromStart={false}
            headerToolbar={false}
            slotMinTime="07:30:00"
            slotMaxTime="22:00:00"
            slotDuration="00:15:00"
            slotLabelInterval={HOUR_LABEL_INTERVAL}
            slotLabelContent={hourLabelOnly}
            height="auto"
            contentHeight={680}
            nowIndicator={true}
            allDaySlot={false}
            resourceAreaWidth="90px"
            resourceAreaHeaderContent="Court"
            longPressDelay={250}
            selectLongPressDelay={250}
            eventLongPressDelay={250}
            select={handleSelect}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            titleFormat={{ weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }}
            datesSet={(info) => {
              if (info.view.type === 'resourceTimeGridDay' || info.view.type === 'timeGridDay') {
                const d = info.start;
                const formatted = d.toLocaleDateString('en-US', {
                  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                });
                setCalendarTitle(formatted);
              } else {
                setCalendarTitle(info.view.title);
              }
            }}
            eventContent={(info) => {
              const props = info.event.extendedProps;
              const isDashed = props.eventType === 'reservation';
              const isCanceled = props.eventType === 'class' && props.status === 'canceled';
              const coach = props.eventType === 'class' && props.headCoachId
                ? coaches.find(co => co.id === props.headCoachId)
                : null;
              const courtLabel = props.courtNumber
                ? `Court ${(props.courtNumber || '').replace('court-', '')}`
                : '';
              return (
                <Box sx={{ px: 0.5, py: 0.25, overflow: 'hidden', height: '100%',
                  border: isDashed ? '1px dashed rgba(255,255,255,0.5)' : 'none', borderRadius: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, color: 'white', display: 'block',
                    lineHeight: 1.2, fontSize: '0.72rem' }}>
                    {info.event.title}
                  </Typography>
                  {isCanceled ? (
                    <>
                      <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem', display: 'block' }}>
                        {props.startTime}–{props.endTime}{courtLabel ? ` · ${courtLabel}` : ''}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.65rem', fontStyle: 'italic', display: 'block' }}>
                        Canceled{props.cancelReason ? ` — ${props.cancelReason}` : ''}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem' }}>
                        {info.timeText}
                        {props.courtNumber && (
                          <> · {props.courtNumber?.startsWith('court-')
                            ? `Court ${props.courtNumber.replace('court-', '')}`
                            : `Court ${props.courtNumber}`}</>
                        )}
                      </Typography>
                      {coach && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.65rem', display: 'block' }}>
                          Coach {coach.firstName}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              );
            }}
          />
        </Paper>

        {/* ── Create dialog (choose class vs reservation) ── */}
        <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
            What would you like to create?
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Button fullWidth variant="outlined" size="large" onClick={openCreateClass}
                sx={{ py: 3, borderColor: '#1E3A5F', color: '#1E3A5F', flexDirection: 'column', gap: 1,
                  '&:hover': { backgroundColor: '#DEEAF1' } }}>
                <Typography variant="h6">📅</Typography>
                <Typography variant="body2" fontWeight={600}>Class</Typography>
                <Typography variant="caption" sx={{ color: '#888' }}>Practice, private, drop-in</Typography>
              </Button>
              <Button fullWidth variant="outlined" size="large" onClick={openCreateReservation}
                sx={{ py: 3, borderColor: '#1E3A5F', color: '#1E3A5F', flexDirection: 'column', gap: 1,
                  '&:hover': { backgroundColor: '#DEEAF1' } }}>
                <Typography variant="h6">🏐</Typography>
                <Typography variant="body2" fontWeight={600}>Reservation</Typography>
                <Typography variant="caption" sx={{ color: '#888' }}>Tournament, blocked time</Typography>
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* ── Class dialog ── */}
        <Dialog open={classDialog} onClose={() => { setClassDialog(false); setError(''); }} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
            {selectedClass ? 'Edit Event' : 'New Event'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField fullWidth label="Event Name (optional)" value={classForm.eventName || ''}
                onChange={e => setClassForm(f => ({ ...f, eventName: e.target.value }))}
                placeholder="e.g. Summer Camp Day 1"
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <TextField fullWidth label="URL Slug (optional)" value={classForm.slug || ''}
                onChange={e => setClassForm(f => ({ ...f, slug: e.target.value }))}
                placeholder="auto-generated from event name + date"
                helperText="Used to share a link to this event. Leave blank to auto-generate; uniqueness is enforced."
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <TextField fullWidth label="Description (optional)" value={classForm.description || ''}
                onChange={e => setClassForm(f => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Open to all skill levels. Bring water and sunscreen."
                multiline rows={2}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              {/* The court scoreboard's top row. vb-scores draws its own heading
                  ("RESERVED FOR") unless this is ticked; the API sends the
                  description's first line, cut to the board's 25 characters. */}
              <Box sx={{ mt: -1 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={classForm.scoreboardUseDescription === true}
                    onChange={e => setClassForm(f => ({ ...f, scoreboardUseDescription: e.target.checked }))} />}
                  label={<Typography variant="body2">Show the description as the scoreboard's top line</Typography>}
                />
                <Typography variant="caption" sx={{ display: 'block', color: '#888', ml: 4 }}>
                  {classForm.scoreboardUseDescription === true
                    ? (() => {
                        const first = (String(classForm.description || '').split(/\r?\n/).map(l => l.trim()).find(Boolean) || '').replace(/\s+/g, ' ');
                        if (!first) return 'Enter a description — with it blank the scoreboard keeps its usual heading.';
                        return first.length > 25
                          ? `The board fits 25 characters; this first line is ${first.length}, so it will be cut at a word.`
                          : 'The first line of the description is the top line.';
                      })()
                    : 'Unticked: the scoreboard shows its usual heading, e.g. "RESERVED FOR".'}
                </Typography>
              </Box>
              <TextField fullWidth label="Admin Notes (optional)" value={classForm.notes || ''}
                onChange={e => setClassForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Internal notes — not shown publicly."
                multiline rows={2}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <TextField fullWidth label="Max Athletes" type="number"
                value={classForm.maxAthletes === 0 ? '0' : (classForm.maxAthletes || '')}
                onChange={e => setClassForm(f => ({ ...f, maxAthletes: e.target.value }))}
                placeholder="0 = unlimited"
                helperText="0 or blank = unlimited. Otherwise the class size cap — reserved + checked-in spots count, and extra sign-ups go to the wait-list."
                inputProps={{ min: 0 }}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <TextField fullWidth select label="QR Check-In Plan (optional)" value={classForm.qrPlanId || ''}
                onChange={e => setClassForm(f => ({ ...f, qrPlanId: e.target.value }))}
                helperText="Plan shown when someone scans the QR code for this class"
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                <MenuItem value="">— Auto-select by class type —</MenuItem>
                {plans.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.planName} (${p.price} · {p.sessionsIncluded} session{p.sessionsIncluded !== 1 ? 's' : ''})</MenuItem>
                ))}
              </TextField>
              <TextField fullWidth select label="Event Color" value={classForm.eventColor || ''}
                onChange={e => setClassForm(f => ({ ...f, eventColor: e.target.value }))}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                SelectProps={{ renderValue: (val) => {
                  const opt = EVENT_COLOR_OPTIONS.find(o => o.value === val);
                  const defaultColor = CLASS_COLORS[classForm.sessionType] || '#1E3A5F';
                  const color = val || defaultColor;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                      {opt ? opt.label : 'Default'}
                    </Box>
                  );
                }}}>
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: CLASS_COLORS[classForm.sessionType] || '#1E3A5F', flexShrink: 0 }} />
                    Default ({EVENT_COLOR_OPTIONS.find(o => o.value === (CLASS_COLORS[classForm.sessionType] || '#1E3A5F'))?.label || 'Navy'})
                  </Box>
                </MenuItem>
                {EVENT_COLOR_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: opt.value, flexShrink: 0 }} />
                      {opt.label}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
              <TextField fullWidth select label="Session Type" value={classForm.sessionType}
                onChange={e => setClassForm(f => ({ ...f, sessionType: e.target.value, eventColor: f.eventColor || '' }))}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                {SESSION_TYPES.map(t => <MenuItem key={t} value={t}>{formatSessionLabel(t)}</MenuItem>)}
              </TextField>
              <TextField fullWidth select label="Customer (optional)" value={classForm.customerId || ''}
                onChange={e => setClassForm(f => ({ ...f, customerId: e.target.value }))}
                helperText="Link this event to a customer — it will show in their reservation history and the Court Usage report."
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                SelectProps={{ renderValue: (val) => {
                  if (!val) return '— No customer —';
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: CUSTOMER_COLOR, flexShrink: 0 }} />
                      {customerDisplayName(customers.find(c => c.id === val)) || val}
                    </Box>
                  );
                }}}>
                <MenuItem value="">— No customer —</MenuItem>
                {customers.map(c => (
                  <MenuItem key={c.id} value={c.id}>{customerDisplayName(c)}{c.email ? ` (${c.email})` : ''}</MenuItem>
                ))}
              </TextField>
              <TextField fullWidth select label="Skill Group" value={classForm.skillGroup}
                onChange={e => setClassForm(f => ({ ...f, skillGroup: e.target.value }))}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                {SKILL_GROUPS.map(s => <MenuItem key={s} value={s}>{s || '— Any —'}</MenuItem>)}
              </TextField>
              <TextField fullWidth label="Date" type="date" value={classForm.date}
                onChange={e => setClassForm(f => ({ ...f, date: e.target.value }))}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                InputLabelProps={{ shrink: true }} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Quarter hours, the calendar's own grid, rather than a
                    free time box that takes 7:07. */}
                <TextField fullWidth select label="Start Time" value={classForm.startTime || ''}
                  onChange={e => setClassForm(f => ({ ...f, startTime: e.target.value }))}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} InputLabelProps={{ shrink: true }}>
                  {quarterHourOptions(classForm.startTime).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField fullWidth select label="End Time" value={classForm.endTime || ''}
                  onChange={e => setClassForm(f => ({ ...f, endTime: e.target.value }))}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} InputLabelProps={{ shrink: true }}>
                  {quarterHourOptions(classForm.endTime).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>Courts *</Typography>
                  <Button size="small" onClick={() => setClassForm(f => ({
                    ...f,
                    courts: f.courts?.length === COURTS.length ? [] : COURTS.map(c => c.id),
                    courtNumber: f.courts?.length === COURTS.length ? '' : COURTS[0].id,
                  }))} sx={{ fontSize: '0.7rem', color: '#1E3A5F', p: 0, minWidth: 0 }}>
                    {(classForm.courts || []).length === COURTS.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {COURTS.map(c => {
                    const selected = (classForm.courts || [classForm.courtNumber]).includes(c.id);
                    return (
                      <Box key={c.id} onClick={() => setClassForm(f => {
                        const cur = f.courts || [f.courtNumber];
                        const next = selected ? cur.filter(x => x !== c.id) : [...cur, c.id];
                        return { ...f, courts: next, courtNumber: next[0] || '' };
                      })} sx={{
                        px: 2, py: 0.8, borderRadius: 1, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                        border: `2px solid ${selected ? '#1E3A5F' : '#ddd'}`,
                        backgroundColor: selected ? '#1E3A5F' : 'white',
                        color: selected ? 'white' : '#555',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: '#1E3A5F', backgroundColor: selected ? '#2E6DA4' : '#DEEAF1' },
                      }}>
                        {c.title}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <TextField fullWidth select label="Head Coach" value={classForm.headCoachId}
                onChange={e => setClassForm(f => ({ ...f, headCoachId: e.target.value, assistantCoachIds: (f.assistantCoachIds || []).filter(id => id !== e.target.value) }))}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                <MenuItem value="">— None —</MenuItem>
                {coaches
                  .filter(c => {
                    // Always show the currently-assigned coach so they appear in the field on edit
                    if (c.id === classForm.headCoachId) return true;
                    // Exclude coaches already assigned as head coach at same time (except current class)
                    if (!classForm.date || !classForm.startTime || !classForm.endTime) return true;
                    const conflict = classes.some(cl =>
                      cl.id !== (selectedClass?.id) &&
                      cl.date === classForm.date &&
                      cl.headCoachId === c.id &&
                      cl.startTime < classForm.endTime &&
                      cl.endTime > classForm.startTime
                    );
                    return !conflict;
                  })
                  .map(c => <MenuItem key={c.id} value={c.id}>{c.firstName} {c.lastName}</MenuItem>)}
              </TextField>
              <TextField fullWidth select label="Assistant Coach" value={classForm.assistantCoachIds?.[0] || ''}
                onChange={e => {
                  const val = e.target.value;
                  setClassForm(f => ({ ...f, assistantCoachIds: val ? [val] : [] }));
                }}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                <MenuItem value="">— None —</MenuItem>
                {coaches
                  .filter(c => {
                    // Always show the currently-assigned assistant coach so they appear in the field on edit
                    if (classForm.assistantCoachIds?.includes(c.id)) return true;
                    // Exclude head coach and coaches with conflicts at same time
                    if (c.id === classForm.headCoachId) return false;
                    if (!classForm.date || !classForm.startTime || !classForm.endTime) return true;
                    const conflict = classes.some(cl =>
                      cl.id !== (selectedClass?.id) &&
                      cl.date === classForm.date &&
                      (cl.headCoachId === c.id || (cl.assistantCoachIds || []).includes(c.id)) &&
                      cl.startTime < classForm.endTime &&
                      cl.endTime > classForm.startTime
                    );
                    return !conflict;
                  })
                  .map(c => <MenuItem key={c.id} value={c.id}>{c.firstName} {c.lastName}</MenuItem>)}
              </TextField>
              <TextField fullWidth select label="Status" value={classForm.status}
                onChange={e => setClassForm(f => ({ ...f, status: e.target.value }))}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
              </TextField>
              {classForm.status === 'canceled' && (
                <TextField fullWidth select label="Cancel Reason" value={classForm.cancelReason}
                  onChange={e => setClassForm(f => ({ ...f, cancelReason: e.target.value }))}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                  <MenuItem value="">— None —</MenuItem>
                  {['No Coach Available', 'Weather', 'Holiday', 'Low Attendance'].map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </TextField>
              )}
              <Box>
                <FormControlLabel control={
                  <Checkbox checked={classForm.repeat}
                    onChange={e => setClassForm(f => ({
                      ...f, repeat: e.target.checked,
                      // Start from the class's own weekday, the usual answer.
                      repeatDays: e.target.checked && !(f.repeatDays || []).length && f.date
                        ? [DAY_NAMES[new Date(f.date + 'T12:00:00').getDay()]]
                        : f.repeatDays,
                    }))} />
                } label="Repeat weekly" />
                {selectedClass && !selectedClass.repeatGroupId && classForm.repeat && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#666', ml: 4, mt: -0.5 }}>
                    This class becomes the first date of a weekly series, keeping its check-ins and log.
                  </Typography>
                )}
                {classForm.repeat && (
                  <Box sx={{ ml: 2, mt: 1 }}>
                    <FormLabel>Repeat on:</FormLabel>
                    <FormGroup row>
                      {DAY_NAMES.map(d => (
                        <FormControlLabel key={d} label={d.slice(0,3)} control={
                          <Checkbox size="small" checked={(classForm.repeatDays || []).includes(d)}
                            onChange={e => setClassForm(f => ({
                              ...f,
                              repeatDays: e.target.checked
                                ? [...(f.repeatDays || []), d]
                                : (f.repeatDays || []).filter(x => x !== d)
                            }))} />
                        } />
                      ))}
                    </FormGroup>
                    <TextField fullWidth label="Repeat until" type="date" value={classForm.repeatUntil}
                      onChange={e => setClassForm(f => ({ ...f, repeatUntil: e.target.value }))}
                      sx={{ mt: 1, backgroundColor: '#eeeeee', borderRadius: 1 }}
                      InputLabelProps={{ shrink: true }} />
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
            <Box>
              {selectedClass && (
                <Button color="error" startIcon={<DeleteIcon />}
                  onClick={() => selectedClass.repeatGroupId
                    ? (setDeleteAllCourts(false), setDeleteScopeDialog(true))
                    : requestDeleteClass('this')}>
                  Delete
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => { setClassDialog(false); setError(''); }}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveClass} disabled={saving}
                sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}>
                {saving ? 'Saving...' : selectedClass ? 'Update' : 'Create'}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* ── Reservation dialog ── */}
        <Dialog open={resDialog} onClose={() => { setResDialog(false); setError(''); }} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
            {selectedRes ? 'Edit Reservation' : 'New Reservation'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField fullWidth label="Title *" value={resForm.title}
                onChange={e => setResForm(f => ({ ...f, title: e.target.value }))}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <TextField fullWidth select label="Type" value={resForm.type}
                onChange={e => setResForm(f => ({ ...f, type: e.target.value }))}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                {RESERVATION_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: t.color }} />
                      {t.label}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
              <TextField fullWidth label="Date *" type="date" value={resForm.date}
                onChange={e => setResForm(f => ({ ...f, date: e.target.value }))}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} InputLabelProps={{ shrink: true }} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth select label="Start Time *" value={resForm.startTime || ''}
                  onChange={e => setResForm(f => ({ ...f, startTime: e.target.value }))}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} InputLabelProps={{ shrink: true }}>
                  {quarterHourOptions(resForm.startTime).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField fullWidth select label="End Time *" value={resForm.endTime || ''}
                  onChange={e => setResForm(f => ({ ...f, endTime: e.target.value }))}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} InputLabelProps={{ shrink: true }}>
                  {quarterHourOptions(resForm.endTime).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Courts *</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {COURTS.map(court => (
                    <Chip key={court.id} label={court.title} onClick={() => toggleCourt(court.id)}
                      variant={resForm.courts.includes(court.id) ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: resForm.courts.includes(court.id) ? '#1E3A5F' : 'transparent',
                        color: resForm.courts.includes(court.id) ? 'white' : '#1E3A5F',
                        borderColor: '#1E3A5F', fontWeight: 600,
                        '&:hover': { backgroundColor: resForm.courts.includes(court.id) ? '#2E6DA4' : '#DEEAF1' },
                      }} />
                  ))}
                </Box>
                <Button size="small" sx={{ mt: 0.5, color: '#888' }}
                  onClick={() => setResForm(f => ({
                    ...f, courts: f.courts.length === COURTS.length ? [] : COURTS.map(c => c.id)
                  }))}>
                  {resForm.courts.length === COURTS.length ? 'Deselect All' : 'Select All Courts'}
                </Button>
              </Box>
              <TextField fullWidth label="Notes" value={resForm.notes}
                onChange={e => setResForm(f => ({ ...f, notes: e.target.value }))}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} multiline rows={2} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
            <Box>
              {selectedRes && (
                <Button color="error" startIcon={<DeleteIcon />} onClick={handleDeleteRes}>Delete</Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => { setResDialog(false); setError(''); }}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveRes} disabled={saving}
                sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}>
                {saving ? 'Saving...' : selectedRes ? 'Update' : 'Create'}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* ── Update scope dialog ── */}
        <Dialog open={updateScopeDialog} onClose={() => setUpdateScopeDialog(false)}>
          <DialogTitle>Update Recurring Classes</DialogTitle>
          <DialogContent>
            <Typography>This class is part of a recurring series. Which classes do you want to update?</Typography>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
            <Button fullWidth variant="outlined" onClick={() => updateSeries('this')}>This class only</Button>
            <Button fullWidth variant="outlined" onClick={() => updateSeries('future')}>This and future classes</Button>
            <Button fullWidth variant="outlined" onClick={() => updateSeries('all')}>All classes in series</Button>
            <Button fullWidth onClick={() => setUpdateScopeDialog(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* ── Delete scope dialog ── */}
        <Dialog open={deleteScopeDialog} onClose={() => setDeleteScopeDialog(false)}>
          <DialogTitle>Delete Recurring Classes</DialogTitle>
          <DialogContent>
            <Typography>This class is part of a recurring series. Which classes do you want to delete?</Typography>
            {seriesCourts().length > 1 && (
              <Box sx={{ mt: 1.5 }}>
                <FormControlLabel
                  control={<Checkbox checked={deleteAllCourts} onChange={e => setDeleteAllCourts(e.target.checked)} />}
                  label={`Also delete the other courts (this series runs on Court ${seriesCourts().map(courtNum).join(', ')})`}
                />
                {!deleteAllCourts && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>
                    Only Court {courtNum(selectedClass?.courtNumber)} will be affected.
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
            <Button fullWidth variant="outlined" color="error" onClick={() => requestDeleteClass('this')}>
              This class only
            </Button>
            <Button fullWidth variant="outlined" color="error" onClick={() => requestDeleteClass('future')}>
              This and future classes ({classesForScope('future').length})
            </Button>
            <Button fullWidth variant="outlined" color="error" onClick={() => requestDeleteClass('all')}>
              All classes in series ({classesForScope('all').length})
            </Button>
            <Button fullWidth onClick={() => setDeleteScopeDialog(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* ── Delete confirmation (multi-class scopes only) ── */}
        <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ color: '#C0392B' }}>
            Delete {deleteConfirm?.count} classes?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1 }}>
              This permanently deletes <strong>{deleteConfirm?.count} classes</strong>
              {deleteConfirm?.courts?.length > 0 && (
                <> on <strong>Court {deleteConfirm.courts.map(courtNum).join(', ')}</strong></>
              )}
              {deleteConfirm?.first && deleteConfirm?.last && (
                <> from <strong>{deleteConfirm.first}</strong> through <strong>{deleteConfirm.last}</strong></>
              )}.
            </Typography>
            {deleteConfirm?.kept > 0 && (
              <Typography variant="body2" sx={{ mb: 1, color: '#1E6B3C' }}>
                {deleteConfirm.kept} past class{deleteConfirm.kept === 1 ? '' : 'es'} with check-ins
                {deleteConfirm.kept === 1 ? ' is' : ' are'} excluded and will be kept.
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: '#888' }}>
              This can't be undone from the app.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="contained" color="error"
              onClick={() => handleDeleteClass(deleteConfirm.scope, deleteConfirm.list)}>
              Delete {deleteConfirm?.count} classes
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Drag scope dialog ── */}
        {/* ── Class readonly view dialog ── */}
        <Dialog open={classViewDialog} onClose={() => setClassViewDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{
            backgroundColor: '#0D1E35', color: 'white', fontWeight: 700,
            py: { xs: 1.5, md: 2 }, px: { xs: 2, md: 3 },
          }}>
            <Box sx={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              gap: 1, mb: { xs: 1, md: viewingClass ? 1 : 0 },
            }}>
              <Box sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, flex: 1, minWidth: 0 }}>
                {viewingClass ? `${formatSessionLabel(viewingClass.sessionType)}${viewingClass.skillGroup ? ` — ${viewingClass.skillGroup}` : ''}` : 'Class Details'}
              </Box>
              {/* Mobile: overflow menu replacing the 4 buttons */}
              {isMobile && viewingClass && (
                <>
                  <IconButton size="small" onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
                    sx={{ color: '#C9A84C', mt: -0.5 }}>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={actionsMenuAnchor}
                    open={Boolean(actionsMenuAnchor)}
                    onClose={() => setActionsMenuAnchor(null)}
                    PaperProps={{ sx: { minWidth: 200 } }}
                  >
                    {isAdmin && (
                      <MenuItem onClick={() => { setActionsMenuAnchor(null); setQrDialog(true); }}>
                        <QrCode2Icon fontSize="small" sx={{ mr: 1, color: '#1E3A5F' }} />
                        QR Check-In
                      </MenuItem>
                    )}
                    {viewingClass.slug && (
                      <MenuItem onClick={() => {
                        setActionsMenuAnchor(null);
                        navigator.clipboard.writeText(`${window.location.origin}/event/${viewingClass.slug}`);
                        setSuccess('Link copied to clipboard.');
                        setTimeout(() => setSuccess(''), 2000);
                      }}>
                        Copy Event Link
                      </MenuItem>
                    )}
                    {(isAdmin || isCoach) && (
                      <MenuItem onClick={() => {
                        setActionsMenuAnchor(null);
                        if (!viewingClass) return;
                        const coach = coaches.find(c => c.id === viewingClass.headCoachId);
                        const emails = viewingAttendance
                          .map(a => a.email)
                          .filter(Boolean)
                          .join(',');
                        const subject = encodeURIComponent(`${formatSessionLabel(viewingClass.sessionType)} — ${viewingClass.date}`);
                        const body = encodeURIComponent(`Hi,\n\nThis is a reminder for your upcoming class:\n\nDate: ${viewingClass.date}\nTime: ${viewingClass.startTime} – ${viewingClass.endTime}${viewingClass.courtNumber ? `\nCourt: ${viewingClass.courtNumber.replace('court-', 'Court ')}` : ''}${coach ? `\nCoach: ${coach.firstName} ${coach.lastName}` : ''}\n\nSee you there!\n\nTorch Beach Volleyball`);
                        window.open(`mailto:${emails}?subject=${subject}&body=${body}`);
                      }}>
                        <EmailIcon fontSize="small" sx={{ mr: 1, color: '#1E3A5F' }} />
                        Email Class
                      </MenuItem>
                    )}
                    {isAdmin && (
                      <MenuItem onClick={() => {
                        setActionsMenuAnchor(null);
                        setClassViewDialog(false);
                        const props = viewingClass;
                        setSelectedClass({ id: viewingClass.id, ...props });
                        setClassForm({
                          customerId: props.customerId || '',
                          sessionType: props.sessionType || 'junior-practice',
                          skillGroup: props.skillGroup || '',
                          date: props.date || '',
                          startTime: props.startTime || '',
                          endTime: props.endTime || '',
                          eventName: props.eventName || '',
                          eventColor: props.eventColor || '',
                          description: props.description || '',
                          scoreboardUseDescription: props.scoreboardUseDescription === true,
                          notes: props.notes || '',
                          qrPlanId: props.qrPlanId || '',
                          slug: props.slug || '',
                          headCoachId: props.headCoachId || '',
                          assistantCoachIds: props.assistantCoachIds || [],
                          courtNumber: props.courtNumber || 'court-1',
                          // Every court this class actually runs on, not just
                          // this document's own — see courtsForEdit.
                          courts: courtsForEdit(props),
                          status: props.status || 'scheduled',
                          cancelReason: props.cancelReason || '',
                          maxAthletes: props.maxAthletes || '',
                          seasonId: props.seasonId || 'season_36',
                          repeat: !!props.repeatGroupId,
                          repeatDays: (() => {
                            if (!props.repeatGroupId) return [];
                            const groupClasses = classes.filter(c => c.repeatGroupId === props.repeatGroupId);
                            const days = [...new Set(groupClasses.map(c => DAY_NAMES[new Date(c.date + 'T12:00:00').getDay()]))];
                            return DAY_NAMES.filter(d => days.includes(d));
                          })(),
                          repeatUntil: (() => {
                            if (!props.repeatGroupId) return '';
                            const groupClasses = classes.filter(c => c.repeatGroupId === props.repeatGroupId);
                            return groupClasses.reduce((max, c) => c.date > max ? c.date : max, '');
                          })(),
                        });
                        setClassDialog(true);
                      }}>
                        Edit Class
                      </MenuItem>
                    )}
                    {isAdmin && (
                      <MenuItem disabled={attendanceSending}
                        onClick={() => { setActionsMenuAnchor(null); handleSendAttendance(); }}>
                        <SmsIcon fontSize="small" sx={{ mr: 1, color: '#1E3A5F' }} />
                        {attendanceSending ? 'Sending…' : 'Attendance'}
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>
            {/* Desktop: keep the 4-button row */}
            {!isMobile && viewingClass && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {/* QR Check-In: admin only. Opens the QR display dialog. */}
                {isAdmin && (
                  <Button size="small" variant="outlined"
                    startIcon={<QrCode2Icon />}
                    onClick={() => setQrDialog(true)}
                    sx={{ borderColor: 'rgba(201,168,76,0.5)', color: '#C9A84C', fontSize: '0.75rem',
                      '&:hover': { borderColor: '#C9A84C', backgroundColor: 'rgba(201,168,76,0.1)' } }}>
                    QR Check-In
                  </Button>
                )}
                {/* Copy Event Link: moved up from the body so all class-level
                    actions sit together. Visible whenever the class has a slug. */}
                {viewingClass.slug && (
                  <Button size="small" variant="outlined"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/event/${viewingClass.slug}`);
                      setSuccess('Link copied to clipboard.');
                      setTimeout(() => setSuccess(''), 2000);
                    }}
                    sx={{ borderColor: 'rgba(201,168,76,0.5)', color: '#C9A84C', fontSize: '0.75rem',
                      '&:hover': { borderColor: '#C9A84C', backgroundColor: 'rgba(201,168,76,0.1)' } }}>
                    Copy Event Link
                  </Button>
                )}
                {/* Email Class: admin/coach only. Opens default mail client
                    with attendee emails (BCC-friendly, no SMTP required). */}
                {(isAdmin || isCoach) && (
                  <Button size="small" variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={() => {
                      if (!viewingClass) return;
                      const coach = coaches.find(c => c.id === viewingClass.headCoachId);
                      const emails = viewingAttendance
                        .map(a => a.email)
                        .filter(Boolean)
                        .join(',');
                      const subject = encodeURIComponent(`${formatSessionLabel(viewingClass.sessionType)} — ${viewingClass.date}`);
                      const body = encodeURIComponent(`Hi,\n\nThis is a reminder for your upcoming class:\n\nDate: ${viewingClass.date}\nTime: ${viewingClass.startTime} – ${viewingClass.endTime}${viewingClass.courtNumber ? `\nCourt: ${viewingClass.courtNumber.replace('court-', 'Court ')}` : ''}${coach ? `\nCoach: ${coach.firstName} ${coach.lastName}` : ''}\n\nSee you there!\n\nTorch Beach Volleyball`);
                      window.open(`mailto:${emails}?subject=${subject}&body=${body}`);
                    }}
                    sx={{ borderColor: 'rgba(201,168,76,0.5)', color: '#C9A84C', fontSize: '0.75rem',
                      '&:hover': { borderColor: '#C9A84C', backgroundColor: 'rgba(201,168,76,0.1)' } }}>
                    Email Class
                  </Button>
                )}
                {/* Edit Class: admin only. */}
                {isAdmin && (
                  <Button size="small" variant="outlined"
                    onClick={() => {
                      setClassViewDialog(false);
                      const props = viewingClass;
                      setSelectedClass({ id: viewingClass.id, ...props });
                      setClassForm({
                    customerId: props.customerId || '',
                    sessionType: props.sessionType || 'junior-practice',
                    skillGroup: props.skillGroup || '',
                    date: props.date || '',
                    startTime: props.startTime || '',
                    endTime: props.endTime || '',
                    eventName: props.eventName || '',
                    eventColor: props.eventColor || '',
                    description: props.description || '',
                    scoreboardUseDescription: props.scoreboardUseDescription === true,
                    notes: props.notes || '',
                    qrPlanId: props.qrPlanId || '',
                    slug: props.slug || '',
                    headCoachId: props.headCoachId || '',
                    assistantCoachIds: props.assistantCoachIds || [],
                    courtNumber: props.courtNumber || 'court-1',
                    // Seed the multi-select from every court this class runs on,
                    // not just this document's. Seeding from one court showed the
                    // set as smaller than it is, and re-ticking a court already in
                    // the set was silently discarded as a duplicate. Without any
                    // seed the picker kept whatever the form last held — court-1
                    // from the empty form — so a second court was computed
                    // against stale state.
                    courts: courtsForEdit(props),
                    status: props.status || 'scheduled',
                    cancelReason: props.cancelReason || '',
                    maxAthletes: props.maxAthletes || '',
                    seasonId: props.seasonId || 'season_36',
                    repeat: !!props.repeatGroupId,
                    repeatDays: (() => {
                      if (!props.repeatGroupId) return [];
                      const groupClasses = classes.filter(c => c.repeatGroupId === props.repeatGroupId);
                      const days = [...new Set(groupClasses.map(c => DAY_NAMES[new Date(c.date + 'T12:00:00').getDay()]))];
                      return DAY_NAMES.filter(d => days.includes(d));
                    })(),
                    repeatUntil: (() => {
                      if (!props.repeatGroupId) return '';
                      const groupClasses = classes.filter(c => c.repeatGroupId === props.repeatGroupId);
                      return groupClasses.reduce((max, c) => c.date > max ? c.date : max, '');
                    })(),
                  });
                  setClassDialog(true);
                }}
                sx={{ borderColor: '#C9A84C', color: '#C9A84C', fontSize: '0.75rem', '&:hover': { borderColor: '#e0be6a', color: '#e0be6a' } }}>
                  Edit Class
                </Button>
                )}
                {/* Attendance: admin only. Texts the admin who taps it the
                    coaches and players checked in (sendAttendanceSummary). */}
                {isAdmin && (
                  <Button size="small" variant="outlined"
                    startIcon={<SmsIcon />}
                    disabled={attendanceSending}
                    onClick={handleSendAttendance}
                    sx={{ borderColor: '#C9A84C', color: '#C9A84C', fontSize: '0.75rem', '&:hover': { borderColor: '#e0be6a', color: '#e0be6a' } }}>
                    {attendanceSending ? 'Sending…' : 'Attendance'}
                  </Button>
                )}
              </Box>
            )}
          </DialogTitle>
          <DialogContent sx={{
            pt: 2, backgroundColor: '#0D1E35',
            // Lock dialog body to a flex column with a constrained height.
            // The metadata block stays pinned at the top while the athletes
            // list takes the remaining space and scrolls on its own.
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            // Full screen on phones, so the body simply takes what is left.
            maxHeight: { xs: 'none', sm: 'calc(100vh - 180px)' },
          }}>
            {attendanceNotice && (
              <Alert severity={attendanceNotice.severity} onClose={() => setAttendanceNotice(null)}
                sx={{ mb: 1.5, flexShrink: 0 }}>
                {attendanceNotice.text}
              </Alert>
            )}
            {viewingClass && (
              <Box sx={{
                mb: { xs: 1.5, md: 2 },
                p: { xs: 1.5, md: 2 },
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 2,
                border: '1px solid rgba(201,168,76,0.15)',
              }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 2 } }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(201,168,76,0.6)', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.65rem' }}>Date & Time</Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>{viewingClass.date} · {viewingClass.startTime} – {viewingClass.endTime}</Typography>
                  </Box>
                  {viewingClass.courtNumber && (
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(201,168,76,0.6)', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.65rem' }}>Court</Typography>
                      <Typography variant="body2" sx={{ color: 'white' }}>{viewingClass.courtNumber?.startsWith('court-') ? `Court ${viewingClass.courtNumber.replace('court-', '')}` : `Court ${viewingClass.courtNumber}`}</Typography>
                    </Box>
                  )}

                  {/* Coaches — ONE row, all screen sizes. Each coach is a chip:
                      green ✓ when marked attending, gold outline when not; a
                      tap toggles it (staff). Edit opens the head/assistant
                      dropdowns below for changing who is assigned. This
                      replaces both the mobile summary and the separate
                      Coaches list that used to sit above the athletes, which
                      showed the same people a second time.

                      Assigned coaches are shown whether or not they are marked
                      attending, so a gap is visible rather than silent. A coach
                      with a record but no assignment is shown too. */}
                  {(() => {
                    const assigned = [
                      ...(viewingClass.headCoachId ? [{ id: viewingClass.headCoachId, role: 'head', label: 'Head' }] : []),
                      ...((viewingClass.assistantCoachIds || []).filter(Boolean)
                        .map(id => ({ id, role: 'assistant', label: 'Assistant' }))),
                    ];
                    const assignedIds = new Set(assigned.map(r => r.id));
                    const strays = [...new Map(viewingAttendance
                      .filter(a => a.type === 'coach' && a.coachId && !assignedIds.has(a.coachId))
                      .map(a => [a.coachId, { id: a.coachId, role: a.coachRole || 'assistant', label: 'Not assigned' }])
                    ).values()];
                    const rows = [...assigned, ...strays];
                    const staff = isAdmin || isCoach;
                    return (
                      <Box sx={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap',
                        pt: 1, borderTop: '1px solid rgba(201,168,76,0.1)' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(201,168,76,0.6)', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.65rem', mr: 0.5 }}>
                          Coaches
                        </Typography>
                        {rows.length === 0 && (
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>None assigned</Typography>
                        )}
                        {rows.map(r => {
                          const c = coaches.find(x => x.id === r.id);
                          const rec = viewingAttendance.find(a => a.type === 'coach' && a.coachId === r.id);
                          const name = c
                            ? `${c.firstName || ''} ${(c.lastName || '').charAt(0)}${c.lastName ? '.' : ''}`.trim()
                            : (rec?.coachName || 'Unknown coach');
                          const tappable = staff && !!c;
                          return (
                            <Box key={`${r.id}-${r.role}`} component={tappable ? 'button' : 'span'}
                              title={`${r.label} coach${rec ? ' · attending' : ' · not marked attending'}${tappable ? ' — tap to toggle' : ''}`}
                              onClick={tappable ? () => handleCoachCheckIn(c, r.role) : undefined}
                              sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                px: 1.25, py: 0.4, borderRadius: 999, font: 'inherit', fontSize: '0.8rem', fontWeight: 600,
                                cursor: tappable ? 'pointer' : 'default',
                                color: rec ? 'white' : '#C9A84C',
                                backgroundColor: rec ? '#27AE60' : 'transparent',
                                border: `1px solid ${rec ? '#27AE60' : 'rgba(201,168,76,0.6)'}`,
                              }}>
                              {rec ? '✓ ' : ''}{name}
                              {r.role === 'head' && <Box component="span" sx={{ opacity: 0.7, fontWeight: 400, fontSize: '0.7rem' }}>· H</Box>}
                            </Box>
                          );
                        })}
                        {staff && (
                          <Button size="small" onClick={() => setCoachSectionOpen(v => !v)}
                            endIcon={coachSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{ color: '#C9A84C', minWidth: 0, fontSize: '0.75rem', ml: 'auto' }}>
                            {coachSectionOpen ? 'Done' : 'Edit'}
                          </Button>
                        )}
                      </Box>
                    );
                  })()}

                  {/* Coach assignment + check-in. Admins/coaches see editable
                      dropdowns that swap the assignment AND auto-check-in the
                      new coach in one tap (see handleSwapCoach). Non-staff see
                      a read-only label. The dropdowns hide the coach assigned
                      to the OTHER role to avoid double-booking the same person.
                      Wrapped in Collapse on mobile so it can be hidden by default. */}
                  <Box sx={{
                    gridColumn: { xs: '1 / -1', sm: 'auto' },
                    flexBasis: { xs: '100%', sm: 'auto' },
                    // Assignment dropdowns live behind the coach row's Edit.
                    display: (isAdmin || isCoach) && coachSectionOpen ? 'block' : 'none',
                  }}>
                    <Typography variant="caption" sx={{ color: 'rgba(201,168,76,0.6)', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.65rem' }}>Head Coach</Typography>
                    {(isAdmin || isCoach) ? (
                      <TextField
                        select fullWidth size="small"
                        value={viewingClass.headCoachId || ''}
                        onChange={e => handleSwapCoach('head', e.target.value)}
                        sx={{
                          mt: 0.5,
                          '& .MuiInputBase-input': { color: 'white', fontSize: '0.85rem', py: 0.6 },
                          '& .MuiSelect-icon': { color: '#C9A84C' },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'rgba(201,168,76,0.3)' },
                            '&:hover fieldset': { borderColor: 'rgba(201,168,76,0.6)' },
                            '&.Mui-focused fieldset': { borderColor: '#C9A84C' },
                          },
                        }}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {coaches
                          .filter(c => !(viewingClass.assistantCoachIds || []).includes(c.id))
                          .map(c => {
                            const checkedIn = viewingAttendance.some(a => a.type === 'coach' && a.coachId === c.id);
                            return (
                              <MenuItem key={c.id} value={c.id}>
                                {c.firstName} {c.lastName}{checkedIn ? ' ✓' : ''}
                              </MenuItem>
                            );
                          })}
                      </TextField>
                    ) : (() => {
                      const coach = coaches.find(c => c.id === viewingClass.headCoachId);
                      return (
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          {coach ? `${coach.firstName} ${coach.lastName}` : '—'}
                        </Typography>
                      );
                    })()}
                  </Box>
                  <Box sx={{
                    gridColumn: { xs: '1 / -1', sm: 'auto' },
                    flexBasis: { xs: '100%', sm: 'auto' },
                    // Assignment dropdowns live behind the coach row's Edit.
                    display: (isAdmin || isCoach) && coachSectionOpen ? 'block' : 'none',
                  }}>
                    <Typography variant="caption" sx={{ color: 'rgba(201,168,76,0.6)', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.65rem' }}>Assistant Coach</Typography>
                    {(isAdmin || isCoach) ? (
                      <TextField
                        select fullWidth size="small"
                        value={(viewingClass.assistantCoachIds || [])[0] || ''}
                        onChange={e => handleSwapCoach('assistant', e.target.value)}
                        sx={{
                          mt: 0.5,
                          '& .MuiInputBase-input': { color: 'white', fontSize: '0.85rem', py: 0.6 },
                          '& .MuiSelect-icon': { color: '#C9A84C' },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'rgba(201,168,76,0.3)' },
                            '&:hover fieldset': { borderColor: 'rgba(201,168,76,0.6)' },
                            '&.Mui-focused fieldset': { borderColor: '#C9A84C' },
                          },
                        }}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {coaches
                          .filter(c => c.id !== viewingClass.headCoachId)
                          .map(c => {
                            const checkedIn = viewingAttendance.some(a => a.type === 'coach' && a.coachId === c.id);
                            return (
                              <MenuItem key={c.id} value={c.id}>
                                {c.firstName} {c.lastName}{checkedIn ? ' ✓' : ''}
                              </MenuItem>
                            );
                          })}
                      </TextField>
                    ) : (() => {
                      const asstId = (viewingClass.assistantCoachIds || [])[0];
                      const asst = coaches.find(c => c.id === asstId);
                      return (
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          {asst ? `${asst.firstName} ${asst.lastName}` : '—'}
                        </Typography>
                      );
                    })()}
                  </Box>
                  {viewingClass.status && viewingClass.status !== 'scheduled' && (
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(201,168,76,0.6)', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.65rem' }}>Status</Typography>
                      <Typography variant="body2" sx={{ color: viewingClass.status === 'canceled' ? '#E74C3C' : 'white', textTransform: 'capitalize' }}>{viewingClass.status}</Typography>
                    </Box>
                  )}
                  {viewingClass.maxAthletes && (
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(201,168,76,0.6)', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.65rem' }}>Capacity</Typography>
                      <Typography variant="body2" sx={{ color: 'white' }}>{[...new Map(viewingAttendance.filter(a => a.type !== 'coach' && a.status !== 'waitlisted').map(a => [a.athleteId, a])).values()].length} / {viewingClass.maxAthletes}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Athletes list. Single tap on a row toggles check-in:
                - Not checked in + has balance (or admin/coach): check in
                - Already checked in: tap to undo (admins/coaches only)
                Replaces the prior split Checked-In/Available columns for a
                cleaner mobile experience.
                flex/overflow combo makes the list the only scrolling region
                inside the dialog body — metadata AND the athletes-count header
                stay pinned while the list scrolls. */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'rgba(201,168,76,0.6)', mb: 1, letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.75rem', flexShrink: 0 }}>
                Athletes ({(() => {
                  const nonCoach = viewingAttendance.filter(a => a.type !== 'coach');
                  const checkedInCount = [...new Map(nonCoach.filter(a => a.status !== 'reserved' && a.status !== 'waitlisted').map(a => [a.athleteId, a])).values()].length;
                  const reservedCount = [...new Map(nonCoach.filter(a => a.status === 'reserved').map(a => [a.athleteId, a])).values()].length;
                  const waitCount = [...new Map(nonCoach.filter(a => a.status === 'waitlisted').map(a => [a.athleteId, a])).values()].length;
                  return `${checkedInCount} checked in${reservedCount ? ` \u00b7 ${reservedCount} reserved` : ''}${waitCount ? ` \u00b7 ${waitCount} wait-listed` : ''} / ${viewingAthletes.length} eligible`;
                })()})
            </Typography>
            {(isAdmin || isCoach) && viewingAthletes.length > 1 && (() => {
              const adultRoster = (viewingClass?.sessionType || '').toLowerCase().startsWith('adult');
              const sortLabels = adultRoster ? ADULT_ROSTER_SORT_LABELS : ROSTER_SORT_LABELS;
              return (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
              {/* Type a few letters of a first or last name to jump to them in
                  a long roster. Narrows whichever order is picked beside it. */}
              <TextField
                size="small" placeholder="Search names…" value={rosterSearch}
                onChange={e => setRosterSearch(e.target.value)}
                sx={{
                  mb: 1, flex: 1, minWidth: 150,
                  '& .MuiInputBase-root': { color: 'white', fontSize: '0.8rem' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
                }}
              />
              <TextField
                select size="small" value={rosterSort}
                onChange={e => setRosterSort(e.target.value)}
                disabled={athleteActivity === null}
                sx={{
                  mb: 1, minWidth: 190, flexShrink: 0,
                  '& .MuiInputBase-root': { color: 'white', fontSize: '0.8rem' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
                  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
                }}
                SelectProps={{ renderValue: v => sortLabels[v] || sortLabels.name }}
              >
                <MenuItem value="name">A–Z (name)</MenuItem>
                <MenuItem value="active">Most active first</MenuItem>
                <MenuItem value="checkedin">Checked in only</MenuItem>
                <MenuItem value="boys">{adultRoster ? 'Men only' : 'Boys only'}</MenuItem>
                <MenuItem value="girls">{adultRoster ? 'Women only' : 'Girls only'}</MenuItem>
              </TextField>
              </Box>
              );
            })()}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {athletesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} sx={{ color: '#C9A84C' }} /></Box>
              ) : (() => {
                // The last three dropdown choices narrow the roster instead of
                // reordering it. Checked-in is read the same way the rows below
                // read it, so the filter and the badges always agree.
                const rosterQuery = rosterSearch.trim().toLowerCase();
                const rosterVisible = [...viewingAthletes].filter(a => {
                  if (rosterQuery && !`${a.firstName || ''} ${a.lastName || ''}`.toLowerCase().includes(rosterQuery)) return false;
                  if (rosterSort === 'checkedin') {
                    const rec = viewingAttendance.find(att => (att.athleteId === a.id || att.athleteId === a.athleteId) && att.type !== 'coach');
                    if (!rec || rec.status === 'reserved' || rec.status === 'waitlisted') return false;
                  }
                  if (rosterSort === 'boys' && rosterGenderOf(a) !== 'male') return false;
                  if (rosterSort === 'girls' && rosterGenderOf(a) !== 'female') return false;
                  return true;
                }).sort((x, y) => {
                    // Sorting here rather than in state means toggling reorders
                    // instantly without touching Firestore.
                    if (rosterSort === 'active' && athleteActivity) {
                      const d = (athleteActivity[y.id] || 0) - (athleteActivity[x.id] || 0);
                      if (d) return d;          // ties fall back to alphabetical
                    }
                    return `${x.firstName || ''}${x.lastName || ''}`.toLowerCase()
                      .localeCompare(`${y.firstName || ''}${y.lastName || ''}`.toLowerCase());
                });
                if (viewingAthletes.length === 0) {
                  return <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>No athletes found.</Typography>;
                }
                // Without this a filter matching nobody left a blank panel.
                if (rosterVisible.length === 0) {
                  return <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>No athletes match this filter.</Typography>;
                }
                return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {rosterVisible.map(a => {
                    const attRec = viewingAttendance.find(att => (att.athleteId === a.id || att.athleteId === a.athleteId) && att.type !== 'coach');
                    const isReserved = !!attRec && attRec.status === 'reserved';
                    const isWaitlisted = !!attRec && attRec.status === 'waitlisted';
                    const checkedIn = !!attRec && !isReserved && !isWaitlisted;
                    const isFutureClass = !!(viewingClass && viewingClass.date > schedToday());
                    const classFull = !!(viewingClass && viewingClass.maxAthletes &&
                      viewingAttendance.filter(x => x.type !== 'coach' && x.status !== 'waitlisted').length >= viewingClass.maxAthletes);
                    const waitPos = isWaitlisted
                      ? viewingAttendance.filter(x => x.status === 'waitlisted').sort(sortByWaitTime).findIndex(x => x.id === attRec.id) + 1
                      : 0;
                    const initials = `${a.firstName?.charAt(0) || ''}${a.lastName?.charAt(0) || ''}`;
                    // Comped athletes always have 'balance' — they never spend one.
                    const hasBalance = isComped(a) || (a.sessionBalance || 0) > 0;
                    const canCheckIn = !attRec && !isFutureClass && (isAdmin || isCoach || hasBalance);
                    const canReserve = !attRec && isFutureClass;
                    // Undo is permitted for staff or for the record owner:
                    //   • adult athlete: a.email matches the logged-in email
                    //   • parent: a.parentUserId matches the logged-in uid
                    // Mirrors the permission check in handleUndoCheckIn.
                    const ownsThisAthlete = (
                      (a.email && currentUserEmail && a.email.toLowerCase() === currentUserEmail) ||
                      (a.parentUserId && a.parentUserId === currentUserUid)
                    );
                    // A player or parent may take back their own check-in only
                    // until the session starts; after that it is a staff
                    // decision, because by then it is a record of who was on
                    // the court. CheckIn.js has always worked this way; this
                    // screen did not, so the same check-in could be undone
                    // here after the fact.
                    const startedAlready = !!(viewingClass?.date && viewingClass?.startTime)
                      && new Date() >= new Date(`${viewingClass.date}T${viewingClass.startTime}`);
                    const canUndo = checkedIn
                      && (isAdmin || isCoach || (ownsThisAthlete && !startedAlready));
                    const canManageReservation = isReserved && (isAdmin || isCoach || ownsThisAthlete);
                    const canManageWaitlist = isWaitlisted && (isAdmin || isCoach || ownsThisAthlete);
                    const tappable = canCheckIn || canUndo || canReserve || canManageReservation || canManageWaitlist;
                    return (
                      <Box key={a.id}
                        onClick={() => {
                          if (canManageReservation) {
                            if (isFutureClass) handleCancelReservation(attRec);
                            else setReserveChoice({ athlete: a, attRec });
                          }
                          else if (canManageWaitlist) {
                            if (isAdmin || isCoach) setReserveChoice({ athlete: a, attRec });
                            else handleCancelReservation(attRec);
                          }
                          else if (canReserve) handleViewReserve(a);
                          else if (canCheckIn) handleViewCheckIn(a);
                          else if (canUndo && attRec) handleUndoCheckIn(attRec);
                        }}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1,
                        background: checkedIn ? 'rgba(39,174,96,0.08)' : isReserved ? 'rgba(201,168,76,0.1)' : isWaitlisted ? 'rgba(155,89,182,0.12)' : 'rgba(255,255,255,0.04)',
                        borderRadius: 1, border: `1px solid ${checkedIn ? 'rgba(39,174,96,0.2)' : isReserved ? 'rgba(201,168,76,0.35)' : isWaitlisted ? 'rgba(155,89,182,0.35)' : 'rgba(255,255,255,0.06)'}`,
                        cursor: tappable ? 'pointer' : 'default',
                        '&:hover': tappable ? (checkedIn
                          ? { borderColor: 'rgba(231,76,60,0.4)', background: 'rgba(231,76,60,0.08)' }
                          : { borderColor: 'rgba(201,168,76,0.4)', background: 'rgba(201,168,76,0.08)' }) : {} }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '50%',
                          backgroundColor: checkedIn ? '#27AE60' : isReserved ? '#C9A84C' : isWaitlisted ? '#9B59B6' : 'rgba(201,168,76,0.25)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Typography variant="caption" sx={{ color: checkedIn ? 'white' : isReserved ? '#0D1E35' : isWaitlisted ? 'white' : '#C9A84C', fontWeight: 700, fontSize: '0.7rem' }}>{initials}</Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '0.82rem' }}>
                            {a.firstName} {a.lastName}
                            {shirtAthleteIds.has(a.id) && (
                              <Box component="span" title="New junior — give shirt"
                                   sx={{ ml: 1, px: 0.75, py: 0.1, borderRadius: 1, fontSize: '0.7rem', fontWeight: 700,
                                         backgroundColor: tintColors.prime, color: '#C9A84C' }}>
                                👕 shirt
                              </Box>
                            )}
                            {' '}{checkedIn && '✓'} {checkingInAthlete === a.id && '...'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: !hasBalance && !checkedIn ? '#E74C3C' : 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>
                            {checkedIn
                              ? 'Checked in'
                              : isReserved
                                ? (isFutureClass ? 'Reserved — tap to cancel' : 'Reserved — tap to check in or cancel')
                                : isWaitlisted
                                  ? `Wait-list #${waitPos} — tap to leave`
                                  : isFutureClass
                                    ? (classFull ? 'Class full — tap to join wait-list' : 'Tap to reserve')
                                    : !hasBalance && !(isAdmin || isCoach)
                                      ? 'No sessions available'
                                      : `${isComped(a) ? 'Comp' : `${a.sessionBalance || 0} sessions`}${a.skillGroup ? ' · ' + a.skillGroup : ''}`}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                );
              })()}
            </Box>
          </DialogContent>
          <Dialog open={!!reserveChoice} onClose={() => setReserveChoice(null)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
              {reserveChoice ? `${reserveChoice.athlete.firstName} ${reserveChoice.athlete.lastName}` : ''}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Typography variant="body2">
                {reserveChoice?.attRec?.status === 'waitlisted'
                  ? 'This athlete is on the wait-list. Check them in (staff override — deducts a session) or remove them from the list.'
                  : 'This spot is reserved. Check in now (deducts a session) or cancel the reservation.'}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setReserveChoice(null)}>Close</Button>
              <Button color="error" variant="outlined" onClick={() => reserveChoice && handleCancelReservation(reserveChoice.attRec)}>
                {reserveChoice?.attRec?.status === 'waitlisted' ? 'Remove from Wait-list' : 'Cancel Reservation'}
              </Button>
              <Button variant="contained" sx={{ backgroundColor: '#1E3A5F' }} onClick={() => reserveChoice && handleViewCheckIn(reserveChoice.athlete)}>
                Check In
              </Button>
            </DialogActions>
          </Dialog>
          {(isAdmin || isCoach) && (
            <Box sx={{ px: 3, py: classLogOpen ? 2 : 1, backgroundColor: '#0D1E35', borderTop: '1px solid rgba(201,168,76,0.1)' }}>
              <Box onClick={() => setClassLogOpen(v => !v)}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mb: classLogOpen ? 1 : 0 }}>
                <Typography variant="caption" sx={{ color: 'rgba(201,168,76,0.6)', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  Class Log{classLogs.length ? ` (${classLogs.length})` : ''}
                </Typography>
                {classLogOpen ? <ExpandLessIcon sx={{ color: '#C9A84C', fontSize: 18 }} /> : <ExpandMoreIcon sx={{ color: '#C9A84C', fontSize: 18 }} />}
              </Box>
              {classLogOpen && classLogs.length > 0 && (
                <Box sx={{ mb: 1.5, maxHeight: 120, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {classLogs.map(log => {
                    const ts = log.timestamp?.toDate ? log.timestamp.toDate() : new Date();
                    const timeStr = ts.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                    return (
                      <Typography key={log.id} variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem', lineHeight: 1.4 }}>
                        <span style={{ color: '#C9A84C', fontWeight: 600 }}>{log.author}</span>
                        <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}> {timeStr}</span>
                        {' — '}{log.message}
                      </Typography>
                    );
                  })}
                </Box>
              )}
              {classLogOpen && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small" fullWidth
                  placeholder="Add a note..."
                  value={logInput}
                  onChange={e => setLogInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddLog(); } }}
                  sx={{ '& .MuiInputBase-input': { color: 'white', fontSize: '0.82rem' },
                    '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
                    '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' }, '&:hover fieldset': { borderColor: 'rgba(201,168,76,0.4)' }, '&.Mui-focused fieldset': { borderColor: '#C9A84C' } } }}
                />
                <Button size="small" variant="outlined" onClick={handleAddLog} disabled={logSaving || !logInput.trim()}
                  sx={{ borderColor: '#C9A84C', color: '#C9A84C', whiteSpace: 'nowrap', minWidth: 60,
                    '&:hover': { borderColor: '#e0be6a', backgroundColor: 'rgba(201,168,76,0.08)' } }}>
                  {logSaving ? '...' : 'Add'}
                </Button>
              </Box>
              )}
            </Box>
          )}
          <DialogActions sx={{ px: 2, py: { xs: 1, sm: 2 }, backgroundColor: '#0D1E35', borderTop: '1px solid rgba(201,168,76,0.1)' }}>
            <Button onClick={() => setClassViewDialog(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>Close</Button>
            <Box sx={{ flexGrow: 1 }} />
            {/* Cancel this one class. Admins / coaches only. Opens a small
                dialog with a reason dropdown (same reason list as bulk cancel).
                Distinct from the bulk cancel state. */}
            {(isAdmin || isCoach) && viewingClass?.status !== 'canceled' && (
              <Button
                variant="outlined"
                onClick={() => { setSingleCancelReason(''); setSingleCancelDialog(true); }}
                sx={{ borderColor: 'rgba(231,76,60,0.5)', color: '#E74C3C', '&:hover': { borderColor: '#E74C3C', backgroundColor: 'rgba(231,76,60,0.08)' } }}
              >
                Cancel Class
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* ── QR Check-In Dialog ── */}
        <Dialog open={qrDialog} onClose={() => setQrDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#0D1E35', color: 'white', fontWeight: 700 }}>
            QR Check-In
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: '#0D1E35', pt: 3, textAlign: 'center' }}>
            {viewingClass && (() => {
              const url = `${window.location.origin}/quick-checkin?classId=${viewingClass.id}&sessionType=${encodeURIComponent(viewingClass.sessionType || '')}`;
              const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(url)}`;
              return (
                <>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
                    {formatSessionLabel(viewingClass.sessionType)}{viewingClass.skillGroup ? ` — ${viewingClass.skillGroup}` : ''}<br/>
                    {viewingClass.date} · {viewingClass.startTime}–{viewingClass.endTime}
                  </Typography>
                  <Box sx={{ background: 'white', p: 2, borderRadius: 2, display: 'inline-block', mb: 2 }}>
                    <img src={qrSrc} alt="QR Code" style={{ display: 'block', width: 220, height: 220 }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', display: 'block', wordBreak: 'break-all', fontSize: '0.65rem' }}>
                    {url}
                  </Typography>
                </>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ p: 2, backgroundColor: '#0D1E35' }}>
            <Button onClick={() => setQrDialog(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>Close</Button>
            {viewingClass && (
              <Button variant="outlined" onClick={() => {
                const url = `${window.location.origin}/quick-checkin?classId=${viewingClass.id}&sessionType=${encodeURIComponent(viewingClass.sessionType || '')}`;
                navigator.clipboard.writeText(url);
              }} sx={{ borderColor: '#C9A84C', color: '#C9A84C' }}>
                Copy Link
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* ── Bulk Cancel Dialog ── */}
        <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
            Cancel Classes
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
              Cancel all classes of the selected type(s) on the currently viewed date.
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>Class Type</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
              {[
                { value: 'junior',    label: 'Junior' },
                { value: 'adult',     label: 'Adult' },
                { value: 'private',   label: 'Private' },
                { value: 'block',     label: 'Block Time' },
                { value: 'all-today', label: 'All Today' },
              ].map(tile => {
                const sel = cancelTypes.includes(tile.value);
                return (
                  <Box key={tile.value} onClick={() => {
                    if (tile.value === 'all-today') {
                      setCancelTypes(sel ? [] : ['all-today']);
                    } else {
                      setCancelTypes(prev => {
                        const without = prev.filter(v => v !== 'all-today');
                        return without.includes(tile.value)
                          ? without.filter(v => v !== tile.value)
                          : [...without, tile.value];
                      });
                    }
                  }} sx={{
                    px: 3, py: 1.5, borderRadius: 2, cursor: 'pointer', fontWeight: 600,
                    fontSize: '0.9rem', userSelect: 'none',
                    border: `2px solid ${sel ? '#C0392B' : '#ddd'}`,
                    backgroundColor: sel ? '#C0392B' : 'white',
                    color: sel ? 'white' : '#555',
                    '&:hover': { borderColor: '#C0392B', backgroundColor: sel ? '#a93226' : '#fdecea' },
                  }}>
                    {tile.label}
                  </Box>
                );
              })}
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>Cancellation Reason</Typography>
            <TextField fullWidth select value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              sx={{ backgroundColor: '#eee', borderRadius: 1 }}>
              <MenuItem value="">— Select a reason —</MenuItem>
              {['No Coach Available', 'Weather', 'Holiday', 'Low Attendance'].map(r => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setCancelDialog(false)}>Close</Button>
            <Button variant="contained" onClick={handleBulkCancel}
              disabled={cancelSaving || cancelTypes.length === 0 || !cancelReason}
              sx={{ backgroundColor: '#C0392B', '&:hover': { backgroundColor: '#a93226' } }}>
              {cancelSaving ? <CircularProgress size={20} /> : 'Cancel Classes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Single-Class Cancel Dialog ── */}
        <Dialog open={singleCancelDialog} onClose={() => setSingleCancelDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
            Cancel This Class
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {viewingClass && (
              <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                {formatSessionLabel(viewingClass.sessionType)}{viewingClass.skillGroup ? ` — ${viewingClass.skillGroup}` : ''}<br/>
                {viewingClass.date} · {viewingClass.startTime}–{viewingClass.endTime}
              </Typography>
            )}
            <TextField
              fullWidth select label="Cancellation Reason"
              value={singleCancelReason}
              onChange={e => setSingleCancelReason(e.target.value)}
              sx={{ mb: 1 }}
            >
              {['No Coach Available', 'Weather', 'Holiday', 'Low Attendance'].map(r => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setSingleCancelDialog(false)}>Close</Button>
            <Button
              variant="contained" color="error"
              onClick={handleSingleCancel}
              disabled={singleCancelSaving || !singleCancelReason}
            >
              {singleCancelSaving ? <CircularProgress size={20} /> : 'Cancel Class'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Merge Classes Dialog ── */}
        <Dialog open={mergeDialog} onClose={() => setMergeDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
            Merge Classes
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
              Select 2 or more classes to merge. Attendance from all selected classes will be combined into the merged class.
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>Select Classes to Merge</Typography>
            <TextField
              type="date"
              size="small"
              label="Classes on date"
              value={(() => {
                if (mergeDate) return mergeDate;
                const now = new Date();
                return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
              })()}
              onChange={e => { setMergeDate(e.target.value); setMergeSelected([]); }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1, width: 220 }}
              helperText="Pick any date (past, today, or future) to merge classes from"
            />
            <Box sx={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 1, mb: 3 }}>
              {(() => {
                const now = new Date();
                const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
                const filterDate = mergeDate || todayStr;
                return [...classes].filter(c => c.date === filterDate).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || '')).map(c => {
                const selected = mergeSelected.find(s => s.id === c.id);
                const coach = coaches.find(co => co.id === c.headCoachId);
                return (
                  <Box key={c.id} onClick={() => setMergeSelected(prev =>
                    prev.find(s => s.id === c.id) ? prev.filter(s => s.id !== c.id) : [...prev, c]
                  )} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
                    cursor: 'pointer', borderBottom: '1px solid #eee',
                    backgroundColor: selected ? 'rgba(30,58,95,0.08)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(30,58,95,0.05)' },
                  }}>
                    <Checkbox size="small" checked={!!selected} sx={{ p: 0 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatSessionLabel(c.sessionType)}{c.skillGroup ? ` — ${c.skillGroup}` : ''}{c.eventName ? ` (${c.eventName})` : ''}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {c.date} · {c.startTime}–{c.endTime} · {c.courtNumber?.replace('court-', 'Court ')}
                        {coach ? ` · ${coach.firstName} ${coach.lastName}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                );
              });
              })()}
            </Box>
            {mergeSelected.length >= 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>
                  Merged Class Settings ({mergeSelected.length} classes selected)
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField fullWidth label="Date" type="date" size="small"
                    value={mergeDate || mergeSelected[0]?.date || ''}
                    onChange={e => setMergeDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ backgroundColor: '#eee', borderRadius: 1 }} />
                  <TextField fullWidth label="Start Time" type="time" size="small"
                    value={mergeStartTime || mergeSelected[0]?.startTime || ''}
                    onChange={e => setMergeStartTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ backgroundColor: '#eee', borderRadius: 1 }} />
                  <TextField fullWidth label="End Time" type="time" size="small"
                    value={mergeEndTime || mergeSelected[0]?.endTime || ''}
                    onChange={e => setMergeEndTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ backgroundColor: '#eee', borderRadius: 1 }} />
                </Box>
                <TextField fullWidth select label="Coach" size="small"
                  value={mergeCoach || mergeSelected[0]?.headCoachId || ''}
                  onChange={e => setMergeCoach(e.target.value)}
                  sx={{ backgroundColor: '#eee', borderRadius: 1 }}>
                  <MenuItem value="">— None —</MenuItem>
                  {coaches.map(c => <MenuItem key={c.id} value={c.id}>{c.firstName} {c.lastName}</MenuItem>)}
                </TextField>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>Courts *</Typography>
                    <Button size="small" onClick={() => setMergeCourts(prev =>
                      prev.length === COURTS.length ? [] : COURTS.map(c => c.id)
                    )} sx={{ fontSize: '0.7rem', color: '#1E3A5F', p: 0, minWidth: 0 }}>
                      {mergeCourts.length === COURTS.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {COURTS.map(c => {
                      const sel = mergeCourts.includes(c.id);
                      return (
                        <Box key={c.id} onClick={() => setMergeCourts(prev =>
                          sel ? prev.filter(x => x !== c.id) : [...prev, c.id]
                        )} sx={{
                          px: 2, py: 0.8, borderRadius: 1, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                          border: `2px solid ${sel ? '#1E3A5F' : '#ddd'}`,
                          backgroundColor: sel ? '#1E3A5F' : 'white',
                          color: sel ? 'white' : '#555',
                          '&:hover': { borderColor: '#1E3A5F', backgroundColor: sel ? '#2E6DA4' : '#DEEAF1' },
                        }}>
                          {c.title}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setMergeDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleMerge}
              disabled={mergeSaving || mergeSelected.length < 2 || mergeCourts.length === 0}
              sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}>
              {mergeSaving ? <CircularProgress size={20} /> : `Merge ${mergeSelected.length} Classes`}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Layout>
  );
}
